/*
Copyright Â© 2024 T3D project contributors.

This file is part of the T3D Library.

T3D Library is free software: you can redistribute it and/or modify
it under the terms of the GNU General Public License as published by
the Free Software Foundation, either version 3 of the License, or
(at your option) any later version.

T3D Library is distributed in the hope that it will be useful,
but WITHOUT ANY WARRANTY; without even the implied warranty of
MERCHANTABILITY or FITNESS FOR A PARTICULAR PURPOSE.  See the
GNU General Public License for more details.

You should have received a copy of the GNU General Public License
along with the T3D Library. If not, see <http://www.gnu.org/licenses/>.
*/

import { FileParser, ArchiveParser, ParsingUtils } from 't3d-parser';
export { FileParser } from 't3d-parser';
import * as THREE from 'three';
import { createMultiMaterialObject } from 'three/examples/jsm/utils/SceneUtils.js';

/**
 * A Logger class for T3D
 *
 * This satic class defines severity levels of messages and provides
 * basic logging functionality. Replacing the reference to
 *
 * @namespace
 * @static
 */
class Logger {
    TYPE_ERROR = 4;
    TYPE_WARNING = 3;
    TYPE_MESSAGE = 2;
    TYPE_PROGRESS = 1;
    TYPE_DEBUG = 0;
    logFunctions = new Array(5);
    constructor() {
        this.logFunctions[this.TYPE_ERROR] = function (...args) {
            console.error(...args);
        };
        this.logFunctions[this.TYPE_WARNING] = function (...args) {
            console.warn(...args);
        };
        this.logFunctions[this.TYPE_MESSAGE] = function (...args) {
            console.log(...args);
        };
        this.logFunctions[this.TYPE_PROGRESS] = function (...args) {
            const argArr = args;
            argArr.unshift("Progress: ");
            console.log(...argArr);
        };
        this.logFunctions[this.TYPE_DEBUG] = function (...args) {
            const argArr = args;
            console.debug(...argArr);
        };
    }
    /**
     * Main logging method. Takes 1 to N arguments. If there are more than 1 arguments
     * the first argument is interpreted as severity. If there is only one argument
     * severity defaults to
     * {{#crossLink "Logger/TYPE_MESSAGE:property"}}{{/crossLink}}.
     *
     * The following arguments are passed to a logging function matching the
     * severity.
     *
  
    *
    */
    log(...args) {
        /// Require at least 1 argument
        if (arguments.length === 0) {
            return;
        }
        /// Parse arguments to an actual array
        const argArr = args;
        /// Default to message if just one argument was passed
        if (argArr.length === 1) {
            argArr.unshift(this.TYPE_MESSAGE);
        }
        /// Otherwise 1st arg is severity, log/warn/error
        const severity = Math.max(0, Math.min(this.logFunctions.length, argArr.shift()));
        const logFunc = this.logFunctions[severity];
        /// Ouput the rest of the arguments
        logFunc.apply(this, argArr);
    }
}
const logger = new Logger();

/// Indexed DB versioning
const DB_VERSION = 4;
/**
 * This class handles offline storage of the .dat indexes and files metadata
 * @class PersistantStore
 */
class PersistantStore {
    _dbConnection;
    constructor() {
        // They may be multiple connection request issued at the same time, but it's actually okay since
        // as soon as they are registered, the not-used ones will get garbage collected
        this._dbConnection = undefined;
        this._getConnection();
    }
    /**
     *   Initialize the IndexedDB connection and manages version changes.
     *
     * @async
     * @private
     * @returns {Promise<IDBDatabase>} Promise to the Database connection
     */
    _getConnection() {
        const self = this;
        return new Promise((resolve, reject) => {
            if (self._dbConnection)
                resolve(self._dbConnection);
            // Let us open our database
            const request = window.indexedDB.open("Tyria3DLibrary", DB_VERSION);
            /// onblocked is fired when the db needs an upgrade but an older version is opened in another tab
            request.onblocked = () => {
                logger.log(logger.TYPE_ERROR, "The T3D persistant database cannot be upgraded while the app is opened somewhere else.");
            };
            /// fired when the database needs to be upgraded (or the first time)
            request.onupgradeneeded = (event) => {
                // Probably bugged
                //@ts-ignore
                const db = event.target.result;
                const currentVersion = event.oldVersion;
                if (currentVersion < 2) {
                    db.createObjectStore("listings", {
                        autoIncrement: true,
                    });
                }
                if (currentVersion < 3) {
                    const storeListing = event.currentTarget.transaction.objectStore("listings");
                    storeListing.createIndex("filename", "filename", { unique: false });
                }
            };
            request.onsuccess = (event) => {
                self._dbConnection = event.target.result;
                //@ts-ignore
                self.isReady = true;
                resolve(self._dbConnection);
            };
            request.onerror = () => {
                logger.log(logger.TYPE_ERROR, "The T3D persistant database could not be opened.");
                reject();
            };
        });
    }
    /**
     *   Add or update a listing into the database
     *
     * @async
     * @param {number|undefined} id This ID doesn't really matter, it's just the index of the object in the database, can be undefined
     * @param {Array} listing
     * @param {string} fileName .dat file name, allows to have multiple listings for different .dat files.
     * @param {boolean} isComplete Keep back the information if that was the last update on the current scan or not.
     * @returns {Promise<number>} On success, the number is the object key in the database
     */
    putListing(id, listing, fileName, isComplete) {
        const self = this;
        return new Promise((resolve, reject) => {
            self._getConnection().then((db) => {
                const store = db.transaction(["listings"], "readwrite").objectStore("listings");
                const request = id
                    ? store.put({ array: listing, filename: fileName, complete: isComplete }, id)
                    : store.put({ array: listing, name: fileName });
                request.onsuccess = () => {
                    resolve(request.result);
                };
                request.onerror = () => {
                    reject();
                };
            });
        });
    }
    /**
     * Returns the last valid listing in the database
     *
     * @async
     * @param {string} fileName .dat file name, allows to have multiple listings for different .dat files.
     * @returns {Promise<{array: Array, key: number, complete: boolean}>}
     *      array: the last listing
     *      key: the index of the last listing in the database
     */
    getLastListing(fileName) {
        const self = this;
        return new Promise((resolve) => {
            self._getConnection().then((db) => {
                const listingsStore = db.transaction(["listings"], "readonly").objectStore("listings").index("filename");
                listingsStore.openCursor(IDBKeyRange.only(fileName), "prev").onsuccess = (event) => {
                    const cursor = event.target.result;
                    if (!cursor)
                        resolve({ array: [], key: undefined, complete: true });
                    else {
                        resolve({
                            array: cursor.value.array,
                            key: cursor.primaryKey,
                            complete: cursor.value.complete,
                        });
                    }
                };
            });
        });
    }
}

/**
 * Organized thread pool of extractors
 */
class DataReader {
    settings;
    _workerPool;
    _workerLoad;
    _inflateCallbacks;
    _scanCallbacks;
    _scanByMft;
    _nextScanId;
    _pendingScanJobs;
    _pendingInflateJobs;
    constructor(settings) {
        this.settings = settings;
        this._workerPool = [];
        this._workerLoad = [];
        this._inflateCallbacks = [];
        this._scanCallbacks = [];
        this._scanByMft = new Map();
        this._nextScanId = 1;
        this._pendingScanJobs = [];
        this._pendingInflateJobs = [];
        for (let i = 0; i < settings.workersNb; i++) {
            this._startWorker(settings.workerPath);
        }
    }
    /**
     * Give every worker its own reference to the archive File so they can
     * do their own slicing during the type-detection scan. Cheap — the
     * underlying blob handle is shared, not copied.
     */
    init(file) {
        for (const worker of this._workerPool) {
            worker.postMessage({ type: "init", file });
        }
    }
    /**
     * Worker-side scan: slice `length` bytes at `offset`, inflate up to
     * `capLength` bytes if `compressed`, return the resulting buffer.
     * Concurrent calls for the same `mftId` are coalesced.
     */
    scan(mftId, offset, length, capLength, compressed) {
        return new Promise((resolve, reject) => {
            const existingId = this._scanByMft.get(mftId);
            if (existingId !== undefined && this._scanCallbacks[existingId]) {
                this._scanCallbacks[existingId].push({ resolve, reject });
                return;
            }
            const id = this._nextScanId++;
            this._scanCallbacks[id] = [{ resolve, reject }];
            this._scanByMft.set(mftId, id);
            this._enqueueScanJob({
                kind: "scan",
                id,
                mftId,
                offset,
                length,
                capLength,
                compressed,
            });
        });
    }
    inflate(buffer, _size, mftId, isImage, capLength) {
        return new Promise((resolve, reject) => {
            const arrayBuffer = buffer;
            // If no capLength then inflate the whole file
            if (!capLength || capLength < 0) {
                capLength = 0;
            }
            // Buffer length size check
            if (arrayBuffer.byteLength < 12) {
                logger.log(logger.TYPE_WARNING, `not inflating, length is too short (${arrayBuffer.byteLength})`, mftId);
                reject(new Error("Couldn't inflate " + mftId + " (mftId)"));
                return;
            }
            // Register the callback
            if (this._inflateCallbacks[mftId]) {
                this._inflateCallbacks[mftId].push({
                    resolve: resolve,
                    reject: reject,
                });
                /// No need to make another call, just wait for callback event to fire.
                return;
            }
            else {
                this._inflateCallbacks[mftId] = [{ resolve: resolve, reject: reject }];
            }
            this._enqueueInflateJob({
                kind: "inflate",
                mftId,
                buffer: arrayBuffer,
                isImage: isImage === true,
                capLength,
            });
        });
    }
    // Initialization function for creating a new worker (thread)
    _startWorker(path) {
        const self = this;
        const worker = new Worker(path);
        const selfWorkerId = this._workerPool.push(worker) - 1;
        if (this._workerLoad.push(0) !== selfWorkerId + 1) {
            throw new Error("WorkerLoad and WorkerPool don't have the same length");
        }
        worker.onmessage = function (message_event) {
            self._workerLoad[selfWorkerId] = 0;
            const data = message_event.data;
            // Scan protocol — object messages with a `type` field.
            if (data && !Array.isArray(data) && typeof data === "object" && data.type) {
                self._handleScanResponse(data);
                self._drainQueues(selfWorkerId);
                return;
            }
            let mftId;
            // Legacy inflate error path — string message.
            if (typeof data === "string") {
                logger.log(logger.TYPE_WARNING, "Inflater threw an error", data);
                mftId = parseInt(data.split(":")[0]);
                const callbacks = self._inflateCallbacks[mftId];
                delete self._inflateCallbacks[mftId];
                if (callbacks) {
                    for (const callback of callbacks) {
                        callback.reject(new Error(data));
                    }
                }
            }
            else {
                mftId = data[0];
                const callbacks = self._inflateCallbacks[mftId];
                delete self._inflateCallbacks[mftId];
                if (callbacks) {
                    for (const callback of callbacks) {
                        callback.resolve({
                            buffer: data[1],
                            dxtType: data[2],
                            imageWidth: data[3],
                            imageHeight: data[4],
                        });
                    }
                }
                else {
                    logger.log(logger.TYPE_ERROR, "Inflater threw an error", data);
                }
            }
            self._drainQueues(selfWorkerId);
        };
    }
    _handleScanResponse(data) {
        const callbacks = this._scanCallbacks[data.id];
        this._scanCallbacks[data.id] = undefined;
        // Drop the mftId→id mapping. We don't know which mftId this was for from
        // the response, but the dedup map's lifecycle matches the callbacks: when
        // callbacks resolve, no future scan() can coalesce onto this id anyway.
        // Walk the small map to find and remove; map stays bounded by concurrent
        // unique mftIds in flight, never the full archive.
        for (const [mft, sid] of this._scanByMft) {
            if (sid === data.id) {
                this._scanByMft.delete(mft);
                break;
            }
        }
        if (!callbacks)
            return;
        if (data.type === "scan-result" && data.buffer) {
            for (const cb of callbacks)
                cb.resolve(data.buffer);
        }
        else {
            const err = new Error(data.error || "scan failed");
            for (const cb of callbacks)
                cb.reject(err);
        }
    }
    _enqueueScanJob(job) {
        const workerId = this._findIdleWorkerIndex();
        if (workerId !== -1 && this._pendingInflateJobs.length === 0) {
            this._dispatchScanJob(workerId, job);
            return;
        }
        this._pendingScanJobs.push(job);
    }
    _enqueueInflateJob(job) {
        const workerId = this._findIdleWorkerIndex();
        if (workerId !== -1) {
            this._dispatchInflateJob(workerId, job);
            return;
        }
        this._pendingInflateJobs.push(job);
    }
    _drainQueues(workerId) {
        if (this._workerLoad[workerId] !== 0)
            return;
        const inflateJob = this._pendingInflateJobs.shift();
        if (inflateJob) {
            this._dispatchInflateJob(workerId, inflateJob);
            return;
        }
        const scanJob = this._pendingScanJobs.shift();
        if (scanJob) {
            this._dispatchScanJob(workerId, scanJob);
        }
    }
    _dispatchScanJob(workerId, job) {
        this._workerLoad[workerId] = 1;
        this._workerPool[workerId].postMessage({
            type: "scan",
            id: job.id,
            mftId: job.mftId,
            offset: job.offset,
            length: job.length,
            capLength: job.capLength,
            compressed: job.compressed,
        });
    }
    _dispatchInflateJob(workerId, job) {
        this._workerLoad[workerId] = 1;
        this._workerPool[workerId].postMessage([job.mftId, job.buffer, job.isImage, job.capLength], [job.buffer]);
    }
    _findIdleWorkerIndex() {
        for (let i = 0; i < this._workerLoad.length; i++) {
            if (this._workerLoad[i] === 0)
                return i;
        }
        return -1;
    }
}

// prettier-ignore
const mapMap = {
    /**
     * @property maps
     * @type {Array.<{name: String, maps: Array.<{fileName: String, name: String}>}>}
     */
    maps: [
        {
            name: "(X0) Core Game - Ascalon [Regrown]",
            maps: [
                { fileName: "196585.data", name: "(City) Black Citadel [RegrownCitadel]" },
                { fileName: "188591.data", name: "(1-15) Plains of Ashford [RegrownAshford]" },
                { fileName: "190490.data", name: "(15-25) Diessa Plateau [RegrownRange]" },
                { fileName: "283574.data", name: "(30-40) Fields of Ruin [RegrownHawke]" },
                { fileName: "282668.data", name: "(40-50) Iron Marches [RegrownGullet]" },
                { fileName: "280025.data", name: "(50-60) Blazeridge Steppes [RegrownBrand]" },
                { fileName: "281313.data", name: "(60-70) Fireheart Rise [RegrownFlame]" },
            ],
        },
        {
            name: "(X0) Core Game - Kryta [Valley]",
            maps: [
                { fileName: "191265.data", name: "(City) Divinity's Reach [ValleyDivinity]" },
                { fileName: "2771534.data", name: "(City) Old Lion's Arch [ValleyArchProbed]" },
                { fileName: "191000.data", name: "(City) Lion's Arch [ValleyArch]" },
                { fileName: "1427048.data", name: "(Lobby) Lion's Arch Aerodrome [ValleyArchRaidLobby]" },
                { fileName: "192711.data", name: "(1-15) Queensdale [ValleyHill]" },
                { fileName: "2737234.data", name: "(15-25) Kessex Hills [ValleyWildernessTower]" },
                { fileName: "289176.data", name: "(25-35) Gendarran Fields [ValleySettlement]" },
                { fileName: "287870.data", name: "(35-45) Harathi Hinterlands [ValleyHeadland]" },
                { fileName: "286945.data", name: "(45-55) Bloodtide Coast [ValleyCoast]" },
                { fileName: "520479.data", name: "(80) Southsun Cove [EventValleyLost]" },
                { fileName: "194288.data", name: "(Story) Kessex Hills [ValleyWilderness]" },
                { fileName: "1019669.data", name: "(Story) Old Lion's Arch [ValleyArchOrig]" },
                { fileName: "294938.data", name: "(Story) Claw Island [ValleyClaw]" },
                { fileName: "295005.data", name: "(Other) Chantry of Secrets [ValleyWhisper]" },
            ],
        },
        {
            name: "(X0) Core Game - Shiverpeak Mountains [Alpine]",
            maps: [
                { fileName: "197122.data", name: "(City) Hoelbrak [AlpineHall]" },
                { fileName: "187611.data", name: "(1-15) Wayfarer Foothills [AlpineSnowline]" },
                { fileName: "186397.data", name: "(15-25) Snowden Drifts [AlpinePowder]" },
                { fileName: "277587.data", name: "(25-40) Lornar's Pass [AlpineSlope]" },
                { fileName: "275155.data", name: "(40-50) Dredgehaunt Cliffs [AlpineCrest]" },
                { fileName: "278717.data", name: "(50-60) Timberline Falls [AlpineTimberland]" },
                { fileName: "276252.data", name: "(70-80) Frostgorge Sound [AlpineGlacier]" },
                { fileName: "1018612.data", name: "(Story) Timberline Falls [AlpineTimberlandBeforeConcordiaVines]" },
                { fileName: "295282.data", name: "(Other) Eye of the North [AlpineMonument]" },
            ],
        },
        {
            name: "(X0) Core Game - Tarnished Coast [Wetland]",
            maps: [
                { fileName: "198272.data", name: "(City) Rata Sum [WetlandComplex]" },
                { fileName: "198076.data", name: "(City) The Grove [WetlandGrove]" },
                { fileName: "195149.data", name: "(1-15) Caledon Forest [WetlandBayou]" },
                { fileName: "195493.data", name: "(1-15) Metrica Province [WetlandRiverside]" },
                { fileName: "293307.data", name: "(15-25) Brisban Wildlands [WetlandGrotto]" },
                { fileName: "292254.data", name: "(55-65) Sparkfly Fen [WetlandGlade]" },
                { fileName: "291064.data", name: "(60-70) Mount Maelstrom [WetlandCape]" },
                { fileName: "295962.data", name: "(Story) The Grove, Cursed Shore [WetlandDestiny]" },
            ],
        },
        {
            name: "(X0) Core Game - Ruins of Orr [Risen]",
            maps: [
                { fileName: "284829.data", name: "(70-75) Straits of Devastation [RisenBeachhead]" },
                { fileName: "285089.data", name: "(75-80) Malchor's Leap [RisenCliff]" },
                { fileName: "285634.data", name: "(80) Cursed Shore [RisenShore]" },
                { fileName: "295179.data", name: "(Story) Cathedral of Hidden Depths [RisenBeachheadAbaddon]" },
                { fileName: "473765.data", name: "(Story) The Ruined City of Arah [RisenDragonStory]" },
            ],
        },
        {
            name: "(X0) Core Game: Dungeons",
            maps: [
                { fileName: "189364.data", name: "(30/35) Ascalonian Catacombs [RegrownCatacomb]" },
                { fileName: "287214.data", name: "(40/45) Caudecus's Manor [ValleyEstate]" },
                { fileName: "291284.data", name: "(50) Twilight Arbor [WetlandGarden]" },
                { fileName: "645968.data", name: "(55/80) Twilight Arbor [WetlandGardenRepeat]" },
                { fileName: "275474.data", name: "(60/65) Sorrow's Embrace [AlpineFurnace]" },
                { fileName: "284039.data", name: "(70/75) Citadel of Flame [RegrownShaman]" },
                { fileName: "276520.data", name: "(76/80) Honor of the Waves [AlpineIceberg]" },
                { fileName: "293606.data", name: "(78/80) Crucible of Eternity [WetlandWeapon]" },
                { fileName: "473930.data", name: "(80) The Ruined City of Arah [RisenDragonRepeat]" },
            ],
        },
        {
            name: "(LW1) Living World Season 1",
            maps: [
                { fileName: "679089.data", name: "(Zone #1) The Tower of Nightmares [ValleyWildernessKraitTowerInterior]" },
                { fileName: "2469960.data", name: "(Zone #2) The Twisted Marionette [AlpineSlopeMarionette]" },
                { fileName: "2770873.data", name: "(Zone #3) The Battle for Lion's Arch [ValleyArch2]" },
                { fileName: "568778.data", name: "(Story) Cragstead [AlpineEnclave]" },
                { fileName: "2690992.data", name: "(Story) Old Lion's Arch [ValleyArchDragon]" },
                { fileName: "2711089.data", name: "(Story) The Crown Pavilion [JubileeArena-2]" },
                { fileName: "672138.data", name: "(Story) Kessex Hills [ValleyWildernessKraitTowerInstance]" },
                { fileName: "2773298.data", name: "(Story) Gendarran Fields [ValleySettlementFeb2014]" },
                { fileName: "814803.data", name: "(Story) Old Lion's Arch [ValleyArch3]" },
                { fileName: "2771205.data", name: "(Raid #1) Old Lion's Court [ValleyArch2Strike]" },
            ],
        },
        {
            name: "(LW2) Living World Season 2",
            maps: [
                { fileName: "836211.data", name: "(Zone #1) Dry Top [DryTop]" },
                { fileName: "996202.data", name: "(Zone #2) The Silverwastes [SandcastleToFleet]" },
                { fileName: "861770.data", name: "(Story) Dry Top [DryTopE2]" },
                { fileName: "861815.data", name: "(Story) Kessex Hills [ValleyWildernessFortSalma]" },
                { fileName: "909361.data", name: "(Story) The Silverwastes [SandCastle]" },
                { fileName: "908730.data", name: "(Story) Special Collections, Glint's Lair [SandGrain]" },
                { fileName: "922320.data", name: "(Story) Metrica Province [WetlandRiversideAsuraFirstContact]" },
            ],
        },
        {
            name: "(X1) Heart of Thorns - Maguuma Jungle [Jungle]",
            maps: [
                { fileName: "969663.data", name: "(Zone #1) Verdant Brink [JungleFleet]" },
                { fileName: "1263739.data", name: "(Zone #2) Auric Basin [JungleGold]" },
                { fileName: "1264291.data", name: "(Zone #3) Tangled Depths [JungleRoots]" },
                { fileName: "1262310.data", name: "(Zone #4) Dragon's Stand [JungleArmy]" },
                { fileName: "1262460.data", name: "(Story) Mordremoth's Mind [JungleArmyNightmare]" },
                { fileName: "1151420.data", name: "(Raid #1-4) Spirit Vale [RaidJungle01]" },
                { fileName: "1383309.data", name: "(Raid #5-7) Salvation Pass [E1R2]" },
                { fileName: "1454070.data", name: "(Raid #8-11) Stronghold of the Faithful [E1R3]" },
                { fileName: "1645215.data", name: "(Raid #12-15) Bastion of the Penitent [E1R4]" },
            ],
        },
        {
            name: "(LW3) Living World Season 3",
            maps: [
                { fileName: "1472635.data", name: "(Zone #1) Bloodstone Fen [JungleBloodstone]" },
                { fileName: "1498578.data", name: "(Zone #2) Ember Bay [FireIsland]" },
                { fileName: "1605211.data", name: "(Zone #3) Bitterfrost Frontier [AlpineTundra]" },
                { fileName: "1646520.data", name: "(Zone #4) Lake Doric [ValleyPort]" },
                { fileName: "1682493.data", name: "(Zone #5) Draconis Mons [LavaLamp]" },
                { fileName: "1735346.data", name: "(Zone #6) Siren's Landing [Reclaimed]" },
                { fileName: "1498071.data", name: "(Story) Virtual Theorem Replicator [HoloRoom]" },
                { fileName: "1645474.data", name: "(Story) Caudecus's Manor [ValleyEstatePort]" },
                { fileName: "1682763.data", name: "(Story) Heart of the Volcano [LavaLampInstance2]" },
                { fileName: "1734839.data", name: "(Story) White Mantle Hideout [S0E6AspectHunt]" },
                { fileName: "1735440.data", name: "(Story) Shining Blade Headquarters [ValleyBlade]" },
            ],
        },
        {
            name: "(X2) Path of Fire - Crystal Desert [Desert]",
            maps: [
                { fileName: "1794574.data", name: "(Zone #1) Crystal Oasis [DesertOasis]" },
                { fileName: "1833034.data", name: "(Zone #2) Desert Highlands [DesertHighlands]" },
                { fileName: "1840103.data", name: "(Zone #3) Elon Riverlands [DesertRiver]" },
                { fileName: "1842533.data", name: "(Zone #4) The Desolation [DesertTorment]" },
                { fileName: "1839188.data", name: "(Zone #5) Domain of Vabbi [DesertPalace]" },
                { fileName: "1840368.data", name: "(Story) The Sanctum [DesertSanctum]" },
                { fileName: "1833726.data", name: "(Story) Kesho, Domain of the Lost [DesertMists]" },
                { fileName: "1906329.data", name: "(Raid #1-4) Hall of Chains [E1R5]" },
                { fileName: "2045250.data", name: "(Raid #5-8) Mythwright Gambit [E2R2]" },
                { fileName: "2157962.data", name: "(Raid #9-12) The Key of Ahdashim [E2R3]" },
                { fileName: "2124612.data", name: "(Other) Mists Rift [BrandedMistfight]" },
            ],
        },
        {
            name: "(LW4) Living World Season 4",
            maps: [
                { fileName: "1902235.data", name: "(Zone #1) Domain of Istan [DesertJewel]" },
                { fileName: "1957526.data", name: "(Zone #2) Sandswept Isles [DesertArchipelagoLargeMap]" },
                { fileName: "2004704.data", name: "(Zone #3) Domain of Kourna [DesertMoon]" },
                { fileName: "2044320.data", name: "(Zone #4) Jahai Bluffs [DesertBluffs]" },
                { fileName: "2092435.data", name: "(Zone #5) Thunderhead Peaks [AlpineKeep]" },
                { fileName: "2146125.data", name: "(Zone #6) Dragonfall [DesertDragonfall]" },
                { fileName: "1903523.data", name: "(Story) Crystal Oasis [DesertOasisInstanceS4E1]" },
                { fileName: "1901428.data", name: "(Story) Fahranur, the First City [DesertCity]" },
                { fileName: "1954984.data", name: "(Story) Inquest Transport Facility [DesertArchipelago_Chapter1]" },
                { fileName: "1955224.data", name: "(Story) Lab Sigma-05 [DesertArchipelago_Chapter2]" },
                { fileName: "1955471.data", name: "(Story) Rata Primus [DesertArchipelago_Chapter5]" },
                { fileName: "1955642.data", name: "(Story) Divinity's Reach [DesertArchipelago_Chapter5Boss_1]" },
                { fileName: "1955915.data", name: "(Story) Mount Maelstrom [DesertArchipelago_Chapter5Boss_2]" },
                { fileName: "1956140.data", name: "(Story) Frostgorge Sound [DesertArchipelago_Chapter5Boss_3]" },
                { fileName: "1956245.data", name: "(Story) ??? [DesertArchipelago_Chapter5Boss_4]" },
                { fileName: "2005467.data", name: "(Story) Crystal Oasis [DesertOasisS4E3]" },
                { fileName: "2044502.data", name: "(Story) Mist Chase [DesertBluffsCh5]" },
                { fileName: "2093791.data", name: "(Story) Glint's Trials [The_Begining]" },
                { fileName: "2092812.data", name: "(Story) The Auditorium [AlpineKeepInstance]" },
                { fileName: "2093450.data", name: "(Story) The Auditorium [AlpineKeepInstanceKralk]" },
                { fileName: "2146312.data", name: "(Story) The Auditorium [DesertDragonfallCh01]" },
                { fileName: "2146346.data", name: "(Story) Dragonflight [DesertDragonfallCh02]" },
                { fileName: "2146510.data", name: "(Story) Kralkatorrik's Heart [DesertDragonfallFinalInstance]" },
                { fileName: "2146376.data", name: "(Story) Sayida's Airship [DesertDragonfallEpilogue]" },
            ],
        },
        {
            name: "(LW5) The Icebrood Saga",
            maps: [
                { fileName: "2251447.data", name: "(Lobby) Eye of the North [AlpineMonumentDragon]" },
                { fileName: "2203371.data", name: "(Zone #1, Raid #1) Grothmar Valley, Shiverpeaks Pass [RegrownBlood]" },
                { fileName: "2251232.data", name: "(Zone #2) Bjora Marches [BjoraMarchesCombined]" },
                { fileName: "2318029.data", name: "(Zone #3) Drizzlewood Coast [AlpineCascades02]" },
                { fileName: "2434582.data", name: "(Zone #4) Dragonstorm [DragonFight]" },
                { fileName: "2249817.data", name: "(Story) Voice in the Deep [AlpineMarchesEp2FinalInstance]" },
                { fileName: "2273128.data", name: "(Story) Darkrime Delves [AlpineDelves]" },
                { fileName: "2298865.data", name: "(Story) Drizzlewood Coast [AlpineCascades]" },
                { fileName: "2369582.data", name: "(Story) Rata Sum [WetlandComplexE5Story]" },
                { fileName: "2414140.data", name: "(Story) Heart of the Volcano [LavaLampInstance_S5E5]" },
                { fileName: "2434675.data", name: "(Story) Dragonstorm [DragonFightStoryInstance]" },
                { fileName: "2224355.data", name: "(Raid #2) Voice of the Fallen and Claw of the Fallen [KodanBNB]" },
                { fileName: "2224381.data", name: "(Raid #3) Fraenir of Jormag [shamanbnb]" },
                { fileName: "2224406.data", name: "(Raid #4) Boneskinner [WendigoBNB]" },
                { fileName: "2251486.data", name: "(Raid #5) Whisper of Jormag [WhisperofJormag]" },
                { fileName: "2272807.data", name: "(Raid #6) Forging Steel [AlpineClimb]" },
                { fileName: "2299088.data", name: "(Raid #7) Cold War [AlpineCascadesStrike]" },
            ],
        },
        {
            name: "(LW5) The Icebrood Saga: Dragon Response Missions",
            maps: [
                { fileName: "2370614.data", name: "(DRM #1) Metrica Province [WetlandRiversideS5E5]" },
                { fileName: "2370017.data", name: "(DRM #2) Brisban Wildlands [WetlandGrottoS5E5]" },
                { fileName: "2369116.data", name: "(DRM #3) Gendarran Fields [ValleySettlementS5E5]" },
                { fileName: "2366776.data", name: "(DRM #4) Fields of Ruin [RegrownHawkeS5E5]" },
                { fileName: "2364032.data", name: "(DRM #5) Thunderhead Peaks [AlpineKeepS5E5]" },
                { fileName: "2368400.data", name: "(DRM #6) Lake Doric [ValleyPortS5E5]" },
                { fileName: "2365787.data", name: "(DRM #7) Snowden Drifts [AlpinePowderS5E5]" },
                { fileName: "2369398.data", name: "(DRM #8) Caledon Forest [WetlandBayouS5E5]" },
                { fileName: "2367211.data", name: "(DRM #9) Bloodtide Coast [ValleyCoastS5E5]" },
                { fileName: "2366368.data", name: "(DRM #10) Fireheart Rise [RegrownFlameS5E5]" },
            ],
        },
        {
            name: "(X3) End of Dragons - Cantha [Cantha]",
            maps: [
                { fileName: "2669133.data", name: "(Lounge) Thousand Seas Pavilion [MTXLounge]" },
                { fileName: "2640407.data", name: "(Lobby) Arborstone [CanthaArborstone]" },
                { fileName: "2647516.data", name: "(Zone #1) Seitung Province [CanthaShingJea]" },
                { fileName: "2645519.data", name: "(Zone #2) New Kaineng City [CanthaKaineng]" },
                { fileName: "2641850.data", name: "(Zone #3) The Echovald Wilds [CanthaEchovald]" },
                { fileName: "2644196.data", name: "(Zone #4) Dragon's End [CanthaJadeSea]" },
                { fileName: "3043972.data", name: "(Zone #5) Gyala Delve [CanthaDeepQR2]" },
                { fileName: "2639738.data", name: "(Story) Lornar's Pass [AlpineSlopeX03]" },
                { fileName: "2639795.data", name: "(Story) Aetherblade Armada [CanthaArmada]" },
                { fileName: "2645805.data", name: "(Story) Yong Reactor [CanthaKainengCH5Reactor]" },
                { fileName: "3012212.data", name: "(Story) Gyala Delve [CanthaDeep]" },
                { fileName: "3100195.data", name: "(Story) Seitung Province [CanthaShingJeaExpac4AureneInstance]" },
                { fileName: "2649141.data", name: "(Raid #1) Aetherblade Hideout [MaiTrin]" },
                { fileName: "2642769.data", name: "(Raid #2) Xunlai Jade Junkyard [CanthaEchovaldStrikeMissions]" },
                { fileName: "2646104.data", name: "(Raid #3) Kaineng Overlook [CanthaKainengMinSecStrike]" },
                { fileName: "2702043.data", name: "(Raid #4) Harvest Temple [CanthaJadeSeaStrikeMission]" },
            ],
        },
        {
            name: "(X4) Secrets of the Obscure - Skies of Tyria [Sky]",
            maps: [
                { fileName: "3135660.data", name: "(Lobby) The Wizard's Tower [SkyTower]" },
                { fileName: "3134712.data", name: "(Zone #1) Skywatch Archipelago [SkyRise]" },
                { fileName: "3135285.data", name: "(Zone #2) Amnytas [SkySpire]" },
                { fileName: "3316196.data", name: "(Zone #3) Inner Nayos [Dream3]" },
                { fileName: "3136072.data", name: "(Story) Gendarran Fields [ValleySettlementExpac4StealthMission]" },
                { fileName: "3194054.data", name: "(Story) Inner Nayos [Dream]" },
                { fileName: "3264516.data", name: "(Story) Inner Nayos [Dream2]" },
                { fileName: "3134778.data", name: "(Raid #1) Cosmic Observatory [SkyRiseStrikeObservatory]" },
                { fileName: "3135805.data", name: "(Raid #2) Temple of Febe [CerusArena]" },
                { fileName: "3193573.data", name: "(Convergence) Outer Nayos [BountyIslands]" },
            ],
        },
        {
            name: "(X5) Janthir Wilds - Isles of Janthir [Wild]",
            maps: [
                { fileName: "3414888.data", name: "(Zone #1) Lowland Shore [WildDelta]" },
                { fileName: "3415804.data", name: "(Zone #2) Janthir Syntri [WildIsland]" },
                { fileName: "3598092.data", name: "(Zone #3) Mistburned Barrens [WildRuins]" },
                { fileName: "3638621.data", name: "(Zone #4) Bava Nisos [WildMaw]" },
                { fileName: "3406364.data", name: "(Story) Lion's Arch [ValleyArchEx5Intro]" },
                { fileName: "3559095.data", name: "(Raid #1-4, Convergence) Mount Balrior [WildTitan]" },
                { fileName: "3560860.data", name: "(Other) Mists Vault [WizardsVault]" },
            ],
        },
        {
            name: "(X6) Visions of Eternity - Castora [Magic]",
            maps: [
                { fileName: "3719751.data", name: "(Zone #1) Shipwreck Strand [MagicCoast]" },
                { fileName: "3721199.data", name: "(Zone #2) Starlit Weald [MagicForest]" },
                { fileName: "3776288.data", name: "(Zone #3) Eternity's Garden [MagicRitual]" },
                { fileName: "3719841.data", name: "(Story) Unending Ocean [MagicCoast_Story01Ocean]" },
                { fileName: "3776392.data", name: "(Story) Empyreal Threshold [MagicRitualStoryAscension]" },
                { fileName: "3748908.data", name: "(Raid #1) Guardian's Glade [MagicCoastRaid]" },
                { fileName: "3749178.data", name: "(Other) Laboratory CAS-U-5 [WetlandWeaponCoralQuest]" },
            ],
        },
        {
            name: "Guild Halls & Homesteads",
            maps: [
                { fileName: "1256064.data", name: "(Guild Hall #1) Lost Precipice [GuildHeights]" },
                { fileName: "1255378.data", name: "(Guild Hall #2) Gilded Hollow [GuildCavern]" },
                { fileName: "1843274.data", name: "(Guild Hall #3) Windswept Haven [GuildPyramid]" },
                { fileName: "2648082.data", name: "(Guild Hall #4) Isle of Reflection [GuildIsle]" },
                { fileName: "3399578.data", name: "(Homestead #1) Hearth's Glow [HomesteadBoreal]" },
                { fileName: "3718508.data", name: "(Homestead #2) Comosus Isle [Magic]" },
            ],
        },
        {
            name: "Fractals of the Mists [Infinite]",
            maps: [
                { fileName: "1733961.data", name: "(Lounge) Mistlock Sanctuary [InfiniteCosmicVIPLounge]" },
                { fileName: "1498016.data", name: "(FotM #0) Mistlock Observatory [InfiniteHub]" },
                { fileName: "519839.data", name: "(FotM #1/#2/#4/#8/#9) Fractals of the Mists [InfiniteIslands]" },
                { fileName: "1472382.data", name: "(FotM #3) Snowblind [InfiniteSnowblind]" },
                { fileName: "1472406.data", name: "(FotM #5) Swampland [InfiniteSwamp]" },
                { fileName: "1426653.data", name: "(FotM #6) Cliffside [InfiniteCliffside]" },
                { fileName: "2187042.data", name: "(FotM #7) Aquatic Ruins [InfiniteUnderwater]" },
                { fileName: "580061.data", name: "(FotM #10) Molten Furnace [FlameFrost]" },
                { fileName: "1905739.data", name: "(FotM #11) Molten Boss [FlameFrostFractalExtended]" },
                { fileName: "1498798.data", name: "(FotM #12/#13) Aetherblade, Captain Mai Trin Boss [ValleyArchDungeonUpdated02]" },
                { fileName: "697450.data", name: "(FotM #14) Thaumanova Reactor [InfiniteReactor]" },
                { fileName: "1472361.data", name: "(FotM #15) Chaos Isles [InfiniteChaos]" },
                { fileName: "1605344.data", name: "(FotM #16) Nightmare [InfiniteToxic]" },
                { fileName: "1733857.data", name: "(FotM #17) Shattered Observatory [InfiniteCosmic]" },
                { fileName: "1905889.data", name: "(FotM #18) Twilight Oasis [InfiniteMordant]" },
                { fileName: "2005713.data", name: "(FotM #19) Deepstone [InfiniteLabyrinth]" },
                { fileName: "2094098.data", name: "(FotM #20) Siren's Reef [InfiniteCove]" },
                { fileName: "2333932.data", name: "(FotM #21) Sunqua Peak [FractalElementalIslands]" },
                { fileName: "3100947.data", name: "(FotM #22) Silent Surf [LuxonFractal]" },
                { fileName: "3317434.data", name: "(FotM #23) Lonely Tower [SkyRiseFractalEparch]" },
                { fileName: "3635285.data", name: "(FotM #24) Kinfall [WildBjora]" },
            ],
        },
        {
            name: "Player vs. Player [PvP]",
            maps: [
                { fileName: "2129692.data", name: "(Lobby) Heart of the Mists [PvPLobby1016rework]" },
                { fileName: "1666233.data", name: "(Conquest #1) Battle of Khylo [PvPConquestSiege3]" },
                { fileName: "197545.data", name: "(Conquest #2) Forest of Niflhel [PvPConquestForest]" },
                { fileName: "376916.data", name: "(Conquest #3) Legacy of the Foefire [PvPConquestGuild]" },
                { fileName: "520609.data", name: "(Conquest #4) Temple of the Silent Storm [PvPConquestDojo]" },
                { fileName: "1473061.data", name: "(Conquest #5) Spirit Watch [PVPConquestSpirit2]" },
                { fileName: "1934470.data", name: "(Conquest #6) Skyhammer [PvPConquestCannonCapture2]" },
                { fileName: "1644624.data", name: "(Conquest #7) Revenge of the Capricorn [PvPConquestCoastReturnshrunk]" },
                { fileName: "2128880.data", name: "(Conquest #8) Eternal Coliseum [PVPConquestArenaGodsTheme]" },
                { fileName: "2175965.data", name: "(Conquest #9) Djinn's Dominion [DesertConqWallMid2]" },
                { fileName: "1713054.data", name: "(Murderball #1, Team Deathmatch #2) Courtyard [DMCourtyard2]" },
                { fileName: "870987.data", name: "(Stronghold #1) Battle of Champion's Dusk [PvPStrongholdCity]" },
                { fileName: "1712986.data", name: "(Team Deathmatch #1) Hall of the Mists [Hallway2v2]" },
                { fileName: "1712945.data", name: "(Team Deathmatch #3) Asura Arena [asura2v2]" },
                { fileName: "2187125.data", name: "(Team Deathmatch #4) Auric Span [Jungle2v2]" },
                { fileName: "3621310.data", name: "(Push #1) Sunjiang Backstreets [PushCartKaineng]" },
            ],
        },
        {
            name: "World vs. World [WvW]",
            maps: [
                { fileName: "2113077.data", name: "(Lounge) Armistice Bastion [WvwLounge]" },
                { fileName: "2263889.data", name: "(Mist War #1) Eternal Battlegrounds [WvWCenterWallChange10_19]" },
                { fileName: "2112733.data", name: "(Mist War #2) Desert Borderlands [WvWDesertHome]" },
                { fileName: "1918037.data", name: "(Mist War #3/#4) Alpine Borderlands [WvWRedHomeLake02]" },
                { fileName: "1885693.data", name: "(Mist War #5) Obsidian Sanctum [WvWCenter4]" },
                { fileName: "736241.data", name: "(EotM #1) Edge of the Mists [WvWIslands]" },
            ],
        },
        {
            name: "Festivals & Bonus Events",
            maps: [
                { fileName: "595582.data", name: "(New Year) Dragon Ball Arena [DragonBall]" },
                { fileName: "636133.data", name: "(Super Adventure) Hub [SABHub]" },
                { fileName: "635555.data", name: "(Super Adventure) World 1 [SABWorld1]" },
                { fileName: "635960.data", name: "(Super Adventure) World 2 [SABWorld2]" },
                { fileName: "3281398.data", name: "(Super Adventure) World 3 Test Zone 1 [SABWorld3-1]" },
                { fileName: "3763434.data", name: "(Super Adventure) World 3 Test Zone 2 [SABWorld3-2]" },
                { fileName: "2164993.data", name: "(Dragon Bash) Dragon Arena [AlpineHallDragonBash]" },
                { fileName: "606255.data", name: "(Four Winds) Labyrinthine Cliffs [EvenKiteCity]" },
                { fileName: "622681.data", name: "(Four Winds) The Crown Pavilion [JubileeArena]" },
                { fileName: "605983.data", name: "(Four Winds) Sanctum Sprint [EvenTriathlonKiteCity]" },
                { fileName: "617120.data", name: "(Four Winds) Aspect Arena [EvenPVPKiteCity]" },
                { fileName: "2499169.data", name: "(Halloween) Mad King's Labyrinth [MadKingMaze2021]" },
                { fileName: "662436.data", name: "(Halloween) Lunatic Inquisition [MadKingMaze2013]" },
                { fileName: "506539.data", name: "(Halloween) Mad King's Raceway, Reaper's Rumble [MadKingField]" },
                { fileName: "506739.data", name: "(Halloween) Mad King's Clock Tower [MadKingTower]" },
                { fileName: "506592.data", name: "(Halloween) Ascent to Madness [MadKingBoss]" },
                { fileName: "529718.data", name: "(Wintersday) Snowball Mayhem, Bell Choir Ensemble [WintersdaySnowGlobe]" },
                { fileName: "529945.data", name: "(Wintersday) Winter Wonderland [WintersdayFrostyland]" },
                { fileName: "529896.data", name: "(Wintersday) Tixx's Infinirarium, Toypocalypse [WintersdayAirship]" },
                { fileName: "2076921.data", name: "(Wintersday) Secret Lair of the Snowmen [FrozenTyrant]" },
            ],
        },
        {
            name: "Historical & Unimplemented",
            maps: [
                { fileName: "184799.data", name: "Dummy [Dummy]" },
                { fileName: "193081.data", name: "Lake Doric [ValleyReach]" },
                { fileName: "195806.data", name: "Eternal Battlegrounds [WvWCenter]" },
                { fileName: "197249.data", name: "Heart of the Mists [PvPLobby]" },
                { fileName: "197402.data", name: "Battle of Kyhlo [PvPConquestSiege]" },
                { fileName: "197562.data", name: "Preparing for PvP [PvPBlackBox]" },
                { fileName: "467374.data", name: "Raid on the Capricorn [PvPConquestCoast]" },
                { fileName: "506670.data", name: "Mad King's Labyrinth [MadKingMaze]" },
                { fileName: "595722.data", name: "Aetherblade Retreat [ValleyArchDungeon]" },
                { fileName: "606030.data", name: "Basket Brawl [BasketBrawl]" },
                { fileName: "641501.data", name: "Alpine Borderlands [WvWRedHomeLake01]" },
                { fileName: "705746.data", name: "Divinity's Reach [WintersDayValleyDivinity2013]" },
                { fileName: "791564.data", name: "Courtyard [DMCourtyard]" },
                { fileName: "871093.data", name: "Battle of Champion's Dusk [PvPStrongholdCityMercs]" },
                { fileName: "875614.data", name: "Tutelage Isles [TutorialChina]" },
                { fileName: "969964.data", name: "Verdant Brink [RootsAfterShip]" },
                { fileName: "1255516.data", name: "Guild Coliseum [GuildColiseum]" },
                { fileName: "1426736.data", name: "Skyhammer [PvPConquestCannonCapture]" },
                { fileName: "1427803.data", name: "Desert Borderlands [WvWDesertHomeUpdate]" },
                { fileName: "1472979.data", name: "Revenge of the Capricorn [PvPConquestCoastReturn]" },
                { fileName: "1498174.data", name: "Eternal Coliseum [PVPConquestArenaGods2]" },
                { fileName: "1498193.data", name: "Conquest Prototype [PvPConquestTemp]" },
                { fileName: "1513556.data", name: "Competitive PvP Infection 2 (Valley) [PvPInfection2]" },
                { fileName: "1513607.data", name: "Conquest Vine [PvPConquestTest_01]" },
                { fileName: "1513620.data", name: "Conquest Islands [PvPConquestTest_02]" },
                { fileName: "1513675.data", name: "Conquest Mocap [PvPConquestTest_04]" },
                { fileName: "1625212.data", name: "Divinity's Reach [ValleyDivinityLNY]" },
                { fileName: "1644708.data", name: "Battle of Khylo [PvPConquestSiege2]" },
                { fileName: "1647236.data", name: "Desert Borderlands [WvWDesertHomeUpdateMIDrework]" },
                { fileName: "1666365.data", name: "Rata Sum [WetlandComplexSAB]" },
                { fileName: "1704155.data", name: "Eternal Coliseum [PVPConquestArenaGods4]" },
                { fileName: "1713939.data", name: "Heart of the Mists [pvplobbyrework_cats]" },
                { fileName: "1734729.data", name: "Heart of the Mists [pvplobbyrework_catsMoreRoom]" },
                { fileName: "1796999.data", name: "Lion's Arch [ValleyArchKiel]" },
                { fileName: "1798709.data", name: "Eternal Battlegrounds [WvWCenterGliding]" },
                { fileName: "1799442.data", name: "Desert Borderlands [WvWDesertHome4]" },
                { fileName: "1799855.data", name: "Alpine Borderlands [WvWRedHomeLake01gliding]" },
                { fileName: "1869665.data", name: "Lion's Arch [ValleyArchKielHalloween]" },
                { fileName: "1917775.data", name: "Divinity's Reach [ValleyDivinityholiday]" },
                { fileName: "1956299.data", name: "[DesertArchipelago_Instance]" },
                { fileName: "2065760.data", name: "Djinn's Dominion [desertconq]" },
                { fileName: "2128938.data", name: "Djinn's Dominion [DesertConqWallMid]" },
                { fileName: "2204239.data", name: "Grothmar Valley [RegrownBloodCinematic]" },
                { fileName: "2224545.data", name: "Jaga Moraine [Alpinemoraine]" },
                { fileName: "2257438.data", name: "Test [Test]" },
                { fileName: "2644298.data", name: "[JadeSeaWhirlpool]" },
                { fileName: "2649061.data", name: "Grothmar Valley [RegrownBlood_CinematicEOD]" },
                { fileName: "2689589.data", name: "Desert Highlands [DesertHighlandsE3]" },
                { fileName: "3024941.data", name: "World 3 Test [SABWorld3-0]" },
                { fileName: "3323396.data", name: "[AlpineMoraineFinalInstance]" },
                { fileName: "3424867.data", name: "Push Prototype—BETA [PVPConquestNornia]" },
            ],
        },
    ],
};

/**
 * Parse the beginning of a file to find its type
 *
 * @memberof FileTypes
 */
function getFileType(buffer) {
    const dataView = new DataView(buffer);
    const first4 = String.fromCharCode(dataView.getUint8(0), dataView.getUint8(1), dataView.getUint8(2), dataView.getUint8(3));
    // Parse textures
    switch (first4) {
        case "ATEC":
            return "TEXTURE_ATEC";
        case "ATEP":
            return "TEXTURE_ATEP";
        case "ATET":
            return "TEXTURE_ATET";
        case "ATEU":
            return "TEXTURE_ATEU";
        case "ATEX":
            return "TEXTURE_ATEX";
        case "ATTX":
            return "TEXTURE_ATTX";
        case "CTEX":
            return "TEXTURE_CTEX";
        case "CTEU":
            return "TEXTURE_CTEU";
    }
    if (first4.indexOf("DDS") === 0)
        return "TEXTURE_DDS";
    if (first4.indexOf("PNG") === 1)
        return "TEXTURE_PNG";
    if (first4.indexOf("RIFF") === 0)
        return "TEXTURE_RIFF";
    if (first4.indexOf("YUI") === 0)
        return "TEXT_YUI";
    // PackFiles
    if (first4.indexOf("PF") === 0) {
        const file = new FileParser(buffer, true); /// true for "plz no load chunkz"
        return "PF_" + file.header.type;
    }
    // Binaries
    if (first4.indexOf("MZ") === 0)
        return "BINARIES";
    // Strings
    if (first4.indexOf("strs") === 0)
        return "STRINGS";
    // Raw asnd chunk (without pack file)
    if (first4.indexOf("asnd") === 0)
        return "CHUNK_ASND";
    // TODO: parse all buffers and if all bytes are valid unicode symbols then
    // return TEXT_UNKNOWN;
    // Unknown
    return "UNKNOWN";
}

var FileTypes = /*#__PURE__*/Object.freeze({
  __proto__: null,
  getFileType: getFileType
});

/**
 * A statefull class that handles reading and inflating data from a local GW2 dat file.
 */
class LocalReader {
    settings;
    dataReader;
    persistantStore;
    file;
    indexTable;
    fileMetaTable;
    persistantData = [];
    _fileTypeCache;
    constructor(settings) {
        this.settings = settings;
        this.dataReader = new DataReader(settings);
        this.file = undefined;
        this.indexTable = [];
        this.fileMetaTable = [];
        if (settings.noIndexedDB !== false) {
            this.persistantStore = new PersistantStore();
        }
    }
    /**
     *   Asynchronously loads the archive by parsing its file index and header.
     */
    async openArchive(file) {
        const { metaTable, indexTable } = await ArchiveParser.readArchive(file);
        this.fileMetaTable = metaTable;
        this.indexTable = indexTable;
        this.file = file;
        this.dataReader.init(file);
    }
    /**
     *   Gets MFT index by baseId
     */
    getFileIndex(baseId) {
        return this.indexTable[baseId];
    }
    /**
     *   Returns the metadata of a file stored in the archive
     */
    getFileMeta(mftId) {
        return this.fileMetaTable[mftId];
    }
    /**
     *   Fetch a file and uncompress it if needed / required.
     */
    async readFile(mftId, isImage, raw, fileLength, extractLength) {
        if (!this.file)
            throw new Error("No file loaded");
        //let buffer, dxtType, imageWidth, imageHeight;
        const meta = this.getFileMeta(mftId);
        if (!meta)
            throw new Error("Unexistant file");
        // Slice up the data
        const buffer = await ParsingUtils.sliceFile(this.file, meta.offset, fileLength || meta.size);
        // If needed we decompress, if not we keep raw
        if (raw || meta.compressed) {
            let data = {
                buffer: undefined,
                dxtType: undefined,
                imageWidth: undefined,
                imageHeight: undefined,
            };
            await this.dataReader
                .inflate(buffer, buffer.byteLength, mftId, isImage, extractLength || 0)
                .then((result) => {
                data = result;
            })
                .catch(() => {
                data = {
                    buffer: undefined,
                    dxtType: undefined,
                    imageWidth: undefined,
                    imageHeight: undefined,
                };
            });
            return data;
        }
        else
            return { buffer };
    }
    /**
     *   Scans asynchronously the types of all the files listed in the archive.
     *   Uses persistant store to cache and speed up a rescan.
     */
    async readFileList(
    // This is a way for platforms not supporting indexDB to provide their own persistant storage.
    oldFileList, options) {
        return this._readFileListInternal(oldFileList, undefined, options);
    }
    async readFileListIncremental(callbacks, oldFileList, options) {
        return this._readFileListInternal(oldFileList, callbacks, options);
    }
    async _readFileListInternal(oldFileList, callbacks, options) {
        if (!this.file)
            throw new Error("No file loaded");
        const self = this;
        const forceRescan = options?.forceRescan === true;
        let persistantList = oldFileList || [];
        let persistantId;
        try {
            // Load previously saved data
            if (this.persistantStore && !forceRescan) {
                const lastListing = await this.persistantStore.getLastListing(this.file.name);
                persistantList = lastListing.array;
                // If the last scan was not completed then we will just update it..
                if (!lastListing.complete) {
                    persistantId = lastListing.key;
                }
            }
            const reverseIndex = this.getReverseIndex();
            // Create a list of all the baseIds we need to inspect
            const iterateList = Object.keys(self.indexTable).map((i) => Number(i));
            for (const index in persistantList) {
                if (!(index in self.indexTable))
                    iterateList.push(index);
            }
            callbacks?.onStart?.(iterateList.length);
            // Run up to `concurrency` _readFileType calls in flight at once. The pool
            // matches the worker count so every decompression worker can be kept busy.
            const concurrency = Math.max(1, this.settings.workersNb || 1);
            const inFlight = new Set();
            let persistantNeedsUpdate = false;
            let pendingIdbWrite = null;
            const progressStep = Math.max(1, Math.floor(iterateList.length / 100));
            // Only persist progress every ~10% — full-array structured clone on every
            // 1% used to dominate once the scan went parallel.
            const idbWriteStep = progressStep * 10;
            for (let i = 0; i < iterateList.length; i++) {
                const baseId = iterateList[i];
                const result = this._needsScan(baseId, persistantList, forceRescan);
                if (result.scan === true) {
                    if (inFlight.size >= concurrency) {
                        await Promise.race(inFlight);
                    }
                    const task = this._readFileType(baseId, forceRescan).then((scanResult) => {
                        if (scanResult) {
                            persistantList[baseId] = {
                                baseId: baseId,
                                size: scanResult.size,
                                crc: scanResult.crc,
                                fileType: scanResult.fileType,
                            };
                            const entry = this._toScanEntry(baseId, persistantList[baseId], reverseIndex);
                            if (entry)
                                callbacks?.onEntry?.(entry);
                        }
                    });
                    const tracked = task.finally(() => inFlight.delete(tracked));
                    inFlight.add(tracked);
                }
                else if (result.change === "none" && persistantList[baseId]) {
                    const entry = this._toScanEntry(baseId, persistantList[baseId], reverseIndex);
                    if (entry)
                        callbacks?.onEntry?.(entry);
                }
                if (result.change === "removed") {
                    delete persistantList[baseId];
                }
                if (result.change !== "none")
                    persistantNeedsUpdate = true;
                const scanned = i + 1;
                if (i % progressStep === 0 || scanned === iterateList.length) {
                    const pct = Math.floor((scanned / Math.max(1, iterateList.length)) * 100);
                    if (!callbacks?.onProgress) {
                        logger.log(logger.TYPE_PROGRESS, "Finding types", pct);
                    }
                    callbacks?.onProgress?.({
                        scanned,
                        total: iterateList.length,
                        label: "Finding types",
                        pct,
                    });
                }
                // Throttled IDB write — only when no write is pending so we don't pile
                // them up. The final write below catches anything we skipped.
                if (i % idbWriteStep === 0 && self.persistantStore && persistantNeedsUpdate && !pendingIdbWrite) {
                    persistantNeedsUpdate = false;
                    pendingIdbWrite = self.persistantStore
                        .putListing(persistantId, persistantList, self.file.name, false)
                        .then((res) => {
                        persistantId = res;
                    })
                        .finally(() => {
                        pendingIdbWrite = null;
                    });
                }
            }
            await Promise.all(inFlight);
            if (pendingIdbWrite)
                await pendingIdbWrite;
            if (self.persistantStore) {
                await self.persistantStore.putListing(persistantId, persistantList, self.file.name, true);
            }
            this.persistantData = persistantList;
            const finalList = this.getFileList();
            callbacks?.onComplete?.(finalList);
            return finalList;
        }
        catch (err) {
            callbacks?.onError?.(err);
            throw err;
        }
    }
    /**
     * Cheap version of the readFileList which will only scan files registered in the mapFileList
     * This helps us being sure that we only return files that contain a mapc chunk when using
     * the getMapList function
     */
    async readMapList() {
        const fileList = mapMap.maps.reduce((maps, category) => {
            return maps.concat(category.maps.map((entry) => entry.fileName));
        }, []);
        const temporaryStore = [];
        for (const fileName of fileList) {
            const baseId = fileName.split(".data")[0];
            if (this.indexTable[baseId]) {
                const scanResult = await this._readFileType(baseId);
                temporaryStore[baseId] = {
                    baseId: Number(baseId),
                    size: scanResult.size,
                    crc: scanResult.crc,
                    fileType: scanResult.fileType,
                };
            }
        }
        // Fill the store without saving it to disk
        this.persistantData = temporaryStore;
    }
    /**
     *   Returns a list of all the maps with their name and category.
     *   Uncategorized maps are available only if readFileList have been used before.
     */
    async getMapList() {
        const self = this;
        const mapArray = [];
        // If the archive hasn't been completely scanned we do a partial scan for the map files.
        // It should be fast
        if (this.persistantData.length === 0) {
            await this.readMapList();
        }
        // Filter the maps out of all our files
        const reversedIndex = this.getReverseIndex();
        const maps = this.persistantData
            .filter((file) => file.fileType === "PF_mapc")
            .filter((id) => id.baseId === reversedIndex[self.getFileIndex(id.baseId)][0]);
        for (const map of maps) {
            let found = false;
            // Try to see if we already have some informations on this map
            for (const category of mapMap.maps) {
                const fileMap = category.maps.find((item) => Number(item.fileName.split(".data")[0]) === map.baseId);
                if (fileMap) {
                    mapArray.push({
                        name: fileMap.name,
                        category: category.name,
                        baseId: map.baseId,
                        categoryIndex: mapMap.maps.indexOf(category),
                    });
                    found = true;
                    break;
                }
            }
            // If not we register it as Uncategorized
            if (!found) {
                mapArray.push({
                    name: map.baseId.toString(),
                    category: "Uncategorized",
                    baseId: map.baseId,
                    categoryIndex: 99999,
                });
            }
        }
        mapArray.sort((a, b) => a.category.localeCompare(b.category));
        return mapArray;
    }
    /**
     *   Return the meta table with extra information such as an array of baseIds and the file types.
     *   The filetype is available only if readFileList have been used before of course.
     */
    getFileList() {
        const typeList = this.persistantData ? this.persistantData.map((i) => i.fileType) : [];
        const reverseBaseIdList = this.getReverseIndex();
        const fileList = this.fileMetaTable.map((meta, mftId) => {
            const baseIds = reverseBaseIdList[mftId] ? reverseBaseIdList[mftId] : [];
            const type = reverseBaseIdList[mftId] ? typeList[baseIds[0]] : "Non-Registered";
            return {
                mftId: mftId,
                baseIdList: baseIds,
                size: meta.size,
                crc: meta.crc,
                fileType: type,
            };
        });
        fileList[0] = {
            mftId: 0,
            baseIdList: [],
            size: 0,
            crc: 0,
            fileType: "Non-Registered",
        };
        return fileList;
    }
    /**
     * @returns {Array<Array<number>>}
     */
    getReverseIndex() {
        return this.indexTable.reduce((reversed, mftId, baseId) => {
            if (mftId in reversed)
                reversed[mftId].push(baseId);
            else
                reversed[mftId] = [baseId];
            return reversed;
        }, []);
    }
    // Callback wrapper
    /**
     * Reads data from a file in the dat.
     * If `raw` is true, any infation is skipped and raw data is returned.
     */
    loadFile(baseId, callback, isImage, raw) {
        const mftId = this.getFileIndex(baseId);
        if (mftId <= 0)
            return callback(null);
        this.readFile(mftId, isImage, raw).then((result) => {
            if (result.buffer === undefined)
                return callback(null);
            callback(result.buffer, result.dxtType, result.imageWidth, result.imageHeight);
        });
    }
    // Private
    _needsScan(baseId, persistantData, forceRescan = false) {
        if (baseId <= 0)
            return { change: "none", scan: false };
        const mftId = this.getFileIndex(baseId);
        const metaData = this.getFileMeta(mftId);
        // Nothing interesting
        if (metaData === undefined && !(baseId in persistantData)) {
            return { change: "none", scan: false };
        }
        // If the file have been deleted
        else if (metaData === undefined) {
            return { change: "removed", scan: false };
        }
        // If the file is new
        else if (!(baseId in persistantData)) {
            return { change: "added", scan: true };
        }
        // Force a full type refresh even when the archive entry itself is unchanged.
        else if (forceRescan) {
            return { change: "forced", scan: true };
        }
        // If the size or crc don't match
        else if (metaData.size !== persistantData[baseId].size || metaData.crc !== persistantData[baseId].crc) {
            return { change: "modified", scan: true };
        }
        // If everything is the same
        else {
            return { change: "none", scan: false };
        }
    }
    async _readFileType(baseId, forceRescan = false) {
        if (!this._fileTypeCache)
            this._fileTypeCache = [];
        const mftId = this.getFileIndex(baseId);
        const metaData = this.getFileMeta(mftId);
        if (!forceRescan && this._fileTypeCache[baseId] !== undefined) {
            return { fileType: this._fileTypeCache[baseId], crc: metaData.crc, size: metaData.size };
        }
        // Read length: 32 raw bytes is enough for the magic-byte signatures (4)
        // and the PF FileParser header (12). For compressed files we need a larger
        // compressed window so the wasm inflate has enough input to produce ≥4
        // decompressed bytes — 1000 matches the previous cap.
        const compressed = !!metaData.compressed;
        const length = Math.min(metaData.size, compressed ? 1000 : 32);
        if (length < 4)
            return undefined;
        let fileBuffer;
        try {
            fileBuffer = await this.dataReader.scan(mftId, metaData.offset, length, 32, compressed);
        }
        catch {
            return undefined;
        }
        if (fileBuffer.byteLength < 4)
            return undefined;
        const fileType = getFileType(fileBuffer);
        this._fileTypeCache[baseId] = fileType;
        return { fileType, crc: metaData.crc, size: metaData.size };
    }
    _toScanEntry(baseId, item, reverseIndex) {
        const mftId = this.getFileIndex(baseId);
        const meta = this.getFileMeta(mftId);
        if (!meta || meta.size <= 0 || mftId <= 15)
            return null;
        return {
            mftId,
            baseIdList: reverseIndex[mftId] ?? [],
            size: item.size,
            crc: item.crc,
            fileType: item.fileType,
        };
    }
}

/**
 * Base class for data interpretors a.k.a. 'Renderers'
 *
 * Renderers are classes that collect and interpret data from the dat file.
 *
 * A {{#crossLink "LocalReader"}}{{/crossLink}} instance is used for accessing data from the dat.
 *
 * A {{#crossLink "Logger"}}{{/crossLink}} is used for posting progress output or error messages.
 *
 * The generated data, be it strings, numbers or meshes are put into a value object structure called
 * the 'context'. The context can store data generated by multiple renderers and makes sure each
 * renderer type, or class has it's own value object within the context. This enables one renderer
 * to read data written by another renderer wihtout having to worry about overwriting existing data
 * within the context. Keep in mind that you will have to manually pass and clean the conext object!
 *
 * A clean context object should just be the empty javasript object : {}.
 *
 * When a Renderer is done it will fire the callback and any view interrested in retrieving the generated
 * data from the context can read it using
 * {{#crossLink "T3D/getContextValue:method"}}{{/crossLink}}.
 *
 *
 *
 *
 * @class DataRenderer
 * @constructor
 * @param  {LocalReader} localReader  The LocalReader instance to read data from.
 * @param  {Object} settings     Any settings used by this renderer.
 * @param  {Object} context      Shared value object between renderers.
 * @param  {Logger} logger       The logging class to use for progress, warnings, errors et cetera.
 */
class DataRenderer {
    localReader;
    settings;
    context;
    rendererName;
    static rendererName = "DataRenderer";
    logger;
    constructor(localReader, settings, context, logger_, rendererName = "DataRenderer") {
        this.localReader = localReader;
        this.settings = settings;
        this.context = context;
        this.rendererName = rendererName;
        /// Just storing parameters
        if (!settings) {
            settings = {};
        }
        this.context[rendererName] = {};
        if (!logger_) {
            this.logger = logger;
        }
        else {
            this.logger = logger_;
        }
    }
    /**
     * Gets the output value object for a specified class within the context.
     *
     * @param  {Class} otherClass The class to fetch the output value object for.
     * If not specified the class of this instance will be used.
     * @return {Object}            The output value object for this class within the context.
     */
    getOutput(otherClass) {
        return this.context[otherClass ? otherClass.rendererName : this.rendererName];
    }
    /**
     * Basic rendering of unknown files. Output fileds generated:
     *
     * - *fileId* The fileId passed in the settings parameter when constructing this instance.
     *
     * - *rawData* An ArrayBuffer containg the infalted binary data of the loaded file.
     *
     * - *rawString* A string representation of the rawData
     *
     * - *image* A value object witht he fields 'width', 'height' and 'data' describing a RGBA bitmap
     * image. Only set if the loaded file was a texture.
     *
     * - *file* A FileParser representation of the loaded file. Only set if the loaded file was a Pack File.
     *
     * @async
     * @param  {Function} callback Fires when renderer is finished, does not take arguments.
     */
    renderAsync(callback) {
        const self = this;
        this.localReader.loadFile(this.settings.id, function (inflatedData) {
            /// Set fileId so callers can identify this VO
            self.getOutput().fileId = self.settings.id;
            /// Share inflated data
            self.getOutput().rawData = inflatedData;
            /// Construct raw string
            const uarr = new Uint8Array(inflatedData);
            const rawStrings = [];
            const chunksize = 0xffff;
            const len = Math.min(uarr.length, 10000);
            // There is a maximum stack size. We cannot call String.fromCharCode with as many arguments as we want
            for (let i = 0; i * chunksize < len; i++) {
                //@ts-ignore
                rawStrings.push(String.fromCharCode.apply(null, uarr.subarray(i * chunksize, (i + 1) * chunksize)));
            }
            if (len < uarr.length) {
                rawStrings.push("T3D Ignored the last " + (uarr.length - len) + " bytes when generating this raw output");
            }
            self.getOutput().rawString = rawStrings.join();
            /// Check if this is an PF or ATEX file
            // Binareis are MZ
            const dataView = new DataView(inflatedData);
            const first4 = String.fromCharCode(dataView.getUint8(0), dataView.getUint8(1), dataView.getUint8(2), dataView.getUint8(3));
            /// Do special stuff for different fcc signatures
            ///
            /// fourcc != fcc::ATEX && fourcc != fcc::ATEC && fourcc != fcc::ATEP &&
            /// fourcc != fcc::ATET && fourcc != fcc::ATEU && fourcc != fcc::ATTX &&
            /// fourcc != fcc::CTEX && fourcc != fcc::CTEU)
            ///
            if (first4 === "ATEX" ||
                first4 === "ATEC" ||
                first4 === "ATEP" ||
                first4 === "ATET" ||
                first4 === "ATEU" ||
                first4 === "ATTX" ||
                first4 === "CTEX" ||
                first4 === "CTEU") {
                /// TODO: MOVE TO GW2 texture file!!
                /// Load file using LocalReader.
                self.localReader.loadFile(self.settings.id, function (inflatedData, _dxtType, imageWidth, imageHeigth) {
                    /// Create image using returned data.
                    const image = {
                        data: new Uint8Array(inflatedData),
                        width: imageWidth,
                        height: imageHeigth,
                    };
                    self.getOutput().image = image;
                    callback();
                }, true);
            }
            else if (first4.indexOf("PF") === 0) {
                self.getOutput().file = new FileParser(inflatedData);
                callback();
            }
            else {
                self.getOutput().file = null;
                callback();
            }
        });
    }
}

/*
  guid 1683952224941671000 is fucked up floor in SAB HUB
  materialFilename for that mesh is 564821, shared with lots of stuff
  lod 1 and 2 are both 0
  material flags is 2056
*/
/**
 * Builds a custom vertex shader for a given number of uv cannels.
 * WIP not implemented yet!
 *
 * @memberof MaterialUtils
 * @param  {Number} numUv Number of UV channels used by this shader
 * @return {String}       Genereted vertex shader source
 */
function buildVS(numUv) {
    let vdefs = "";
    let adefs = "";
    let reads = "";
    for (let i = 0; i < numUv; i++) {
        vdefs += "varying vec2 vUv_" + (i + 1) + ";\n";
        /// uv and uv2 are defined by THREE
        if (i > 1)
            adefs += "attribute vec2 uv" + (i + 1) + ";\n";
        reads += "vUv_" + (i + 1) + " = uv" + (i > 0 ? i + 1 : "") + ";\n";
    }
    return (adefs +
        vdefs +
        "void main()\n" +
        "{\n" +
        reads +
        "vec4 mvPosition = modelViewMatrix * vec4( position, 1.0 );\n" +
        "gl_Position = projectionMatrix * mvPosition;\n" +
        "}");
}
/**
 * Generate a texture of a specified color, used to be part of THREEjs
 *
 * @memberof MaterialUtils
 * @param {Number} width
 * @param {Number} height
 * @param {THREE.Color} color
 * @returns {THREE.DataTexture}
 */
function generateDataTexture(width, height, color) {
    // create a buffer with color data
    const size = width * height;
    const data = new Uint8Array(4 * size);
    const r = Math.floor(color.r * 255);
    const g = Math.floor(color.g * 255);
    const b = Math.floor(color.b * 255);
    const a = 255;
    for (let i = 0; i < size; i++) {
        const stride = i * 4;
        data[stride] = r;
        data[stride + 1] = g;
        data[stride + 2] = b;
        data[stride + 3] = a;
    }
    // used the buffer to create a DataTexture
    const texture = new THREE.DataTexture(data, width, height, THREE.RGBAFormat);
    return texture;
}
/**
 * Builds a custom pixel shader for a given number of uv cannels.
 * WIP not implemented yet!
 *
 * @memberof MaterialUtils
 * @param  {Array}  textures  THREE textures
 * @param  {Number} numUv     Number of UV channels used by this shader
 * @param  {Number} alphaTest Texture see-trough alpha treshold
 * @param  {any} lightMap  TODO
 * @returns {string}
 */
function buildPS(textures, numUv, alphaTest, lightMap) {
    const t1uv = "vUv_" + (textures[0].uvIdx + 1);
    let discard = "";
    if (alphaTest) {
        discard = "    if (c1.a < 0.5) \n" + "       discard;\n";
    }
    /// Color from 1st text or lighted by 2nd?
    let writeColor = "gl_FragColor = c1;\n";
    if (lightMap) {
        const texIdx = 0;
        // var t2uv = "vUv_4";//+(3-textures[texIdx].uvIdx+1);
        const t2uv = "vUv_1"; // + (textures[texIdx].uvIdx+1);
        // console.log("t2uv",t2uv);
        writeColor = "   vec4 c2 = texture2D( texture" + (texIdx + 1) + ", " + t2uv + " );\n" + "     gl_FragColor = c2;\n";
        // "     gl_FragColor = vec4(c2.rgb * c1.r/.5, c2.a);\n";
    }
    let uniforms = "";
    textures.forEach(function (_t, idx) {
        uniforms += "uniform sampler2D texture" + (idx + 1) + ";\n";
    });
    /* uniforms += "uniform sampler2D texture1;\n";
    if(lightMap)
      uniforms += "uniform sampler2D texture2;\n"; */
    let varyings = "";
    for (let i = 0; i < numUv; i++) {
        varyings += "varying vec2 vUv_" + (i + 1) + ";\n";
    }
    return (uniforms +
        varyings +
        "void main( void ) {\n" +
        "    vec4 c1 = texture2D( texture1, " +
        t1uv +
        " );\n" +
        discard +
        writeColor +
        "}");
}
/**
 * WIP, concept for generatin materials to render multi UV chanelled meshes.
 *
 * @memberof MaterialUtils
 * @param  {Array}   textures  THREE texture
 * @param  {Number} numUV     Number of UV channels used by this shader
 * @param  {Number} alphaTest Texture see-trough alpha treshold
 * @return {THREE.ShaderMaterial} Generated shader
 */
function getUVMat(textures, numUV, alphaTest) {
    let lightMap = false;
    const uniforms = {};
    textures.forEach(function (t, idx) {
        uniforms["texture" + idx] = { type: "t", value: t };
    });
    if (textures.length > 1) {
        lightMap = true;
    }
    const attributes = {};
    for (let i = 2; i < numUV; i++) {
        attributes["uv" + (i + 1)] = { type: "v2", value: [] };
    }
    const vs = buildVS(numUV);
    return new THREE.ShaderMaterial({
        uniforms: uniforms,
        vertexShader: vs,
        fragmentShader: buildPS(textures, numUV, alphaTest, lightMap),
        // @ts-ignore
        attributes: attributes,
        side: THREE.FrontSide,
    });
}
const DIFFUSE_TEXTURE_TOKENS = [1733499172, 27219515885689124n];
const NORMAL_TEXTURE_TOKENS = [404146670, 850610087184878n];
function textureTokenMatches(token, candidates) {
    return candidates.some((candidate) => token === BigInt(candidate));
}
function pickMaterialTextures(material, preferredTextures) {
    const textures = preferredTextures && preferredTextures.length ? preferredTextures : material.textures;
    const allTextures = material.textures;
    const diffuse = textures.find((texture) => texture.filename > 0 && textureTokenMatches(texture.token, DIFFUSE_TEXTURE_TOKENS)) ||
        allTextures.find((texture) => texture.filename > 0 && textureTokenMatches(texture.token, DIFFUSE_TEXTURE_TOKENS)) ||
        textures.find((texture) => texture.filename > 0) ||
        allTextures.find((texture) => texture.filename > 0);
    const normal = textures.find((texture) => texture.filename > 0 && textureTokenMatches(texture.token, NORMAL_TEXTURE_TOKENS)) ||
        allTextures.find((texture) => texture.filename > 0 && textureTokenMatches(texture.token, NORMAL_TEXTURE_TOKENS));
    return { diffuse, normal };
}
/**
 * Builds a THREE texture from a ModelMaterialData by reading settings and
 * loading any required data from the localReader. Uses sharedTextures for
 * texture caching.
 *
 * This method is full of guesses and estimations, and could be improved on
 * a lot, allowing rendering of multi UV channeled materials, or special
 * materials like custom color chanelled gear.
 *
 * @memberof MaterialUtils
 * @param  {ModelMaterialData} material
 * @param  {FileParser} materialFile   A FileParser instance, must be of type AMAT
 * @param  {LocalReader} localReader The LocalReader to load the file contents from.
 * @param  {Object} sharedTextures  Value Object for keeping the texture cache
 * @return {THREE.Material}         A THREE Material with the generated contents and settings.
 */
function getMaterial(material, materialFile, localReader, sharedTextures) {
    if (!materialFile)
        return;
    const dxChunk = materialFile.getChunk("dx9s");
    let grChunk = materialFile.getChunk("grmt");
    if (!dxChunk) {
        return getSimpleMaterial(material, materialFile, localReader, sharedTextures);
    }
    /// Append all textures to the custom material
    const finalTextures = [];
    const samplerTextures = [];
    // Some materials don't use textures..
    if (material && material.textures.length && dxChunk.data.techniques.length > 0) {
        /// TODO: check for flags!
        ///
        /// techinques[] -> passes[] -> effects[] -> samplerIndex[]
        ///
        // console.log("num effects",dxChunk.data.techniques[0].passes[0].effects.length);
        // if(grChunk.data.flags!=76)
        //  return;
        /// 3 teqs : high medium low                GRAPHICS LEVEL SETTINGS
        /// 1 passes                        DON'T CARE
        /// 15 effects      Each effect has a pixel shader     HOW??
        /// 1 or 2 sampler indices                   USE ALL! (Multi material)
        const effects = dxChunk.data.techniques[0].passes[0].effects;
        // var effect = effects[10];
        const effect = effects[0];
        //let shader = dxChunk.data.shaders[effect.pixelShader];
        /* effects.forEach(function (eff) {
          if(eff.samplerIndex.length > effect.samplerIndex.length)
            effect = eff;
        }); */
        // var samplerIdx = effect.samplerIndex[0];
        let textureToken; // UINT64
        let samplerTex;
        for (let i = 0; i < effect.samplerIndex.length; i++) {
            const samplerIdx = effect.samplerIndex[i];
            const sampler = dxChunk.data.samplers[samplerIdx];
            /// SHOULD NEVER HAPPEN, hide mesh!
            if (!sampler)
                continue; // return;
            textureToken = sampler && Number(grChunk.data.texTokens[sampler.textureIndex]);
            if (!textureToken)
                textureToken = 0;
            /* else
              textureToken =textureToken.val; */
            /// Find the texture reffered by this sampler
            samplerTex = null;
            material.textures.forEach(function (tex /*, index*/) {
                /// Seems like only 1st part of token is used...
                if (!samplerTex && Number(tex.token) === textureToken) {
                    // console.log("TEX match",tex.token, textureToken)
                    samplerTex = tex;
                }
            });
            /// Add this sampler's texture to the collection of all textures
            if (samplerTex) {
                samplerTextures.push(samplerTex);
            }
            else {
                /// FALLBACK, just guess what texture we should use
                if (sampler) {
                    samplerTextures.push(material.textures[sampler.textureIndex]);
                }
                else if (material.textures.length > 0) {
                    samplerTextures.push(material.textures[0]);
                }
                else {
                    return;
                }
            }
        } /// END for each sampler index in effect
        /// We now have all textures
        // console.log("textures from sampler", samplerTextures);
        /// Fallback to using whatever texture there is.
        if (samplerTextures.length <= 0) {
            return;
            // mainTex =  material.textures[0];
        }
        // console.log("num samplers ",samplerTextures.length);
        samplerTextures.forEach(function (texture, idx) {
            if (!texture)
                return;
            /// Set texture "URL"
            const texURL = texture && texture.filename;
            /// Load texture from RAM or local reader:
            finalTextures[idx] = getTexture(texURL, localReader, sharedTextures);
            if (finalTextures[idx]) {
                finalTextures[idx].uvIdx = texture.uvPSInputIndex;
            }
        });
    } /// End if material and texture
    let finalMaterial;
    /// Create custom shader material if there are textures
    if (finalTextures) {
        // TODO: make this work!
        // eslint-disable-next-line no-constant-condition, no-constant-binary-expression
        {
            const { diffuse: ft, normal: nt } = pickMaterialTextures(material, samplerTextures);
            if (!ft || ft.filename <= 0)
                return;
            finalMaterial = new THREE.MeshPhongMaterial({
                side: THREE.FrontSide,
                map: getTexture(ft.filename, localReader, sharedTextures),
            });
            if (nt) {
                const normalMap = getTexture(nt.filename, localReader, sharedTextures);
                normalMap.flipY = true;
                finalMaterial.normalMap = normalMap;
            }
            finalMaterial.textureFilename = ft.filename;
            if (grChunk.data.flags !== 16460) {
                // console.log("Setting alpha flag for ",grChunk.data.flags)
                finalMaterial.alphaTest = 0.05;
            }
        }
    }
    /// Fallback material is monocolored red
    else {
        finalMaterial = new THREE.MeshStandardMaterial({
            side: THREE.FrontSide,
            color: 0xff0000,
            flatShading: true,
        });
    }
    finalMaterial.needsUpdate = true;
    /// Set material props
    /// disable for now in order for custom shaders not to fuck up
    if (material) {
        const alphaMask0 = 0x0001; // + 0x0100 + 0x0200;
        const alphaMask1 = 0x0010;
        const alphaMask2 = 0x0100 + 0x0200;
        //let alphaMask2b = 0x0200;
        grChunk = materialFile.getChunk("grmt");
        // Enable alpha test for transparent flags
        if (material.materialFlags & alphaMask0 ||
            material.materialFlags & alphaMask1 ||
            material.materialFlags & alphaMask2 // && solidColor != null
        ) ;
        /// GRCHUNK -> DATA -> FLAGS
        /// HAS LIGHT - TEX - ? - EMISSIVE16460
        ///
        /// 56533 LOD FOR TOMBSTONE?
        //  16460      0100 0000 0100 1100      "standard" stuff rendering OK in SAB (no alpha test)
        //
        //  16452(SAB)    0100 0000 0100 0100      yellow numbers in sab signs
        //  16448(SAB)    0100 0000 0100 0000      faces on rocks, cloudmen, skybox portal images, holes in walls, floor plates...
        //                        no lighting??
        //
        //   8268      0010 0000 0100 1100
        //   3392      0000 1101 0100 0000      Moto machine light bulbs
        //   2380      0000 1001 0100 1100
        //   2368      0000 1001 0100 0000      Fountain water with rings, portal border and circular "light"
        //    332      0000 0001 0100 1100
        //    324      0000 0001 0100 0100      Moto face sprites
        //
        //    320(SAB)    0000 0001 0100 0000      portal textures (normal maps ish)
        //
        //     76      0000 0000 0100 1100      LOTS OF STUFF
        //                           Tree leaves, ground, hills, some roofs, flags, street lights
        //                           sheild textures, some fences, water tops, waterfall
        //
        //                           IN KHYLO "everything with alpha"
        //
        //
        //     68      0000 0000 0100 0100      Some flowers (lo res?) fountain edges foam
        //
        //     64(SAB)    0000 0000 0100 0000      clouds, sun iamge
        const lightMask = 8;
        const knownFileFlags = [24652, 16460, 16452, 16448, 8268, 3392, 2380, 2368, 332, 324, 320, 76, 68, 64];
        if (knownFileFlags.indexOf(grChunk.data.flags) < 0) {
            logger.log(logger.TYPE_WARNING, "unknown GR flag", grChunk.data.flags);
        }
        if (!(grChunk.data.flags & lightMask)) {
            // debugger;
            // console.log("no light");
            finalMaterial = new THREE.MeshStandardMaterial({
                side: THREE.FrontSide,
                map: finalMaterial.map,
            });
        }
        if (grChunk.data.flags !== 16460) {
            finalMaterial.alphaTest = 0.05;
        }
    } /// End if material
    return finalMaterial;
}
function getSimpleMaterial(material, materialFile, localReader, sharedTextures) {
    if (!materialFile)
        return;
    const grChunk = materialFile.getChunk("grmt");
    const { diffuse: ft, normal: nt } = pickMaterialTextures(material);
    if (!ft || ft.filename <= 0) {
        return;
    }
    const finalMaterial = new THREE.MeshPhongMaterial({
        side: THREE.FrontSide,
        map: getTexture(ft.filename, localReader, sharedTextures),
    });
    if (nt) {
        const normalMap = getTexture(nt.filename, localReader, sharedTextures);
        normalMap.flipY = true;
        finalMaterial.normalMap = normalMap;
    }
    finalMaterial.textureFilename = ft.filename;
    if (grChunk.data.flags !== 16460) {
        finalMaterial.alphaTest = 0.05;
    }
    finalMaterial.needsUpdate = true;
    return finalMaterial;
}
/**
 * Load image data into a THREE.Texture from a texture file in the .dat file, using a LocalReader.
 * Any loaded tetures are added to sharedTextures, allowing for texture caching and fewer reads.
 *
 * @memberof MaterialUtils
 * @param  {Number} texURL         The fileId or baseId of the file to load image data from.
 * @param  {LocalReader} localReader    The LocalReader to load the file contents from.
 * @param  {Object} sharedTextures Value Object for keeping the texture cache
 * @return {THREE.Texture} A texture that will be populated by the file data when it is loaded.
 */
function getTexture(texURL, localReader, sharedTextures) {
    let finalTexture;
    /// Read texture from shared array of loaded textures
    /// or read it from URL and add to shared ones!
    if (texURL && sharedTextures[texURL]) {
        /// Just read from already loaded textures.
        finalTexture = sharedTextures[texURL];
    }
    else if (texURL) {
        /// Load and add to shared array.
        finalTexture = loadLocalTexture(localReader, texURL);
        /// Set standard texture functionality.
        finalTexture.wrapT = THREE.RepeatWrapping;
        finalTexture.wrapS = THREE.RepeatWrapping;
        finalTexture.flipY = false;
        sharedTextures[texURL] = finalTexture;
    }
    return finalTexture;
}
/**
 * Load image data into a THREE.Texture from a texture file in the .dat file, using a LocalReader.
 * If you're loading multiple textures, make sure to use
 * {{#crossLink "MaterialUtils/getTexture"}}{{/crossLink}} that allows you to cache textures.
 *
 * @memberof MaterialUtils
 * @param {LocalReader} localReader - The LocalReader to load the file contents from.
 * @param {Number} fileId - The fileId or baseId of the file to load image data from.
 * @param {Number} mapping - What THREE mapping the returned texture will use, not implemented.

 * @return {THREE.Texture} A texture that will be populated by the file data when it is loaded.
 */
function loadLocalTexture(localReader, fileId, _mapping, defaultColor, onerror) {
    if (defaultColor === undefined) {
        defaultColor = Math.floor(0xffffff * Math.random());
    }
    /// Temporary texture that will be returned by the function.
    /// Color is randomized in order to differentiate different textures during loading.
    const texture = generateDataTexture(1, // Width
    1, // Height
    new THREE.Color(defaultColor) // Color
    );
    // Threejs r71 is using these settings by default, r72+ changed it
    texture.minFilter = THREE.LinearMipmapLinearFilter;
    texture.magFilter = THREE.LinearFilter;
    texture.generateMipmaps = true;
    texture.flipY = true;
    /// Only allow non-zero fileId, otherwise jsut return static texture
    if (parseInt(String(fileId)) <= 0) {
        if (onerror)
            onerror();
        texture.needsUpdate = true;
        return texture;
    }
    /// Load file using LocalReader.
    localReader.loadFile(fileId, function (inflatedData, _dxtType, imageWidth, imageHeigth) {
        /// Require infalted data to be returned.
        if (!inflatedData) {
            if (onerror)
                onerror();
            texture.needsUpdate = true;
            return;
        }
        /// Create image using returned data.
        const image = {
            data: new Uint8Array(inflatedData),
            width: imageWidth,
            height: imageHeigth,
        };
        /// Use RGBA for all textures for now...
        /// TODO: don't use alpha for some formats!
        texture.format =
            //dxtType === 3 || dxtType === 5 ? THREE.RGBAFormat : THREE.RGBFormat;
            THREE.RGBAFormat;
        /// Update texture with the loaded image.
        //@ts-ignore
        texture.image = image;
        texture.needsUpdate = true;
    }, true);
    /// Return texture with temporary content.
    return texture;
}

var MaterialUtils = /*#__PURE__*/Object.freeze({
  __proto__: null,
  buildPS: buildPS,
  buildVS: buildVS,
  generateDataTexture: generateDataTexture,
  getMaterial: getMaterial,
  getSimpleMaterial: getSimpleMaterial,
  getTexture: getTexture,
  getUVMat: getUVMat,
  loadLocalTexture: loadLocalTexture,
  pickMaterialTextures: pickMaterialTextures
});

function createVariantId(sourceType, sourceIndex, modeType, slotIndex) {
    return `${sourceType}:${sourceIndex}:${modeType}:${slotIndex}`;
}
function normalizeSourceName(name) {
    if (typeof name !== "string")
        return null;
    const trimmed = name.trim();
    return trimmed.length > 0 ? trimmed : null;
}
function getVariantLabel(sourceType, sourceIndex, modeType, slotIndex, sourceName) {
    const slotLabel = modeType === "skyModeCubeTex" ? `Cube skybox ${slotIndex + 1}` : `Skybox ${slotIndex + 1}`;
    if (sourceType === "global")
        return slotLabel;
    const sourceLabel = sourceName ?? `Override ${sourceIndex + 1}`;
    return `${sourceLabel} - ${slotLabel}`;
}
function hasUsableSkyModeTex(entry) {
    return [entry?.texPathNE, entry?.texPathSW, entry?.texPathT].some((value) => Number(value) > 0);
}
function hasUsableSkyModeCubeTex(entry) {
    return [entry?.texPathE, entry?.texPathW, entry?.texPathN, entry?.texPathS, entry?.texPathB, entry?.texPathT].some((value) => Number(value) > 0);
}
function getSkyTextureFileId(value) {
    const numeric = Number(value);
    return Number.isFinite(numeric) && numeric > 0 ? numeric + 1 : 0;
}
function applyVariantToOutput(output, variant) {
    output.activeVariantId = variant?.id ?? null;
    output.skyBox = variant?.skyBox ?? null;
    output.skyCubeTexture = variant?.skyCubeTexture ?? null;
    output.hazeColor = variant?.hazeColor ?? null;
    output.lights = variant?.lights ?? [];
    output.hasLight = variant?.hasLight ?? false;
}
function setActiveEnvironmentVariant(context, variantId) {
    const output = context?.[EnvironmentRenderer.rendererName];
    if (!output)
        return null;
    const variants = Array.isArray(output.variants) ? output.variants : [];
    if (variants.length === 0) {
        applyVariantToOutput(output, null);
        return null;
    }
    if (variantId == null) {
        applyVariantToOutput(output, variants[0]);
        return variants[0];
    }
    const variant = variants.find((entry) => entry.id === variantId);
    if (!variant)
        return null;
    applyVariantToOutput(output, variant);
    return variant;
}
function collectMaterialTextures(material, textures) {
    for (const value of Object.values(material)) {
        if (value && typeof value === "object" && "isTexture" in value && value.isTexture) {
            textures.add(value);
        }
    }
}
function collectObjectResources(object, geometries, materials, textures) {
    if (!object)
        return;
    object.traverse((node) => {
        const mesh = node;
        if (mesh.geometry && "dispose" in mesh.geometry) {
            geometries.add(mesh.geometry);
        }
        const material = mesh.material;
        if (Array.isArray(material)) {
            material.forEach((entry) => {
                materials.add(entry);
                collectMaterialTextures(entry, textures);
            });
            return;
        }
        if (material) {
            materials.add(material);
            collectMaterialTextures(material, textures);
        }
    });
}
function disposeEnvironmentResources(context) {
    const output = context?.[EnvironmentRenderer.rendererName];
    if (!output)
        return;
    const geometries = new Set();
    const materials = new Set();
    const textures = new Set();
    for (const variant of Array.isArray(output.variants) ? output.variants : []) {
        collectObjectResources(variant.skyBox, geometries, materials, textures);
        if (variant.skyCubeTexture) {
            textures.add(variant.skyCubeTexture);
        }
    }
    for (const texture of textures)
        texture.dispose();
    for (const material of materials)
        material.dispose();
    for (const geometry of geometries)
        geometry.dispose();
    output.variants = [];
    applyVariantToOutput(output, null);
}
/**
 *
 * A renderer that generates some of the environment objects of a map.
 *
 * @class EnvironmentRenderer
 * @constructor
 * @extends DataRenderer
 * @param  {LocalReader} localReader  The LocalReader instance to read data from.
 * @param  {Object} settings     Any settings used by this renderer.
 * *Must* specify "mapFile", a FileParser.
 * @param  {Object} context      Shared value object between renderers.
 * @param  {Logger} logger       The logging class to use for progress, warnings, errors et cetera.
 */
class EnvironmentRenderer extends DataRenderer {
    static rendererName = "EnvironmentRenderer";
    mapFile;
    constructor(localReader, settings, context, logger) {
        super(localReader, settings, context, logger, "EnvironmentRenderer");
        this.mapFile = this.settings.mapFile;
    }
    getMat(tex) {
        return new THREE.MeshBasicMaterial({
            map: tex,
            side: THREE.BackSide,
            fog: false,
            depthWrite: false,
        });
    }
    getEnvironmentGlobals(environmentChunkData) {
        return environmentChunkData?.dataGlobal ?? null;
    }
    loadTextureWithFallback(targetMatIndices, materialArray, filename, fallbackFilename, hazeColorAsInt) {
        const self = this;
        function writeMat(mat) {
            targetMatIndices.forEach(function (i) {
                materialArray[i] = mat;
            });
        }
        function loadFallback() {
            const mat = self.getMat(new THREE.TextureLoader().load(fallbackFilename));
            writeMat(mat);
        }
        function errorCallback() {
            setTimeout(loadFallback, 1);
        }
        const mat = self.getMat(loadLocalTexture(this.localReader, filename, undefined, hazeColorAsInt, errorCallback));
        writeMat(mat);
    }
    getHazeColor(environmentData) {
        const hazes = environmentData?.haze;
        if (!hazes || hazes.length <= 0) {
            return [190, 160, 60];
        }
        else {
            return hazes[0].farColor;
        }
    }
    createLights(environmentData) {
        const lightsOutput = [];
        const lights = environmentData?.lighting ?? [
            {
                lights: [],
                backlightIntensity: 1.0,
                backlightColor: [255, 255, 255],
            },
        ];
        let ambientLight;
        // var light = lights[0];
        //
        let hasLight = false;
        lights.forEach(function (light /*, idx*/) {
            if (hasLight)
                return;
            const directionalLights = Array.isArray(light?.lights) ? light.lights : [];
            const backlightIntensity = Number.isFinite(light?.backlightIntensity) ? light.backlightIntensity : 1.0;
            const backlightColor = Array.isArray(light?.backlightColor) ? light.backlightColor : [255, 255, 255];
            /// Directional lights
            // eslint-disable-next-line @typescript-eslint/no-unused-vars
            let sumDirLightIntensity = 0;
            directionalLights.forEach(function (dirLightData /*, idx*/) {
                hasLight = true;
                const color = new THREE.Color(dirLightData.color[2] / 255.0, dirLightData.color[1] / 255.0, dirLightData.color[0] / 255.0);
                const directionalLight = new THREE.DirectionalLight(color.getHex(), dirLightData.intensity);
                directionalLight.position
                    .set(-dirLightData.direction[0], dirLightData.direction[2], dirLightData.direction[1])
                    .normalize();
                sumDirLightIntensity += dirLightData.intensity;
                lightsOutput.push(directionalLight);
            }); // END for each directional light in light
            /// Add some random directional lighting if there was no, in order to se SOME depth on models
            if (directionalLights.length === 0) {
                const directions = [
                    [0, 1, 0, 0.3],
                    [1, 2, 1, 0.3],
                    [-1, -2, -1, 0.3],
                ];
                directions.forEach(function (lightDir) {
                    const color = new THREE.Color(1, 1, 1);
                    const intensity = lightDir[3];
                    const directionalLight = new THREE.DirectionalLight(color.getHex(), intensity);
                    directionalLight.position.set(lightDir[0], lightDir[1], lightDir[2]).normalize();
                    sumDirLightIntensity += intensity;
                    lightsOutput.push(directionalLight);
                });
            }
            /// Ambient light
            // light.backlightIntensity /= sumDirLightIntensity +light.backlightIntensity;
            // light.backlightIntensity = light.backlightIntensity;
            const color = new THREE.Color((backlightIntensity * (255.0 - backlightColor[2])) / 255.0, (backlightIntensity * (255.0 - backlightColor[1])) / 255.0, (backlightIntensity * (255.0 - backlightColor[0])) / 255.0);
            ambientLight = new THREE.AmbientLight(color);
        }); // END for each light in lighting
        let ambientTotal = 0;
        if (ambientLight) {
            ambientTotal = ambientLight.color.r + ambientLight.color.g + ambientLight.color.b;
            lightsOutput.push(ambientLight);
        }
        return {
            lights: lightsOutput,
            hasLight: hasLight || ambientTotal > 0,
        };
    }
    buildPlanarSkybox(skyModeTex, hazeColorAsInt) {
        const materialArray = [];
        /// Load skybox textures, fallback to hosted png files.
        this.loadTextureWithFallback([1, 4], materialArray, getSkyTextureFileId(skyModeTex?.texPathNE), "img/193068.png", hazeColorAsInt);
        this.loadTextureWithFallback([0, 5], materialArray, getSkyTextureFileId(skyModeTex?.texPathSW), "img/193070.png", hazeColorAsInt);
        this.loadTextureWithFallback([2], materialArray, getSkyTextureFileId(skyModeTex?.texPathT), "img/193072.png", hazeColorAsInt);
        materialArray[3] = new THREE.MeshBasicMaterial({ visible: false });
        /// Create skybox geometry
        const boxSize = 1024; // boundSide * 2;
        const skyGeometry = new THREE.BoxGeometry(boxSize, boxSize / 2, boxSize); // Width Height Depth
        const uvAttribute = skyGeometry.getAttribute("uv");
        const uvArray = uvAttribute.array;
        /// Ugly way of fixing UV maps for the skybox
        for (let i = 0; i < uvArray.length; i += 2) {
            const face = Math.floor(i / 8); // 4 vertices per face, 2 UVs per vertex
            // PX - WEST   NX - EAST
            if (face === 0 || face === 1) {
                uvArray[i] = 1 - uvArray[i]; // Flip x
                uvArray[i + 1] /= 2.0;
                uvArray[i + 1] += 0.5; // Adjust y
            }
            // NZ - SOUTH   PZ - NORTH
            else if (face === 5 || face === 4) {
                uvArray[i + 1] /= -2.0;
                uvArray[i + 1] += 0.5; // Adjust y
            }
            else {
                uvArray[i] = 1 - uvArray[i]; // Flip x
            }
        }
        uvAttribute.needsUpdate = true;
        /// Generate final skybox
        const skyBox = new THREE.Mesh(skyGeometry, materialArray);
        /// Put horizon in camera center
        // skyBox.translateY(-(boxSize / 8));
        // skyBox.translateY( -environmentChunk.data.dataGlobal.sky.verticalOffset );
        /// Write to output
        return skyBox;
    }
    buildCubeSkybox(skyModeCubeTex, hazeColorAsInt) {
        const materialArray = [
            this.getMat(loadLocalTexture(this.localReader, getSkyTextureFileId(skyModeCubeTex?.texPathE), undefined, hazeColorAsInt)),
            this.getMat(loadLocalTexture(this.localReader, getSkyTextureFileId(skyModeCubeTex?.texPathW), undefined, hazeColorAsInt)),
            this.getMat(loadLocalTexture(this.localReader, getSkyTextureFileId(skyModeCubeTex?.texPathT), undefined, hazeColorAsInt)),
            this.getMat(loadLocalTexture(this.localReader, getSkyTextureFileId(skyModeCubeTex?.texPathB), undefined, hazeColorAsInt)),
            this.getMat(loadLocalTexture(this.localReader, getSkyTextureFileId(skyModeCubeTex?.texPathN), undefined, hazeColorAsInt)),
            this.getMat(loadLocalTexture(this.localReader, getSkyTextureFileId(skyModeCubeTex?.texPathS), undefined, hazeColorAsInt)),
        ];
        const boxSize = 1024;
        const skyGeometry = new THREE.BoxGeometry(boxSize, boxSize / 2, boxSize);
        return new THREE.Mesh(skyGeometry, materialArray);
    }
    buildSkyVariant(sourceData, sourceType, sourceIndex, modeType, slotIndex, skyModeData, fallback = false) {
        const hazeColor = this.getHazeColor(sourceData);
        const hazeColorAsInt = hazeColor[2] * 256 * 256 + hazeColor[1] * 256 + hazeColor[0];
        const lighting = this.createLights(sourceData);
        const sourceName = normalizeSourceName(sourceData?.name);
        const defaultSkyModeTex = {
            texPathNE: 187554,
            texPathSW: 187556,
            texPathT: 187558,
        };
        const variantId = fallback
            ? `fallback:${sourceType}:${sourceIndex}:${modeType}:${slotIndex}`
            : createVariantId(sourceType, sourceIndex, modeType, slotIndex);
        const label = fallback
            ? (sourceName ?? "Default skybox")
            : getVariantLabel(sourceType, sourceIndex, modeType, slotIndex, sourceName);
        let skyBox;
        if (modeType === "skyModeCubeTex") {
            skyBox = this.buildCubeSkybox(skyModeData, hazeColorAsInt);
        }
        else {
            skyBox = this.buildPlanarSkybox(skyModeData ?? defaultSkyModeTex, hazeColorAsInt);
        }
        return {
            id: variantId,
            label,
            sourceType,
            sourceIndex,
            slotIndex,
            modeType,
            sourceName,
            bindTarget: sourceData?.bindTarget ?? null,
            guid: sourceData?.guid ?? null,
            token: sourceData?.token ?? null,
            skyBox,
            skyCubeTexture: null,
            hazeColor,
            lights: lighting.lights,
            hasLight: lighting.hasLight,
        };
    }
    collectVariantsFromSource(sourceData, sourceType, sourceIndex) {
        const variants = [];
        const planarModes = Array.isArray(sourceData?.skyModeTex) ? sourceData.skyModeTex : [];
        const cubeModes = Array.isArray(sourceData?.skyModeCubeTex) ? sourceData.skyModeCubeTex : [];
        planarModes.forEach((skyModeTex, slotIndex) => {
            if (!hasUsableSkyModeTex(skyModeTex))
                return;
            variants.push(this.buildSkyVariant(sourceData, sourceType, sourceIndex, "skyModeTex", slotIndex, skyModeTex));
        });
        cubeModes.forEach((skyModeCubeTex, slotIndex) => {
            if (!hasUsableSkyModeCubeTex(skyModeCubeTex))
                return;
            variants.push(this.buildSkyVariant(sourceData, sourceType, sourceIndex, "skyModeCubeTex", slotIndex, skyModeCubeTex));
        });
        return variants;
    }
    buildVariants(environmentChunkData) {
        const variants = [];
        const globalData = this.getEnvironmentGlobals(environmentChunkData);
        variants.push(...this.collectVariantsFromSource(globalData, "global", 0));
        const overrides = Array.isArray(environmentChunkData?.dataOverrideArray)
            ? environmentChunkData.dataOverrideArray
            : [];
        overrides.forEach((sourceData, sourceIndex) => {
            variants.push(...this.collectVariantsFromSource(sourceData, "override", sourceIndex));
        });
        if (variants.length === 0) {
            variants.push(this.buildSkyVariant(globalData, "global", 0, "skyModeTex", 0, null, true));
        }
        return variants;
    }
    /**
     * Output fileds generated:
     *
     * - *hazeColor* Array of RGBA values describing the global haze color of the map.
     * - *lights* An array of THREE.DirectionalLight and  THREE.AmbientLight objects.
     * - *hasLight* Boolean is false if no directional lights were added to "lights".
     * - *skyBox* A textured THREE.Mesh skybox.
     *
     * @async
     * @param  {Function} callback Fires when renderer is finished, does not take arguments.
     */
    renderAsync(callback) {
        if (!this.mapFile) {
            throw new Error("No map file available for EnvironmentRenderer");
        }
        const environmentChunkData = this.mapFile.getChunk("env")?.data;
        const output = this.getOutput();
        output.variants = this.buildVariants(environmentChunkData);
        output.activeVariantId = null;
        output.skyBox = null;
        output.skyCubeTexture = null;
        output.hazeColor = null;
        output.lights = [];
        output.hasLight = false;
        setActiveEnvironmentVariant(this.context, output.variants[0]?.id ?? null);
        /// All parsing is synchronous, just fire callback
        callback();
    }
}

/**
 *
 * A renderer that generates meshes describing the collisions of a map.
 *
 * @class HavokRenderer
 * @constructor
 * @extends DataRenderer
 * @param  {LocalReader} localReader  The LocalReader instance to read data from.
 * @param  {Object} settings     Any settings used by this renderer.
 * *Must* specify "mapFile", a FileParser. If "visible" is specified and true, the generated meshes will be textured
 * with a MeshNormalMaterial, otherwise they will not be visible.
 * @param  {Object} context      Shared value object between renderers.
 * @param  {Logger} logger       The logging class to use for progress, warnings, errors et cetera.
 */
class HavokRenderer extends DataRenderer {
    static rendererName = "HavokRenderer";
    mapFile;
    lastP;
    seed;
    collisionGeometries;
    geometries;
    animations;
    havokChunkData;
    tempPosition;
    tempScale;
    tempEuler;
    tempQuaternion;
    tempMatrix;
    constructor(localReader, settings, context, logger) {
        super(localReader, settings, context, logger, "HavokRenderer");
        this.mapFile = this.settings.mapFile;
        this.lastP = -1;
        this.seed = 1;
        this.collisionGeometries = [];
        this.tempPosition = new THREE.Vector3();
        this.tempScale = new THREE.Vector3(1, 1, 1);
        this.tempEuler = new THREE.Euler(0, 0, 0, "ZXY");
        this.tempQuaternion = new THREE.Quaternion();
        this.tempMatrix = new THREE.Matrix4();
    }
    /**
     * TODO
     *
     * @param  {Function} callback         [description]
     * @async
     */
    renderModels(models, title, callback) {
        let mat;
        if (this.settings && this.settings.visible) {
            const rawOpacity = typeof this.settings?.opacity === "number" ? this.settings.opacity : 1;
            const opacity = Math.min(1, Math.max(0, rawOpacity));
            const transparent = opacity < 1;
            mat = new THREE.MeshNormalMaterial({
                side: THREE.DoubleSide,
                flatShading: true,
                opacity,
                transparent,
                // Prevent collisions from writing depth when translucent so they don't occlude other layers.
                depthWrite: !transparent,
            });
        }
        else if (this.settings && this.settings.export) {
            mat = new THREE.MeshBasicMaterial({ visible: true });
        }
        else {
            mat = new THREE.MeshBasicMaterial({ visible: false });
        }
        const collisionBuckets = new Map();
        const animationCache = new Map();
        this.parseAllModels(models, mat, title, 200, 0, collisionBuckets, animationCache, () => {
            this.flushCollisionBuckets(collisionBuckets, mat);
            callback();
        });
    }
    /**
     * TODO
     *
     * @param  {*} models       [description]
     * @param  {*} mat       [description]
     * @param  {*} title     [description]
     * @param  {*} chunkSize [description]
     * @param  {*} offset    [description]
     * @param  {*} collisionBuckets [description]
     * @param  {*} animationCache [description]
     * @return {*} callback          [description]
     * @async
     */
    parseAllModels(models, mat, title, chunkSize, offset, collisionBuckets, animationCache, callback) {
        let i = offset;
        for (; i < offset + chunkSize && i < models.length; i++) {
            const p = Math.round((i * 100) / models.length);
            if (p !== this.lastP) {
                this.logger.log(this.logger.TYPE_PROGRESS, "Loading Collision Models (" + title + ")", p);
                this.lastP = p;
            }
            const model = models[i];
            /// Get animation object
            const animation = this.animationFromGeomIndex(model.geometryIndex, this.geometries, this.animations, animationCache);
            if (!animation || !animation.collisionIndices) {
                continue;
            }
            for (let j = 0; j < animation.collisionIndices.length; j++) {
                const collisionIndex = animation.collisionIndices[j];
                const collision = this.havokChunkData.collisions[collisionIndex];
                if (!collision) {
                    continue;
                }
                let bucket = collisionBuckets.get(collisionIndex);
                if (!bucket) {
                    bucket = {
                        collision,
                        models: [],
                    };
                    collisionBuckets.set(collisionIndex, bucket);
                }
                bucket.models.push(model);
            }
        }
        if (i < models.length) {
            setTimeout(this.parseAllModels.bind(this, models, mat, title, chunkSize, offset + chunkSize, collisionBuckets, animationCache, callback), 10 /* time in ms to next call */);
        }
        else {
            callback();
        }
    }
    /**
     * TODO
     *
     * @param  {*} propGeomIndex [description]
     * @param  {*} geometries    [description]
     * @param  {*} animations    [description]
     * @return {*}               [description]
     */
    animationFromGeomIndex(propGeomIndex, geometries, animations, cache) {
        if (cache && cache.has(propGeomIndex)) {
            return cache.get(propGeomIndex);
        }
        // geometries is just list of all geometries.animations[end] for now
        const l = geometries[propGeomIndex].animations.length;
        const animation = animations[geometries[propGeomIndex].animations[l - 1]];
        if (cache) {
            cache.set(propGeomIndex, animation);
        }
        return animation;
        // return animations[ geometries[propGeomIndex].animations[0] ];
    }
    /**
     * TODO
     *
     * @param  {*} mat       [description]
     * @return {*}           [description]
     */
    flushCollisionBuckets(collisionBuckets, mat) {
        for (const [collisionIndex, bucket] of collisionBuckets) {
            const geometry = this.parseHavokGeometry(bucket.collision, collisionIndex);
            const instanceCount = bucket.models.length;
            if (!instanceCount) {
                continue;
            }
            const instancedMesh = new THREE.InstancedMesh(geometry, mat, instanceCount);
            instancedMesh.matrixAutoUpdate = false;
            for (let i = 0; i < instanceCount; i++) {
                this.populateTransformMatrix(bucket.models[i], this.tempMatrix);
                instancedMesh.setMatrixAt(i, this.tempMatrix);
            }
            instancedMesh.instanceMatrix.needsUpdate = true;
            instancedMesh.userData.t3dInspect = {
                inspectable: true,
                sourceKind: "collision",
                objectLabel: "Collision mesh",
                collisionIndex,
                instanceCount,
            };
            this.getOutput().meshes.push(instancedMesh);
        }
    }
    /**
     * Build model transform matrix in the same axis order as the legacy mesh path.
     */
    populateTransformMatrix(model, out) {
        const pos = model.translate;
        const rot = model.rotate;
        const scale = 32 * model.scale;
        this.tempPosition.set(pos ? pos[0] : 0, pos ? -pos[2] : 0, pos ? -pos[1] : 0);
        this.tempScale.set(1, 1, 1);
        if (scale) {
            this.tempScale.set(scale, scale, scale);
        }
        this.tempQuaternion.identity();
        if (rot) {
            this.tempEuler.set(rot[0], -rot[2], -rot[1], "ZXY");
            this.tempQuaternion.setFromEuler(this.tempEuler);
        }
        return out.compose(this.tempPosition, this.tempQuaternion, this.tempScale);
    }
    /**
     * TODO
     *
     * @return {*} [description]
     */
    seedRandom() {
        const x = Math.sin(this.seed++) * 10000;
        return x - Math.floor(x);
    }
    /**
     * TODO
     *
     * @param  {*} collision [description]
     * @param  {*} index     [description]
     * @return {*}           [description]
     */
    parseHavokGeometry(collision, index) {
        if (!this.collisionGeometries[index]) {
            const geom = new THREE.BufferGeometry();
            // Pass vertices
            const vertices = new Float32Array(collision.vertices.length * 3);
            for (let i = 0; i < collision.vertices.length; i++) {
                const v = collision.vertices[i];
                const outIndex = i * 3;
                vertices[outIndex] = v[0];
                vertices[outIndex + 1] = v[2];
                vertices[outIndex + 2] = -v[1];
            }
            geom.setAttribute("position", new THREE.BufferAttribute(vertices, 3));
            // Pass faces (indices)
            const maxIndex = collision.vertices.length - 1;
            const IndexArrayCtor = collision.vertices.length > 65535 ? Uint32Array : Uint16Array;
            const indices = new IndexArrayCtor(collision.indices.length);
            let writeIndex = 0;
            let invalidFaceCount = 0;
            for (let i = 0; i < collision.indices.length; i += 3) {
                const f1 = collision.indices[i];
                const f2 = collision.indices[i + 1];
                const f3 = collision.indices[i + 2];
                if (f1 <= maxIndex && f2 <= maxIndex && f3 <= maxIndex) {
                    indices[writeIndex++] = f1;
                    indices[writeIndex++] = f2;
                    indices[writeIndex++] = f3;
                }
                else {
                    invalidFaceCount++;
                }
            }
            if (invalidFaceCount > 0) {
                this.logger.log(this.logger.TYPE_ERROR, "Invalid indices in havok model geometry. Skipped", invalidFaceCount, "faces.");
            }
            const finalIndices = writeIndex === indices.length ? indices : indices.slice(0, writeIndex);
            geom.setIndex(new THREE.BufferAttribute(finalIndices, 1));
            if (this.settings && this.settings.visible) {
                geom.computeVertexNormals();
            }
            this.collisionGeometries[index] = geom;
        }
        return this.collisionGeometries[index];
    }
    /**
     * Output fileds generated:
     *
     * - *boundingBox* Array of values describing the bounding box of all collision.
     * - *meshes* An array of THREE.Mesh objects visualizing all collision in the map.
     *
     * @async
     * @param  {Function} callback Fires when renderer is finished, does not take arguments.
     */
    renderAsync(callback) {
        const self = this;
        // TODO:The design of this method pretty much requires one instance
        // of the class per parallel async render. Should probably fix this
        // at some point...
        /// Get required chunks
        this.havokChunkData = this.mapFile.getChunk("havk").data;
        /// Set static bounds to the bounds of the havk models
        this.getOutput().boundingBox = this.havokChunkData.boundsMax;
        /// Clear old meshes
        this.collisionGeometries = [];
        /// Set up output array
        this.getOutput().meshes = [];
        /// Grab model raw data from the chunk.
        /// Add missing scale value to obs models.
        const propModels = this.havokChunkData.propModels;
        const zoneModels = this.havokChunkData.zoneModels;
        const obsModels = this.havokChunkData.obsModels;
        obsModels.forEach(function (mdl) {
            mdl.scale = 1;
        });
        /// Store geoms and animations from the file in hte instance so we don't
        /// have to pass them arround too much. (fix this later)
        this.geometries = this.havokChunkData.geometries;
        this.animations = this.havokChunkData.animations;
        /// Render "prop", "zone" and "obs" models in that order.
        const renderZoneModelsCB = function () {
            self.renderModels(obsModels, "obs", callback);
        };
        const renderPropModelsCB = function () {
            self.renderModels(zoneModels, "zone", renderZoneModelsCB);
        };
        self.renderModels(propModels, "prop", renderPropModelsCB);
    }
}

const base32Max = Math.pow(2, 32);
/**
 * Collection Math and sorting methods
 * @namespace MathUtils
 */
/**
 * Takes an integer and calculates what the 16 bit float
 * representation of the binary data used to read the integer is.
 *
 * @memberof MathUtils
 * @param  {Number} h Integer value
 * @return {Number} Float value
 */
function f16(h) {
    const s = (h & 0x8000) >> 15;
    const e = (h & 0x7c00) >> 10;
    const f = h & 0x03ff;
    if (e === 0) {
        return (s ? -1 : 1) * Math.pow(2, -14) * (f / Math.pow(2, 10));
    }
    else if (e === 0x1f) {
        return f ? NaN : (s ? -1 : 1) * Infinity;
    }
    return (s ? -1 : 1) * Math.pow(2, e - 15) * (1 + f / Math.pow(2, 10));
}
/**
 * Calculates the number of binary ones present in the data used to
 * generate the input integer.
 *
 * @memberof MathUtils
 * @param  {Number} bits Integer
 * @return {Number}      Number of binary ones in the data
 */
function popcount(bits) {
    const SK5 = 0x55555555;
    const SK3 = 0x33333333;
    const SKF0 = 0x0f0f0f0f;
    // let SKFF = 0xff00ff
    bits -= (bits >> 1) & SK5;
    bits = (bits & SK3) + ((bits >> 2) & SK3);
    bits = (bits & SKF0) + ((bits >> 4) & SKF0);
    bits += bits >> 8;
    return (bits + (bits >> 15)) & 63;
}
/**
 * Calculates the 64 bit integer value of two 32 bit integers. Only works up to
 * the limit of the javascript Number maximum value.
 *
 * @memberof MathUtils
 * @param  {Number[]} arr     Input integers, length should be 2.
 * @return {Number}      64 bit representation of the two integers.
 */
function arr32To64(arr) {
    /// Re-read as uint64 (still little endian)
    /// Warn: this will not work for ~50+ bit longs cus all JS numbers are 64 bit floats...
    return base32Max * arr[1] + arr[0];
}
/**
 * Sorts an array and returns unique values only.
 *
 * @memberof MathUtils
 * @param  {Array} arr_in     Input array
 * @param  {Function} comparator A comparator function between the objects in arr_in
 * @return {Array}            Sorted and unique value.
 */
function sort_unique(arr_in, comparator) {
    const arr = Array.prototype.sort.call(arr_in, comparator);
    const u = {};
    const a = [];
    for (let i = 0, l = arr.length; i < l; ++i) {
        // eslint-disable-next-line no-prototype-builtins
        if (u.hasOwnProperty(arr[i])) {
            continue;
        }
        a.push(arr[i]);
        //@ts-ignore
        u[arr[i]] = 1;
    }
    return a;
}

var MathUtils = /*#__PURE__*/Object.freeze({
  __proto__: null,
  arr32To64: arr32To64,
  f16: f16,
  popcount: popcount,
  sort_unique: sort_unique
});

// TODO: Remove this local cache!!
const matFiles = {};
/**
 * Object describing the meaning of the bits in fvf integers.
 * @property fvfFormat
 * @private
 * @type {Object}
 */
const fvfFormat = {
    Position: 0x00000001 /** < 12 bytes. Position as three 32-bit floats in the order x, y, z. */,
    Weights: 0x00000002 /** < 4 bytes. Contains bone weights. */,
    Group: 0x00000004 /** < 4 bytes. Related to bone weights. */,
    Normal: 0x00000008 /** < 12 bytes. Normal as three 32-bit floats in the order x, y, z. */,
    Color: 0x00000010 /** < 4 bytes. Vertex color. */,
    Tangent: 0x00000020 /** < 12 bytes. Tangent as three 32-bit floats in the order x, y, z. */,
    Bitangent: 0x00000040 /** < 12 bytes. Bitangent as three 32-bit floats in the order x, y, z. */,
    TangentFrame: 0x00000080 /** < 12 bytes. */,
    UV32Mask: 0x0000ff00 /** < 8 bytes for each set bit. Contains UV-coords as two 32-bit floats in the order u, v. */,
    UV16Mask: 0x00ff0000 /** < 4 bytes for each set bit. Contains UV-coords as two 16-bit floats in the order u, v. */,
    Unknown1: 0x01000000 /** < 48 bytes. Unknown data. */,
    Unknown2: 0x02000000 /** < 4 bytes. Unknown data. */,
    Unknown3: 0x04000000 /** < 4 bytes. Unknown data. */,
    Unknown4: 0x08000000 /** < 16 bytes. Unknown data. */,
    PositionCompressed: 0x10000000 /** < 6 bytes. Position as three 16-bit floats in the order x, y, z. */,
    Unknown5: 0x20000000 /** < 12 bytes. Unknown data. **/,
};
/**
 * Collection of methods used for generating THREE meshes from Guild Wars 2 data formats.
 * @namespace RenderUtils
 */
/**
 * Creates a mesh representing a single plane.
 *
 * @memberof RenderUtils
 * @param  {Object} rect     An object with x1,x2,y1 and y2 properties.
 * @param  {Number} yPos     Vertical position of the rectangle.
 * @param  {THREE.Material} material   Mesh material to apply.
 * @param  {Number} dy       Mesh height.
 * @return {THREE.Mesh}      The generated mesh.
 */
function renderRect(rect, yPos, material, dy) {
    const dx = rect.x1 - rect.x2;
    const dz = rect.y1 - rect.y2;
    if (!dy)
        dy = 1;
    const cx = (rect.x1 + rect.x2) / 2;
    const cz = (rect.y1 + rect.y2) / 2;
    const cy = yPos;
    const geometry = new THREE.BoxGeometry(dx, dy, dz);
    material =
        material ||
            new THREE.MeshBasicMaterial({
                color: 0xff0000,
                wireframe: true,
            });
    const plane = new THREE.Mesh(geometry, material);
    //@ts-ignore
    plane.overdraw = true;
    plane.position.x = cx;
    plane.position.y = cy;
    plane.position.z = cz;
    return plane;
}
function textureLabelFromToken(token, index) {
    const asNumber = typeof token === "bigint" ? Number(token) : token;
    if (asNumber === 1733499172 || token === 27219515885689124n)
        return "Diffuse";
    if (asNumber === 404146670 || token === 850610087184878n)
        return "Normal";
    return `Texture ${index + 1}`;
}
function cloneTextureRefs(textureRefs) {
    return textureRefs?.map((textureRef) => ({ ...textureRef }));
}
function setInspectMetadata(mesh, metadata) {
    const current = (mesh.userData?.t3dInspect ?? {});
    mesh.userData.t3dInspect = {
        ...current,
        ...metadata,
        inspectable: true,
        textureRefs: cloneTextureRefs(metadata.textureRefs) ?? cloneTextureRefs(current.textureRefs),
    };
}
/**
 * Returns a THREE representation of the data contained by a GW2 model file.
 * The data is read using a LocalReader reference into the GW2 .dat.
 *
 * @memberof RenderUtils
 * @param {LocalReader} localReader The LocalReader to load the file contents from.
 * @param {Object} chunk Model GEOM chunk.
 * @param {Object} modelDataChunk Model MODL chunk.
 * @param {Object} sharedTextures  Value Object for keeping the texture cache.
 * @param {boolean} showUnmaterialed If false does not render any models with missing materials.
 *
 * @return {Array} Each geometry in the model file represented by a textured THREE.Mesh object
 */
function renderGeomChunk(localReader, chunk, modelDataChunk, sharedTextures, showUnmaterialed) {
    const rawMeshes = chunk.data.meshes;
    const meshes = [];
    const mats = modelDataChunk.data.permutations[0].materials;
    rawMeshes.forEach(function (rawMesh) {
        const rawGeom = rawMesh.geometry;
        const fvf = rawGeom.verts.mesh.fvf; // rawGeom.fvf;
        const numVerts = rawGeom.verts.vertexCount; // rawGeom.vertexCount;
        const rawVerts = rawGeom.verts.mesh.vertices; // rawGeom.vertices
        const indices = rawGeom.indices.indices;
        const geom = new THREE.BufferGeometry();
        const vertsDataView = new DataView(rawVerts.buffer);
        // Dirty step length for now:
        const stride = rawVerts.length / numVerts;
        // Each vertex
        // DO UV as well
        const vertices = new Float32Array(numVerts * 3);
        const uvs = [];
        /// Calculate the distance to the first pair of UV data from the
        /// start of the vertex entry
        ///
        const distToNormals = 
        //@ts-ignore
        !!(fvf & fvfFormat.Position) * 12 + !!(fvf & fvfFormat.Weights) * 4 + !!(fvf & fvfFormat.Group) * 4;
        //@ts-ignore
        const distToTangent = distToNormals + !!(fvf & fvfFormat.Normal) * 12 + !!(fvf & fvfFormat.Color) * 4;
        //@ts-ignore
        const distToBittangent = distToTangent + !!(fvf & fvfFormat.Tangent) * 12;
        //@ts-ignore
        const distToTangentFrame = distToBittangent + !!(fvf & fvfFormat.Bitangent) * 12;
        //@ts-ignore
        const distToUV = distToTangentFrame + !!(fvf & fvfFormat.TangentFrame) * 12;
        /// Check if the UV is 32 bit float or 16 bit float.
        const uv32Flag = (fvf & fvfFormat.UV32Mask) >> 8;
        const uv16Flag = (fvf & fvfFormat.UV16Mask) >> 16;
        const isUV32 = !!uv32Flag;
        const hasUV = !!uv16Flag || !!uv32Flag;
        /// Popcount (count the number of binary 1's) in the UV flag
        /// to get the number of UV pairs used in this vertex format.
        const masked = isUV32 ? uv32Flag : uv16Flag;
        let numUV = popcount(masked);
        numUV = Math.min(numUV, 1.0);
        /// Create typed UV arrays
        if (hasUV) {
            for (let i = 0; i < numUV; i++) {
                uvs[i] = new Float32Array(numVerts * 2);
            }
        }
        /// Read data from each vertex data entry
        for (let i = 0; i < numVerts; i++) {
            /// Go to vertex memory position
            let cursor = i * stride;
            /// Read position data
            /// (we just hope all meshes has 32 bit position...)
            const x = vertsDataView.getFloat32(cursor, true);
            const z = vertsDataView.getFloat32(cursor + 4, true);
            const y = vertsDataView.getFloat32(cursor + 8, true);
            /// Write position data, transformed to Tyria3D coordinate system.
            vertices[i * 3 + 0] = x; // - c.x;
            vertices[i * 3 + 1] = -y; // + c.y;
            vertices[i * 3 + 2] = -z; // + c.z;
            /// Read data at UV position
            if (hasUV) {
                for (let uvIdx = 0; uvIdx < numUV; uvIdx++) {
                    cursor = i * stride + distToUV + uvIdx * (isUV32 ? 8 : 4);
                    /// Add one UV pair:
                    let u, v;
                    if (isUV32) {
                        u = vertsDataView.getFloat32(cursor, true);
                        v = vertsDataView.getFloat32(cursor + 4, true);
                    }
                    else {
                        u = f16(vertsDataView.getUint16(cursor, true));
                        v = f16(vertsDataView.getUint16(cursor + 2, true));
                    }
                    /// Push to correct UV array
                    uvs[uvIdx][i * 2 + 0] = u;
                    uvs[uvIdx][i * 2 + 1] = v;
                }
            } /// End if has UV
        } /// End each vertex
        /// Each face descripbed in indices
        const faces = new Uint16Array(indices.length);
        for (let i = 0; i < indices.length; i += 3) {
            // This is ONE face
            faces[i + 0] = indices[i + 2];
            faces[i + 1] = indices[i + 1];
            faces[i + 2] = indices[i + 0];
        } // End each index aka "face"
        /// Add position, index and uv props to buffered geometry
        geom.setAttribute("position", new THREE.BufferAttribute(vertices, 3));
        // geom.setAttribute( 'index', new THREE.BufferAttribute( faces, 1) );
        geom.setIndex(new THREE.BufferAttribute(faces, 1));
        {
            /// Calculate normals
            geom.computeVertexNormals();
        }
        if (hasUV) {
            for (let uvIdx = 0; uvIdx < numUV; uvIdx++) {
                /// Names are "uv", "uv2", "uv3", ... , "uvN"
                const uvName = "uv" + (uvIdx > 0 ? uvIdx + 1 : "");
                /// Set "custom" attribute uvN
                geom.setAttribute(uvName, new THREE.BufferAttribute(uvs[uvIdx], 2));
                /// Flag for update
                geom.attributes[uvName].needsUpdate = true;
            }
            /// Not needed anymore?
            //@ts-ignore
            geom.uvsNeedUpdate = true;
        }
        /// Tell geometry to update its UVs and buffers
        //@ts-ignore
        geom.buffersNeedUpdate = true;
        /// DONE READING VERTEX DATA
        /// Get material used for this mesh
        const matIdx = rawMesh.materialIndex;
        const mat = mats[matIdx];
        let materialFile = null;
        if (mat && matFiles[mat.filename]) {
            materialFile = matFiles[mat.filename];
        }
        let finalMaterial = getMaterial(mat, materialFile, localReader, sharedTextures);
        /// IF we could not find a material abort OR use a wireframe placeholder.
        if (!finalMaterial) {
            if (showUnmaterialed) {
                finalMaterial = new THREE.MeshLambertMaterial({
                    color: 0x5bb1e8,
                    wireframe: false,
                    side: THREE.DoubleSide,
                });
            }
            else {
                return;
            }
        }
        const textureRefs = mat
            ? mat.textures
                .filter((texture) => texture.filename > 0)
                .map((texture, index) => ({
                fileId: texture.filename,
                label: textureLabelFromToken(texture.token, index),
            }))
            : [];
        /// Create the final mesh from the BufferedGeometry and MeshBasicMaterial
        const finalMesh = new THREE.Mesh(geom, finalMaterial);
        /// Set material info on the returned mesh
        if (mat) {
            finalMesh.materialFlags = mat.materialFlags;
            finalMesh.materialFilename = mat.filename;
        }
        finalMesh.materialName = rawMesh.materialName;
        finalMesh.textureRefs = textureRefs;
        /// Use materialFilename, materialName, and material.textureFilename in order to build export
        /// Set lod info on the returned mesh
        finalMesh.numLods = rawMesh.geometry.lods.length;
        finalMesh.lodOverride = modelDataChunk.data.lodOverride;
        /// Set flag and UV info on the returned mehs
        finalMesh.flags = rawMesh.flags;
        finalMesh.numUV = numUV;
        setInspectMetadata(finalMesh, {
            materialFileId: mat?.filename,
            materialFlags: mat?.materialFlags,
            materialName: String(rawMesh.materialName ?? ""),
            meshFlags: rawMesh.flags,
            meshName: String(rawMesh.materialName ?? ""),
            numUv: numUV,
            textureRefs,
        });
        /// Add mesh to returned Array
        meshes.push(finalMesh);
    }); /// End rawMeshes for Each
    return meshes;
}
function getInstancedMeshes(meshes, size, filterFlags) {
    const instancedMeshes = [];
    for (const mesh of meshes) {
        // If filterFlags is set, we ignore any mesh without the correct flag
        if (filterFlags !== undefined && mesh.flags !== filterFlags) {
            continue;
        }
        const im = new THREE.InstancedMesh(mesh.geometry, mesh.material, size);
        const inspectMetadata = mesh.userData?.t3dInspect;
        if (inspectMetadata?.inspectable) {
            im.userData.t3dInspect = {
                ...inspectMetadata,
                textureRefs: cloneTextureRefs(inspectMetadata.textureRefs),
                instanceCount: size,
            };
        }
        instancedMeshes.push(im);
    }
    return instancedMeshes;
}
/**
 * Loads mesh array from Model file and sends as argument to callback.
 *
 * @memberof RenderUtils
 * @async
 * @param  {Number} filename Name of the model file to load data from.
 * @param  {Array} solidColor RGBA array of 4 integers
 * @param {LocalReader} localReader The LocalReader to load the file contents from.
 * @param {Object} sharedTextures  Value Object for keeping the texture cache.
 * @param {boolean} showUnmaterialed If false does not render any models with missing materials.

 * @param  {Function} callback Fired once all meshes have been loaded.
 * two arguments are passed to the callback function.
 *
 * The first argument is an Array with each textured THREE.Mesh objects.
 *
 * The second argument is the bounding spehere of this model file.
 *
 */
function loadMeshFromModelFile(filename, _solidColor, localReader, sharedTextures, showUnmaterialed, callback) {
    // Short handles prop attributes
    const finalMeshes = [];
    /// Load file
    localReader.loadFile(filename, function (inflatedData) {
        try {
            if (!inflatedData) {
                throw "Could not find MFT entry for " + filename;
            }
            const modelFile = new FileParser(inflatedData);
            // MODL for materials -> textures
            const modelDataChunk = modelFile.getChunk("modl");
            // GEOM for geometry
            const geometryDataChunk = modelFile.getChunk("geom");
            /// Hacky fix for not being able to adjust for position
            const boundingSphere = modelDataChunk.data.boundingSphere;
            const bsc = boundingSphere.center;
            if (bsc) {
                boundingSphere.radius += Math.sqrt(bsc[0] * bsc[0] + Math.sqrt(bsc[1] * bsc[1] + bsc[2] * bsc[2]));
            }
            /// Load all material files
            const allMats = modelDataChunk.data.permutations[0].materials;
            function loadMaterialIndex(mIdx, matCallback) {
                if (mIdx >= allMats.length) {
                    matCallback();
                    return;
                }
                const mat = allMats[mIdx];
                /// Skip if file is loaded
                if (matFiles[mat.filename]) {
                    loadMaterialIndex(mIdx + 1, matCallback);
                    return;
                }
                localReader.loadFile(mat.filename, function (inflatedData) {
                    if (inflatedData) {
                        const materialFile = new FileParser(inflatedData);
                        matFiles[mat.filename] = materialFile;
                    }
                    loadMaterialIndex(mIdx + 1, matCallback);
                });
            }
            loadMaterialIndex(0, function () {
                /// Create meshes
                const meshes = renderGeomChunk(localReader, geometryDataChunk, modelDataChunk, sharedTextures, showUnmaterialed);
                // Build mesh group
                meshes.forEach(function (mesh) {
                    setInspectMetadata(mesh, {
                        modelId: filename,
                    });
                    /// Material flags
                    const knownflags = [
                        /*
                          1-5
                          Has Tex?  IDK      Light?    Alpha?
            
                          5-8
                          0      0       IDK       Water?
            
                          9-12
                          Has Tex?  0      Alpha?    Alpha?
            
                          13
                          IDK KEV
                          */
                        0, // 0 0000 0000 0000    Ground / Wall splashes
                        8, // 0 0000 0000 1000    Broken Khylo roof DDS
                        9, // 0 0000 0000 1001    Tree leaves
                        520, // 0 0010 0000 1000    Some LOD modules, fires, smoke, inside of tents (some DSS textures)
                        2056, // 0 1000 0000 1000    Solid objects, also broken animations
                        /// Solids here are unhappy, or are they? could be animations etc
                        2057, // 0 1000 0000 1001    Windmill sails, bushes, trees, but also a statue and a few pieces of wall
                        2060, // 0 1000 0000 1100    A few solid objects, like wooden barricades, one(!) painting
                        2061, // 0 1000 0000 1101    A few bushes, two paintings
                        2312, // 0 1001 0000 1000    Opaque Clock tower main walls AND IVY
                        2316, // 0 1001 0000 1100    Bushes, inner flower walkway a ramp and a box
                        // Number 10
                        2568, // 0 1010 0000 1000    Lots of solids; walls, tents also some tent details WITH alpa
                        // Number 11
                        2569, // 0 1010 0000 1001    Solids like walls and roofs and appernt non solids like ropes
                        2572, // 0 1010 0000 1100    Solid wooden beems, lamp posts
                        2573, // 0 1010 0000 1101    Lamp holders, bushes, fences, apparent non solids
                        2584, // 0 1010 0001 1000    Fountain Well water
                        2824, // 0 1011 0000 1000    Windows, sign arrows, cloth roofs (non solids) BUT straw roofs
                        2828, // 0 1011 0000 1100    A few fence post (non solids)
                        2840, // 0 1011 0001 1000    Fountain running water + pipe water
                        4617, // 1 0010 0000 1001    Found nothing
                        6664, // 1 1010 0000 1000    Two groups of solid boxes
                    ];
                    // let alphaMask0 = 0x0001 // + 0x0100 + 0x0200;
                    // let alphaMask1 = 0x0010
                    // let alphaMask2 = 0x0100 + 0x0200
                    // let alphaMask2b = 0x0200
                    const texMask = 0x8 + 0x0800;
                    if (knownflags.indexOf(mesh.materialFlags) !== 11) {
                        // return;
                    }
                    // No smoke etc
                    if (mesh.materialFlags === 520) {
                        // return;
                    }
                    // Must have texture
                    if (!showUnmaterialed && !(mesh.materialFlags & texMask)) {
                        return;
                    }
                    // NO lods (Flag 0 = normal)
                    if (mesh.flags === 1 || mesh.flags === 4) {
                        return;
                    }
                    // Add to final colection
                    finalMeshes.push(mesh);
                }); /// END FOR EACH meshes
                callback(finalMeshes, boundingSphere);
            }); /// END LOAD MATERIALS CALLBACK
        }
        catch (e) {
            console.warn("Failed rendering model " + filename, e);
            const mesh = new THREE.Mesh(new THREE.BoxGeometry(200, 2000, 200), new THREE.MeshNormalMaterial());
            //@ts-ignore
            mesh.flags = 4;
            //@ts-ignore
            mesh.materialFlags = 2056;
            //@ts-ignore
            mesh.lodOverride = [1000000, 1000000];
            setInspectMetadata(mesh, {
                modelId: filename,
                materialFlags: 2056,
                meshFlags: 4,
                objectLabel: "Fallback mesh",
            });
            finalMeshes.push(mesh);
            /// Send the final meshes to callback function
            callback(finalMeshes);
        }
    }); /// END FILE LOADED CALLBACK FUNCTION
}
/**
 * Gets a mesh array from Model file and sends as argument to callback. Uses a cache of meshes in order
 * to never read the same model file twice.
 *
 * @memberof RenderUtils
 * @async
 * @param  {Number} filename The fileId or baseId of the Model file to load
 * @param  {Array} color RGBA array of 4 integers
 * @param  {LocalReader} localReader The LocalReader object used to read data from the GW2 .dat file.
 * @param {Object} sharedMeshes  Value Object for keeping the mesh cache.
 * @param {Object} sharedTextures  Value Object for keeping the texture cache.
 * @param {boolean} showUnmaterialed If false does not render any models with missing materials.
 * @param  {Function} callback Fired once all meshes have been loaded.
 * three arguments are passed to the callback function.
 *
 * The first argument is an Array with each textured THREE.Mesh objects.
 *
 * The second argument is a boolean, true indicates that these meshes were not
 * loaded from the dat file, but retrieved from the run time cache.
 *
 * The third argument is the bounding spehere of this model file.
 */
function getMeshesForFilename(filename, color, localReader, sharedMeshes, sharedTextures, showUnmaterialed, callback) {
    /// If this file has already been loaded, just return a reference to the meshes.
    /// isCached will be set to true to inform the caller the meshes will probably
    /// have to be cloned in some way.
    if (sharedMeshes[filename]) {
        callback(sharedMeshes[filename].meshes, true, sharedMeshes[filename].boundingSphere);
    }
    /// If this file has never been loaded, load it using loadMeshFromModelFile
    /// the resulting mesh array will be cached within this model's scope.
    else {
        loadMeshFromModelFile(filename, color, localReader, sharedTextures, showUnmaterialed, function (meshes, boundingSphere) {
            /// Cache result if any.
            if (meshes) {
                sharedMeshes[filename] = {
                    meshes: meshes,
                    boundingSphere: boundingSphere,
                };
            }
            /// Allways fire callback.
            callback(meshes, false, boundingSphere);
        });
    }
}
/**
 * WIP, Tries to find all fileIds refered by a model file.
 *
 * @memberof RenderUtils
 * @async
 * @param  {Number}   filename    Model file Id
 * @param  {LocalReader}   localReader LocalReader instance to read from
 * @param  {Function} callback   First argument is list of used file IDs
 */
function getFilesUsedByModel(filename, localReader, callback) {
    const fileIds = [filename];
    /// Load model file
    localReader.loadFile(filename, function (inflatedData) {
        try {
            if (!inflatedData) {
                throw "Could not find MFT entry for " + filename;
            }
            const modelFile = new FileParser(inflatedData);
            // MODL for materials -> textures
            const modelDataChunk = modelFile.getChunk("modl");
            /// Get materials used by model
            const mats = modelDataChunk.data.permutations[0].materials;
            /// Add each material file AND referenced TEXTURES
            mats.forEach(function (mat) {
                /// Add material file id
                const matFileName = mat.filename;
                fileIds.push(matFileName);
                /// Add each texture file id
                mat.textures.forEach(function (tex) {
                    fileIds.push(tex.filename);
                });
            });
        }
        catch (e) {
            console.warn("Could not export any data", e);
        }
        callback(fileIds);
    });
}

var RenderUtils = /*#__PURE__*/Object.freeze({
  __proto__: null,
  getFilesUsedByModel: getFilesUsedByModel,
  getInstancedMeshes: getInstancedMeshes,
  getMeshesForFilename: getMeshesForFilename,
  loadMeshFromModelFile: loadMeshFromModelFile,
  renderGeomChunk: renderGeomChunk,
  renderRect: renderRect,
  setInspectMetadata: setInspectMetadata
});

// A progress cache is needed to debounce logs at the same percentage
const progressCache = {};
/**
 * This utility function is a helper for showing loading progress of dataRenderers.
 * It automatically generates progress logs which can be used outside of the library to show progress bars.
 * Must be used on loop implementations where the maximum index is known in advance
 *
 * @param {Object} logger The default logger or given one
 * @param {Number} currentIndex Current index of the item being loaded
 * @param {Number} maxIndex Maximum index of items to load
 * @param {String} progressName Name of the resource being loaded
 */
function progress(logger$1, currentIndex, maxIndex, progressName) {
    const percent = Math.round((1000.0 * currentIndex) / maxIndex) / 10.0;
    // Make sure we don't spam logs
    if (progressCache[progressName] !== percent) {
        const consistentPercent = percent + (percent.toString().indexOf(".") < 0 ? ".0" : "");
        logger$1.log(logger.TYPE_PROGRESS, progressName, consistentPercent);
        progressCache[progressName] = percent;
    }
    // Clean cache on last item
    if (currentIndex === maxIndex) {
        progressCache[progressName] = undefined;
    }
}

/**
 *
 * A renderer that generates property models for a map.
 *
 * @class PropertiesRenderer
 * @constructor
 * @extends DataRenderer
 * @param  {LocalReader} localReader  The LocalReader instance to read data from.
 * @param  {Object} settings     Any settings used by this renderer.
 * *Must* specify "mapFile", a FileParser.
 * @param  {Object} context      Shared value object between renderers.
 * @param  {Logger} logger       The logging class to use for progress, warnings, errors et cetera.
 */
class PropertiesRenderer extends DataRenderer {
    static rendererName = "PropertiesRenderer";
    showUnmaterialized;
    mapFile;
    meshCache;
    textureCache;
    models;
    modelsList = [];
    constructor(localReader, settings, context, logger) {
        super(localReader, settings, context, logger, "PropertiesRenderer");
        this.mapFile = this.settings.mapFile;
        this.showUnmaterialized = this.settings.showUnmaterialized || false;
        this.meshCache = {};
        this.textureCache = {};
        this.models = {};
    }
    /**
     * Renders all property meshes in a GW2 map described by the map's PROP chunk.
     * Output fileds generated:
     *
     * - *meshes* An array of THREE.Mesh objects visualizing all property models refered by this map.
     *
     * @async
     * @param  {Function} callback Fires when renderer is finished, does not take arguments.
     */
    renderAsync(callback) {
        this.getOutput().meshes = [];
        const propertiesChunkData = this.mapFile.getChunk("prp2").data;
        if (!propertiesChunkData) {
            return callback();
        }
        // Get all different prop types
        const props = []
            .concat(propertiesChunkData.propArray)
            .concat(propertiesChunkData.propAnimArray)
            .concat(propertiesChunkData.propInstanceArray)
            .concat(propertiesChunkData.propMetaArray);
        /// Build an object containing all the data we need for each prop
        this.models = props.reduce((models, prop) => {
            const propSize = prop.transforms ? prop.transforms.length + 1 : 1;
            if (models[prop.filename]) {
                models[prop.filename].props.push(prop);
                models[prop.filename].size += propSize;
            }
            else {
                models[prop.filename] = {
                    props: [prop],
                    size: propSize,
                };
            }
            return models;
        }, {});
        this.modelsList = Object.keys(this.models);
        this.renderModel(0, callback);
    }
    getFileIdsAsync(callback) {
        this.logger.log(this.logger.TYPE_WARNING, "PropertiesRenderer.getFileIdsAsync is not implemented");
        callback([]);
    }
    /**
     * PRIVATE METHODS
     */
    /**
     * To optimize the rendering on the GPU we render each model only once and use instances for
     * any other place using the same model. This allows us to have a much lower amount of draw calls
     * and usage of GPU memory compared to a naive approach having a mesh for each model.
     */
    renderModel(index, callback) {
        if (index >= this.modelsList.length) {
            this.meshCache = {};
            this.textureCache = {};
            this.models = {};
            return callback();
        }
        progress(this.logger, index, this.modelsList.length, "Loading 3D Models (Props)");
        const modelName = parseInt(this.modelsList[index]);
        getMeshesForFilename(modelName, this.models[modelName].props[0].color, this.localReader, this.meshCache, this.textureCache, this.showUnmaterialized, 
        // We don't care about cached meshes since we know we only ask for each meshes once.
        (meshes) => {
            if (meshes) {
                this.placeModelOnScene(modelName, meshes /*, boundingSphere*/);
            }
            this.renderModel(index + 1, callback);
        });
    }
    /**
     * Gets the meshes of a specific model, merge them together as an instanced mesh
     * and place them in the scene where they are referenced by the props.
     * @param {number} modelName The baseId of the model
     * @param {*} meshes The 3d models of the model
     */
    placeModelOnScene(modelName, meshes) {
        const model = this.models[modelName];
        if (meshes.length === 0) {
            return;
        }
        const instancedMeshes = getInstancedMeshes(meshes, model.size);
        for (const instancedMesh of instancedMeshes) {
            let instancedIndex = 0;
            for (const prop of model.props) {
                instancedMesh.setMatrixAt(instancedIndex, getMatrixForProp(prop));
                instancedIndex += 1;
                for (const transform of prop.transforms || []) {
                    instancedMesh.setMatrixAt(instancedIndex, getMatrixForProp(transform));
                    instancedIndex += 1;
                }
            }
            instancedMesh.instanceMatrix.needsUpdate = true;
            instancedMesh.computeBoundingBox();
            instancedMesh.computeBoundingSphere();
            instancedMesh.userData.t3dInspect = {
                ...(instancedMesh.userData.t3dInspect ?? {}),
                inspectable: true,
                sourceKind: "prop",
                objectLabel: "Prop model",
                modelId: modelName,
                instanceCount: model.size,
            };
        }
        for (const instancedMesh of instancedMeshes) {
            this.getOutput().meshes.push(instancedMesh);
        }
    }
}
/**
 * Return a Matrix4 for a given prop defining the Scale Rotation and Location of a model
 * @param {Object} propData
 * @returns {THREE.Matrix4}
 */
function getMatrixForProp(propData) {
    const matrix = new THREE.Matrix4();
    matrix.makeRotationFromEuler(new THREE.Euler(propData.rotation[0], -propData.rotation[2], -propData.rotation[1], "ZXY"));
    matrix.scale(new THREE.Vector3(propData.scale, propData.scale, propData.scale));
    matrix.setPosition(propData.position[0], -propData.position[2], -propData.position[1]);
    return matrix;
}

/**
 *
 * A renderer that generates meshes for a single model file.
 *
 * @class SingleModelRenderer
 * @constructor
 * @extends DataRenderer
 * @param  {LocalReader} localReader  The LocalReader instance to read data from.
 * @param  {Object} settings     Any settings used by this renderer.
 * *Must* specify "id" the base ID or file ID of the model to generate meshes for.
 * @param  {Object} context      Shared value object between renderers.
 * @param  {Logger} logger       The logging class to use for progress, warnings, errors et cetera.
 */
class SingleModelRenderer extends DataRenderer {
    static rendererName = "SingleModelRenderer";
    constructor(localReader, settings, context, logger) {
        super(localReader, settings, context, logger, "SingleModelRenderer");
    }
    /**
     * Output fileds generated:
     *
     * - *meshes* An array of THREE.Mesh objects visualizing this model file.
     *
     * @async
     * @param  {Function} callback Fires when renderer is finished, does not take arguments.
     */
    renderAsync(callback) {
        const self = this;
        /// Get file id
        const fileId = this.settings.id;
        const showUnmaterialed = true;
        /// Load the model file
        let meshCache = {};
        const textureCache = {};
        /// Set up output array
        self.getOutput().meshes = [];
        getMeshesForFilename(fileId, 0x00ff00, self.localReader, meshCache, textureCache, showUnmaterialed, function (meshes, _isCached, boundingSphere) {
            if (meshes) {
                meshes.forEach(function (mesh) {
                    mesh.boundingSphere = boundingSphere;
                    self.getOutput().meshes.push(mesh);
                });
            }
            /// Fire callback after all meshes have been added.
            meshCache = {};
            callback();
        });
    }
}

/// TODO: port fog from in-engine
function getFragmentShader() {
    return [
        "uniform vec2 uvScale;",
        "uniform vec2 offset;",
        "uniform sampler2D texturePicker;",
        "uniform sampler2D texturePicker2;",
        "uniform sampler2D texture1;",
        "uniform sampler2D texture2;",
        "uniform sampler2D texture3;",
        "uniform sampler2D texture4;",
        "uniform float lightScale;",
        "uniform float shadowStrength;",
        "uniform vec3 sunDirection;",
        "#include <common>",
        "#include <logdepthbuf_pars_fragment>",
        "varying vec2 vUv;",
        "varying vec3 vecNormal;",
        "vec3 blend(",
        "vec4 texture1, float a1, vec4 texture2, float a2,",
        "vec4 texture3, float a3, vec4 texture4, float a4)",
        "{",
        "float depth = 2.0;",
        "float alphaMult = 1.0;",
        "float alphaAdd  = 0.0;",
        "a1 *= 4.0;",
        "a2 *= 4.0;",
        "a3 *= 4.0;",
        "a4 *= 4.0;",
        "a1 =  a1+(1.5+texture1.a);",
        "a2 =  a2+(1.5+texture2.a);",
        "a3 =  a3+(1.5+texture3.a);",
        "a4 =  a4+(1.5+texture4.a);",
        "float ma = max(a1,a2);",
        "ma = max(ma,a3);",
        "ma = max(ma,a4);",
        "ma -= depth;",
        "float b1 = max(a1 - ma, 0.0);",
        "float b2 = max(a2 - ma, 0.0);",
        "float b3 = max(a3 - ma, 0.0);",
        "float b4 = max(a4 - ma, 0.0);",
        "return (",
        "texture1.rgb * b1 + texture2.rgb * b2 +",
        "texture3.rgb * b3 + texture4.rgb * b4 ",
        ") / (b1 + b2 + b3 + b4);",
        "}",
        "void main( void ) {",
        "vec2 position = vUv*uvScale;",
        "float edge = 1.0/1024.0;",
        "vec2 compPos = edge + (vUv*0.25 + offset) * (1.0-edge*2.0);",
        "vec4 tp1 = texture2D( texturePicker, compPos);",
        "vec4 tp2 = texture2D( texturePicker2, compPos);",
        "vec4 composite = tp1;",
        "vec4 t1 = texture2D( texture1, position );",
        "vec4 t2 = texture2D( texture2, position );",
        "vec4 t3 = texture2D( texture3, position );",
        "vec4 t4 = texture2D( texture4, position );",
        "vec3 color = blend(",
        "t1, tp1.a,",
        "t2, tp1.b,",
        "t3, tp1.g,",
        "t4, tp1.r",
        ");",
        "color *= 0.5+tp2.r;",
        // Stable terrain lighting: keeps render reliability and gives terrain shape/slider response.
        "vec3 normal = normalize(vecNormal);",
        "vec3 sunDir = normalize(sunDirection);",
        "float diffuse = max(dot(normal, sunDir), 0.0);",
        "float shaded = 0.35 + 0.65 * diffuse;",
        "float shading = mix(1.0, shaded, clamp(shadowStrength, 0.0, 1.0));",
        "color *= shading * lightScale;",
        "gl_FragColor = vec4(color,1.0);",
        "#include <logdepthbuf_fragment>",
        "}",
    ].join("\n");
}
function getVertexShader() {
    return [
        "varying vec2 vUv;",
        "varying vec3 vecNormal;",
        "#include <common>",
        "#include <logdepthbuf_pars_vertex>",
        "void main()",
        "{",
        "vUv =  uv;",
        "vec4 mvPosition = modelViewMatrix * vec4( position, 1.0 );",
        "vecNormal = (modelMatrix * vec4(normal, 0.0)).xyz;",
        "gl_Position = projectionMatrix * mvPosition;",
        "#include <logdepthbuf_vertex>",
        "}",
    ].join("\n");
}

const DEFAULT_CANVAS_CLEAR_COLOR = 0x342920;
const DEFAULT_LIGHTING_PROFILE = {
    directionalIntensity: 0.7,
    ambientIntensity: 0.45,
    exposure: 0.95,
    directionalDirections: [
        [0, 1, 0],
        [1, 2, 1],
        [-1, -2, -1],
    ],
    skyColor: 0xa4c8e8,
    groundColor: 0x665845,
    // Neutral starting values for the terrain shading uniforms / explorer sliders.
    lightScale: 1.0,
    shadowStrength: 0.6,
    // Fallback terrain sun direction (surface -> light) used until a map's own
    // directional light is known.
    terrainSunDirection: [0.35, 0.9, 0.2],
};
const BASE_INTENSITY_KEY = "__t3dBaseIntensity";
function applyRendererColorManagement(renderer, options = {}) {
    renderer.outputColorSpace = THREE.SRGBColorSpace;
    renderer.toneMapping = THREE.ACESFilmicToneMapping;
    renderer.toneMappingExposure = options.exposure ?? DEFAULT_LIGHTING_PROFILE.exposure;
    return renderer;
}
function createFallbackLightRig(options = {}) {
    const directionalIntensity = options.directionalIntensity ?? DEFAULT_LIGHTING_PROFILE.directionalIntensity;
    const ambientIntensity = options.ambientIntensity ?? DEFAULT_LIGHTING_PROFILE.ambientIntensity;
    const lights = [
        new THREE.HemisphereLight(DEFAULT_LIGHTING_PROFILE.skyColor, DEFAULT_LIGHTING_PROFILE.groundColor, ambientIntensity),
    ];
    for (const dir of DEFAULT_LIGHTING_PROFILE.directionalDirections) {
        const light = new THREE.DirectionalLight(0xffffff, directionalIntensity);
        light.position.set(dir[0], dir[1], dir[2]).normalize();
        lights.push(light);
    }
    return lights;
}
function cloneLights(lights) {
    return lights.map((light) => light.clone());
}
function addLightsToScene(scene, lights) {
    lights.forEach((light) => scene.add(light));
}
function removeLightsFromScene(scene, lights) {
    lights.forEach((light) => scene.remove(light));
}
/**
 * Returns the world-space direction pointing from a surface toward the
 * brightest directional light in the list, or null if there are none. Used to
 * keep the terrain shader's "sun" aligned with the rest of the scene's lighting.
 */
function getDominantLightDirection(lights) {
    let dominant = null;
    for (const light of lights) {
        if (!light.isDirectionalLight) {
            continue;
        }
        const directional = light;
        if (!dominant || directional.intensity > dominant.intensity) {
            dominant = directional;
        }
    }
    if (!dominant) {
        return null;
    }
    // A DirectionalLight travels from its position toward its target, so the
    // direction from the surface back toward the light is position - target.
    const direction = dominant.position.clone().sub(dominant.target.position);
    return direction.lengthSq() > 0 ? direction.normalize() : null;
}
/**
 * Points the terrain shader's `sunDirection` uniform at the brightest
 * directional light so terrain shading lines up with the lit props/models.
 * Tiles without that uniform (e.g. the export basic material) are skipped, and
 * scenes with no directional light keep the shader's default direction.
 */
function applyTerrainSunDirection(terrainTiles, lights) {
    const direction = getDominantLightDirection(lights);
    if (!direction) {
        return;
    }
    for (const tile of terrainTiles) {
        const material = tile.material;
        if (!material) {
            continue;
        }
        const materials = Array.isArray(material) ? material : [material];
        for (const mat of materials) {
            if (mat?.uniforms?.sunDirection) {
                mat.uniforms.sunDirection.value.copy(direction);
            }
        }
    }
}
function scaleDirectionalLights(lights, scale) {
    for (const light of lights) {
        if (!light.isDirectionalLight) {
            continue;
        }
        const typedLight = light;
        const userData = typedLight.userData;
        const currentBase = userData[BASE_INTENSITY_KEY];
        const baseIntensity = typeof currentBase === "number" && Number.isFinite(currentBase) ? currentBase : typedLight.intensity;
        userData[BASE_INTENSITY_KEY] = baseIntensity;
        typedLight.intensity = baseIntensity * scale;
    }
}

var LightingUtils = /*#__PURE__*/Object.freeze({
  __proto__: null,
  DEFAULT_CANVAS_CLEAR_COLOR: DEFAULT_CANVAS_CLEAR_COLOR,
  DEFAULT_LIGHTING_PROFILE: DEFAULT_LIGHTING_PROFILE,
  addLightsToScene: addLightsToScene,
  applyRendererColorManagement: applyRendererColorManagement,
  applyTerrainSunDirection: applyTerrainSunDirection,
  cloneLights: cloneLights,
  createFallbackLightRig: createFallbackLightRig,
  getDominantLightDirection: getDominantLightDirection,
  removeLightsFromScene: removeLightsFromScene,
  scaleDirectionalLights: scaleDirectionalLights
});

const LEGACY_CHUNK_SEGMENTS = 32;
const LEGACY_CHUNK_SAMPLE_WIDTH = 35;
function getChunkResolution(terrainData) {
    const preferredChunkSegments = terrainData.verticesPerChunkSide ?? LEGACY_CHUNK_SEGMENTS;
    const preferredChunkSampleWidth = preferredChunkSegments + 3;
    const chunkCount = terrainData.chunkArray.length;
    const samplesPerChunk = chunkCount > 0 ? terrainData.heightMapArray.length / chunkCount : 0;
    const expectedSamplesPerChunk = preferredChunkSampleWidth * preferredChunkSampleWidth;
    if (samplesPerChunk === expectedSamplesPerChunk) {
        return {
            chunkSegments: preferredChunkSegments,
            chunkSampleWidth: preferredChunkSampleWidth,
        };
    }
    return {
        chunkSegments: LEGACY_CHUNK_SEGMENTS,
        chunkSampleWidth: LEGACY_CHUNK_SAMPLE_WIDTH,
    };
}
function getPageTextureKey(page) {
    const coord = page.coord ?? [0, 0];
    return `${page.layer}:${coord[0]},${coord[1]}`;
}
function getSolidColorKey(color) {
    return Array.from(color).join(",");
}
function getPageSolidColorValue(page) {
    if (!page?.solidColor)
        return null;
    const solidColor = Array.from(page.solidColor);
    if (solidColor.length !== 4)
        return null;
    if (page.filename > 0)
        return null;
    if (page.flags || solidColor.some((value) => value !== 0)) {
        return solidColor;
    }
    return null;
}
function makeSolidColorTexture(color) {
    const [r = 0, g = 0, b = 0, a = 255] = Array.from(color);
    const texture = generateDataTexture(1, 1, new THREE.Color(r / 255, g / 255, b / 255));
    const imageData = texture.image.data;
    imageData[3] = a;
    texture.needsUpdate = true;
    return texture;
}
function sampleTerrainHeightChunk(chunk, x, z) {
    const width = chunk.maxX - chunk.minX;
    const depth = chunk.maxZ - chunk.minZ;
    if (width <= 0 || depth <= 0) {
        return null;
    }
    const u = (x - chunk.minX) / width;
    const v = (z - chunk.minZ) / depth;
    if (u < 0 || u > 1 || v < 0 || v > 1) {
        return null;
    }
    const colFloat = Math.max(0, Math.min(chunk.columns - 1, u * (chunk.columns - 1)));
    const rowFloat = Math.max(0, Math.min(chunk.rows - 1, v * (chunk.rows - 1)));
    const col0 = Math.floor(colFloat);
    const row0 = Math.floor(rowFloat);
    const col1 = Math.min(chunk.columns - 1, col0 + 1);
    const row1 = Math.min(chunk.rows - 1, row0 + 1);
    const tx = colFloat - col0;
    const ty = rowFloat - row0;
    const h00 = chunk.heights[row0 * chunk.columns + col0];
    const h10 = chunk.heights[row0 * chunk.columns + col1];
    const h01 = chunk.heights[row1 * chunk.columns + col0];
    const h11 = chunk.heights[row1 * chunk.columns + col1];
    const hx0 = h00 + (h10 - h00) * tx;
    const hx1 = h01 + (h11 - h01) * tx;
    return hx0 + (hx1 - hx0) * ty;
}
function createTerrainHeightSampler(state) {
    return {
        sampleHeight(x, z) {
            const localX = x - state.originX;
            const localZ = z - state.originZ;
            if (localX < 0 || localZ < 0) {
                return null;
            }
            const chunkX = Math.min(state.xChunks - 1, Math.floor(localX / state.chunkWidth));
            const chunkZ = Math.min(state.yChunks - 1, Math.floor(localZ / state.chunkHeight));
            if (chunkX < 0 || chunkX >= state.xChunks || chunkZ < 0 || chunkZ >= state.yChunks) {
                return null;
            }
            const chunk = state.chunks[chunkZ * state.xChunks + chunkX];
            if (!chunk) {
                return null;
            }
            return sampleTerrainHeightChunk(chunk, x, z);
        },
    };
}
/**
 *
 * A renderer that generates the meshes for the terrain of a map.
 *
 *
 * Requires a context previously populated by a
 * {{#crossLink "EnvironmentRenderer"}}{{/crossLink}}.
 *
 * @class TerrainRenderer
 * @constructor
 * @extends DataRenderer
 * @param  {LocalReader} localReader  The LocalReader instance to read data from.
 * @param  {Object} settings     Any settings used by this renderer.
 * *Must* specify "mapFile", a FileParser.
 * @param  {Object} context      Shared value object between renderers.
 * @param  {Logger} logger       The logging class to use for progress, warnings, errors et cetera.
 */
class TerrainRenderer extends DataRenderer {
    static rendererName = "TerrainRenderer";
    mapFile;
    mapRect;
    constructor(localReader, settings, context, logger) {
        super(localReader, settings, context, logger, "TerrainRenderer");
        this.mapFile = this.settings.mapFile;
    }
    drawWater(rect) {
        /// Add Water
        const material = new THREE.MeshBasicMaterial({
            color: 0x5bb1e8,
            wireframe: false,
            opacity: 0.35,
        });
        material.transparent = true;
        return renderRect(rect, 0, material);
    }
    parseNumChunks(terrainData) {
        terrainData.numChunksD_1 = Math.sqrt((terrainData.dims[0] * terrainData.chunkArray.length) / terrainData.dims[1]);
        terrainData.numChunksD_2 = terrainData.chunkArray.length / terrainData.numChunksD_1;
    }
    loadPagedImageCallback(callback, inflatedBuffer) {
        const self = this;
        // Prep output array
        self.getOutput().terrainTiles = [];
        self.getOutput().terrainHeightSampler = undefined;
        const pimgFile = new FileParser(inflatedBuffer);
        const pimgTableDataChunk = pimgFile.getChunk("pgtb");
        const pimgData = pimgTableDataChunk && pimgTableDataChunk.data;
        this.mapRect = null;
        /// Fetch chunks
        const terrainData = this.mapFile.getChunk("trn").data;
        const parameterData = this.mapFile.getChunk("parm").data;
        /// Read settings
        const maxAnisotropy = this.settings.anisotropy ? this.settings.anisotropy : 1;
        const { chunkSegments, chunkSampleWidth } = getChunkResolution(terrainData);
        const expectedSamplesPerChunk = chunkSampleWidth * chunkSampleWidth;
        const samplesPerChunk = terrainData.chunkArray.length > 0 ? terrainData.heightMapArray.length / terrainData.chunkArray.length : 0;
        if (samplesPerChunk !== expectedSamplesPerChunk) {
            self.logger.log(self.logger.TYPE_WARNING, "TerrainRenderer", `Unexpected terrain chunk sample count (${samplesPerChunk}); falling back to legacy ${LEGACY_CHUNK_SAMPLE_WIDTH}x${LEGACY_CHUNK_SAMPLE_WIDTH} height grid`);
        }
        /// Calculate numChunksD_1 and _2
        this.parseNumChunks(terrainData);
        const xChunks = terrainData.numChunksD_1;
        const yChunks = terrainData.numChunksD_2;
        const allMaterials = terrainData.materials.materials;
        const allTextures = terrainData.materials.texFileArray;
        // Total map dx and dy
        /*
        old parameter data definition:
        "x1", "float32",
        "y1", "float32",
        "x2", "float32",
        "y2", "float32"
        */
        // var dx = parameterData.rect.x2 - parameterData.rect.x1;
        // var dy = parameterData.rect.y2 - parameterData.rect.y1;
        const dx = parameterData.rect[2] - parameterData.rect[0];
        const dy = parameterData.rect[3] - parameterData.rect[1];
        // Each chunk dx and dy
        const cdx = (dx / terrainData.numChunksD_1) * 1; //  35/33;
        const cdy = (dy / terrainData.numChunksD_2) * 1; // 35/33;
        let n = 0;
        const customMaterial = new THREE.MeshLambertMaterial({
            side: THREE.DoubleSide,
            color: 0x666666,
            flatShading: true,
        });
        //let texMats = {};
        /// Load textures from PIMG and inject as material maps (textures)
        const chunkTextures = {};
        const generatedPickerTextures = {};
        const terrainHeightChunks = [];
        let terrainHeightOriginX = null;
        let terrainHeightOriginZ = null;
        const resolvePickerTexture = function (page) {
            const pageTextureKey = getPageTextureKey(page);
            if (generatedPickerTextures[pageTextureKey]) {
                return generatedPickerTextures[pageTextureKey];
            }
            if (page.filename > 0) {
                const texture = loadLocalTexture(self.localReader, page.filename);
                generatedPickerTextures[pageTextureKey] = texture;
                return texture;
            }
            const solidColor = getPageSolidColorValue(page);
            if (solidColor) {
                const solidColorKey = `solid:${getSolidColorKey(solidColor)}`;
                if (!generatedPickerTextures[solidColorKey]) {
                    generatedPickerTextures[solidColorKey] = makeSolidColorTexture(solidColor);
                }
                generatedPickerTextures[pageTextureKey] = generatedPickerTextures[solidColorKey];
                return generatedPickerTextures[pageTextureKey];
            }
            const fallbackColor = page.layer === 1 ? [128, 128, 128, 255] : [0, 0, 0, 255];
            const fallbackKey = `fallback:${page.layer}:${getSolidColorKey(fallbackColor)}`;
            if (!generatedPickerTextures[fallbackKey]) {
                generatedPickerTextures[fallbackKey] = makeSolidColorTexture(fallbackColor);
            }
            generatedPickerTextures[pageTextureKey] = generatedPickerTextures[fallbackKey];
            return generatedPickerTextures[pageTextureKey];
        };
        /// Load textures
        if (pimgData) {
            const strippedPages = pimgData.strippedPages;
            /// Only use layer 0
            strippedPages.forEach(function (page) {
                /// Only load layer 0 and 1
                if (page.layer <= 1) {
                    //let color = page.solidColor;
                    const coord = page.coord;
                    let matName = coord[0] + "," + coord[1];
                    if (page.layer === 1)
                        matName += "-2";
                    /// Add texture to list, note that coord name is used, not actual file name
                    if (!chunkTextures[matName]) {
                        const chunkTex = resolvePickerTexture(page);
                        if (chunkTex) {
                            /// Set repeat, antistropy and repeat Y
                            chunkTex.anisotropy = maxAnisotropy;
                            chunkTex.wrapS = THREE.RepeatWrapping;
                            chunkTex.wrapT = THREE.RepeatWrapping;
                        }
                        /// ...But store in coord name
                        chunkTextures[matName] = chunkTex;
                    }
                }
            }); /// end for each stripped page in pimgData
        }
        /// Render Each chunk
        /// We'll make this async in order for the screen to be able to update
        const renderChunk = function (cx, cy) {
            const chunkIndex = cy * xChunks + cx;
            const pageX = Math.floor(cx / 4);
            const pageY = Math.floor(cy / 4);
            // TODO: Terrain texture LOD ?
            const chunkTextureIndices = allMaterials[chunkIndex].loResMaterial.texIndexArray;
            // let matFileName = allMaterials[chunkIndex].loResMaterial.materialFile;
            // let chunkTextureIndices = allMaterials[chunkIndex].hiResMaterial.texIndexArray;
            // let matFileName = allMaterials[chunkIndex].hiResMaterial.materialFile;
            // let chunkData = terrainData.chunkArray[chunkIndex];
            // let mainTex = allTextures[chunkTextureIndices[0]];
            let mat = customMaterial;
            /// TODO: just tick invert y = false...?
            const pageOffetX = (cx % 4) / 4.0;
            const pageOffetY = 0.75 - (cy % 4) / 4.0;
            // offset 0 -> 0.75
            // Make sure we have shared textures
            /// Load and store all tiled textures
            const fileNames = [];
            for (let gi = 0; gi < chunkTextureIndices.length / 2; gi++) {
                const textureFileName = allTextures[chunkTextureIndices[gi]].filename;
                fileNames.push(textureFileName);
                /// If the texture is not already loaded, read it from the .dat!
                if (!chunkTextures[textureFileName]) {
                    /// Load local texture
                    const chunkTex = loadLocalTexture(self.localReader, textureFileName);
                    if (chunkTex) {
                        /// Set repeat, antistropy and repeat Y
                        chunkTex.anisotropy = maxAnisotropy;
                        chunkTex.wrapS = THREE.RepeatWrapping;
                        chunkTex.wrapT = THREE.RepeatWrapping;
                    }
                    chunkTextures[textureFileName] = chunkTex;
                }
            } /// End for each chunkTextureIndices
            /// Create Composite texture material, refering the shared textures
            const pageTexName = pageX + "," + pageY;
            const pageTexName2 = pageX + "," + pageY + "-2";
            /// Get haze color from environment rednerer
            const envOutput = self.getOutput(EnvironmentRenderer);
            if (envOutput.hazeColor) {
                envOutput.hazeColor[2] / 255.0;
                envOutput.hazeColor[1] / 255.0;
                envOutput.hazeColor[0] / 255.0;
            }
            const uniforms = THREE.UniformsUtils.merge([THREE.UniformsLib["lights"]]);
            /// TODO: READ FROM VO, don't default to hard coded scale
            uniforms.uvScale = { value: new THREE.Vector2(8.0, 8.0) };
            uniforms.offset = {
                value: new THREE.Vector2(pageOffetX, pageOffetY),
            };
            uniforms.texturePicker = {
                value: chunkTextures[pageTexName],
            };
            uniforms.texturePicker2 = {
                value: chunkTextures[pageTexName2],
            };
            uniforms.texture1 = { value: chunkTextures[fileNames[0]] };
            uniforms.texture2 = { value: chunkTextures[fileNames[1]] };
            uniforms.texture3 = { value: chunkTextures[fileNames[2]] };
            uniforms.texture4 = { value: chunkTextures[fileNames[3]] };
            uniforms.lightScale = { value: DEFAULT_LIGHTING_PROFILE.lightScale };
            uniforms.shadowStrength = { value: DEFAULT_LIGHTING_PROFILE.shadowStrength };
            uniforms.sunDirection = {
                value: new THREE.Vector3(...DEFAULT_LIGHTING_PROFILE.terrainSunDirection).normalize(),
            };
            if (self.settings && self.settings.export) {
                mat = new THREE.MeshBasicMaterial({ visible: true });
            }
            else {
                mat = new THREE.ShaderMaterial({
                    uniforms: uniforms,
                    fragmentShader: getFragmentShader(),
                    vertexShader: getVertexShader(),
                });
            }
            /// -1 for faces -> vertices , -2 for ignoring outer faces
            const chunkGeo = new THREE.PlaneGeometry(cdx, cdy, chunkSegments, chunkSegments);
            const chunkHeights = new Float32Array((chunkSegments + 1) * (chunkSegments + 1));
            let cn = 0;
            /// Render chunk
            /// Each chunk vertex
            for (let y = 0; y < chunkSampleWidth; y++) {
                for (let x = 0; x < chunkSampleWidth; x++) {
                    if (x !== 0 && x !== chunkSampleWidth - 1 && y !== 0 && y !== chunkSampleWidth - 1) {
                        const sampledHeight = terrainData.heightMapArray[n];
                        //@ts-ignore
                        chunkGeo.getAttribute("position").array[cn * 3 + 2] = sampledHeight;
                        chunkHeights[cn] = -sampledHeight;
                        cn++;
                    }
                    n++;
                }
            } // End each chunk vertex
            /// Flip the plane to fit wonky THREE js world axes
            const mS = new THREE.Matrix4().identity();
            mS.elements[5] = -1;
            chunkGeo.applyMatrix4(mS);
            /// Compute normals for lighting
            chunkGeo.computeVertexNormals();
            /// Build chunk mesh!
            let chunk;
            chunk = new THREE.Mesh(chunkGeo, customMaterial);
            if (Array.isArray(mat)) {
                chunk = createMultiMaterialObject(chunkGeo, mat);
            }
            else {
                chunk = new THREE.Mesh(chunkGeo, mat);
            }
            /// Move and rotate Mesh to fit in place
            chunk.rotation.set(Math.PI / 2, 0, 0);
            /// Last term is the new one: -cdx*(2/35)
            const globalOffsetX = parameterData.rect[0] + cdx / 2;
            const chunkOffsetX = cx * cdx;
            chunk.position.x = globalOffsetX + chunkOffsetX;
            /// Adjust for odd / even number of chunks
            if (terrainData.numChunksD_2 % 2 === 0) {
                /// Last term is the new one: -cdx*(2/35)
                const globalOffsetY = parameterData.rect[1] + cdy / 2 - 0; // -cdy*(1/35);
                const chunkOffsetY = cy * cdy * 1; // 33/35;
                chunk.position.z = chunkOffsetY + globalOffsetY;
            }
            else {
                const globalOffsetY = parameterData.rect[1] - cdy / 2 + 0; // cdy*(1/35);
                const chunkOffsetY = cy * cdy * 1; // 33/35;
                chunk.position.z = globalOffsetY + chunkOffsetY;
            }
            const px = chunk.position.x;
            const py = chunk.position.z;
            if (!self.mapRect) {
                self.mapRect = {
                    x1: px - cdx / 2,
                    x2: px + cdx / 2,
                    y1: py - cdy / 2,
                    y2: py + cdy / 2,
                };
            }
            self.mapRect.x1 = Math.min(self.mapRect.x1, px - cdx / 2);
            self.mapRect.x2 = Math.max(self.mapRect.x2, px + cdx / 2);
            self.mapRect.y1 = Math.min(self.mapRect.y1, py - cdy / 2);
            self.mapRect.y2 = Math.max(self.mapRect.y2, py + cdy / 2);
            chunk.updateMatrix();
            chunk.updateMatrixWorld();
            const chunkMinX = chunk.position.x - cdx / 2;
            const chunkMinZ = chunk.position.z - cdy / 2;
            terrainHeightChunks[chunkIndex] = {
                minX: chunkMinX,
                maxX: chunk.position.x + cdx / 2,
                minZ: chunkMinZ,
                maxZ: chunk.position.z + cdy / 2,
                columns: chunkSegments + 1,
                rows: chunkSegments + 1,
                heights: chunkHeights,
            };
            if (terrainHeightOriginX === null || chunkMinX < terrainHeightOriginX) {
                terrainHeightOriginX = chunkMinX;
            }
            if (terrainHeightOriginZ === null || chunkMinZ < terrainHeightOriginZ) {
                terrainHeightOriginZ = chunkMinZ;
            }
            /// Add to list of stuff to render
            /// TODO: Perhaps use some kind of props for each entry instead?
            self.getOutput().terrainTiles.push(chunk);
        }; /// End render chunk function
        const stepChunk = function (cx, cy) {
            if (cx >= xChunks) {
                cx = 0;
                cy++;
            }
            if (cy >= yChunks) {
                /// Draw water surface using map bounds
                self.getOutput().water = self.drawWater(self.mapRect);
                /// Set bounds in output VO
                self.getOutput().bounds = self.mapRect;
                if (terrainHeightOriginX !== null && terrainHeightOriginZ !== null) {
                    self.getOutput().terrainHeightSampler = createTerrainHeightSampler({
                        originX: terrainHeightOriginX,
                        originZ: terrainHeightOriginZ,
                        chunkWidth: cdx,
                        chunkHeight: cdy,
                        xChunks: xChunks,
                        yChunks: yChunks,
                        chunks: terrainHeightChunks,
                    });
                }
                /// Fire call back, we're done rendering.
                callback();
                return;
            }
            const pct = Math.floor((100 * (cy * xChunks + cx)) / (xChunks * yChunks));
            self.logger.log(self.logger.TYPE_PROGRESS, "Loading Terrain", pct);
            renderChunk(cx, cy);
            setTimeout(stepChunk, 1, cx + 1, cy);
        };
        stepChunk(0, 0);
    }
    /**
     * Output fileds generated:
     *
     * - *terrainTiles* An array of THREE.Mesh objects visualizing terrain of the map.
     *
     * - *water* A THREE.Mesh object visualizing the bounds of the map.
     *
     * - *bounds* An object wiht x1, x2, y1, and y2 properties specifying the bounds of the map.
     *
     * @async
     * @param  {Function} callback Fires when renderer is finished, does not take arguments.
     */
    renderAsync(callback) {
        /// Load all paged Images, requires inflation of other pack files!
        const pagedImageId = this.mapFile.getChunk("trn").data.materials.pagedImage;
        //@ts-ignore
        this.localReader.loadFile(pagedImageId, this.loadPagedImageCallback.bind(this, callback));
    }
    /**
     * TODO: write description. Used for export feature
     *
     * @param  {Function} callback [description]
     * @return {*}            [description]
     */
    getFileIdsAsync( /* callback */) {
        const terrainChunk = this.mapFile.getChunk("trn");
        const pimgTableDataChunk = this.mapFile.getChunk("pimg");
        const fileIds = [];
        /// ------------ SPLASH TEXTURES ------------
        const pimgData = pimgTableDataChunk && pimgTableDataChunk.data;
        const strippedPages = pimgData.strippedPages;
        /// Only use layer 0
        strippedPages.forEach(function (page) {
            /// Only load layer 0 and 1
            if (page.layer <= 1 && page.filename > 0) {
                fileIds.push(page.filename);
            }
        });
        /// ------------ END SPLASH TEXTURES ------------
        /// ------------ TILED IMAGES ------------
        const terrainData = terrainChunk.data;
        const allTextures = terrainData.materials.texFileArray;
        allTextures.forEach(function (texture) {
            if (texture.filename > 0)
                fileIds.push(texture.filename);
        });
        /// ------------ END TILED IMAGES ------------
        return fileIds;
    }
}

/**
 *
 * A renderer that generates zone models for a map.
 *
 * @class ZoneRenderer
 * @constructor
 * @extends DataRenderer
 * @param  {LocalReader} localReader  The LocalReader instance to read data from.
 * @param  {Object} settings     Any settings used by this renderer.
 * *Must* specify "mapFile", a FileParser.
 * @param  {Object} context      Shared value object between renderers.
 * @param  {Logger} logger       The logging class to use for progress, warnings, errors et cetera.
 */
class ZoneRenderer extends DataRenderer {
    static rendererName = "ZoneRenderer";
    mapFile;
    meshCache;
    textureCache;
    constructor(localReader, settings, context, logger) {
        super(localReader, settings, context, logger, "ZoneRenderer");
        this.mapFile = this.settings.mapFile;
        this.meshCache = {};
        this.textureCache = {};
    }
    collectModelGroups(zones, zoneDefsByToken, mapRect, terrainHeightSampler) {
        const allModelGroups = {};
        for (const zone of zones) {
            const zoneDef = zoneDefsByToken.get(zone.defToken);
            if (!zoneDef) {
                continue;
            }
            const modelGroups = this.getModelGroups(zone, zoneDef, mapRect, terrainHeightSampler);
            for (const key of Object.keys(modelGroups)) {
                const modelName = Number(key);
                if (!allModelGroups[modelName]) {
                    allModelGroups[modelName] = modelGroups[modelName];
                }
                else {
                    allModelGroups[modelName].push(...modelGroups[modelName]);
                }
            }
        }
        return allModelGroups;
    }
    getModelGroups(zone, zoneDef, mapRect, terrainHeightSampler) {
        const mapX = mapRect[0];
        const mapY = mapRect[1];
        const c = 32 + 16;
        const zoneRect = {
            x1: zone.vertRect[0] * c + mapX,
            y1: zone.vertRect[1] * -c - mapY,
        };
        if (zone.encodeData.length === 0) {
            return {};
        }
        const zdx = zone.vertRect[0] - zone.vertRect[2];
        if (zdx === 0) {
            return {};
        }
        let linearPos = 0;
        const modelGroups = {};
        for (let i = 0; i < zone.flags.length; i += 2) {
            linearPos += zone.flags[i];
            const flag = zone.flags[i + 1];
            if (flag === 0) {
                continue;
            }
            const zoneDefLayer = flag >> 4;
            const layer = zoneDef.layerDefArray[zoneDefLayer - 1];
            if (!layer || !layer.modelArray || layer.modelArray.length === 0) {
                continue;
            }
            const worldX = (linearPos % zdx) * c + zoneRect.x1;
            const worldZ = Math.floor(linearPos / zdx) * c + zoneRect.y1;
            const worldY = terrainHeightSampler.sampleHeight(worldX, worldZ);
            if (worldY === null) {
                continue;
            }
            const model = layer.modelArray[0];
            const modelFilename = model.filename;
            if (!modelGroups[modelFilename]) {
                modelGroups[modelFilename] = [];
            }
            modelGroups[modelFilename].push({
                worldX: worldX,
                worldY: worldY,
                worldZ: worldZ,
            });
        }
        return modelGroups;
    }
    placeModelGroup(placements, meshes) {
        const filteredMeshes = meshes.filter((mesh) => mesh.materialFlags !== 525);
        if (filteredMeshes.length === 0) {
            return;
        }
        const instancedMeshes = getInstancedMeshes(filteredMeshes, placements.length);
        for (const instancedMesh of instancedMeshes) {
            const matrix = new THREE.Matrix4();
            for (let i = 0; i < placements.length; i++) {
                const placement = placements[i];
                matrix.makeTranslation(placement.worldX, placement.worldY, placement.worldZ);
                instancedMesh.setMatrixAt(i, matrix);
            }
            instancedMesh.instanceMatrix.needsUpdate = true;
            instancedMesh.computeBoundingBox();
            instancedMesh.computeBoundingSphere();
            instancedMesh.userData.t3dInspect = {
                ...(instancedMesh.userData.t3dInspect ?? {}),
                inspectable: true,
                sourceKind: "zone",
                objectLabel: "Zone model",
                instanceCount: placements.length,
            };
            this.getOutput().meshes.push(instancedMesh);
        }
    }
    renderModelGroups(modelNames, modelGroups, index, callback) {
        if (index >= modelNames.length) {
            this.meshCache = {};
            this.textureCache = {};
            callback();
            return;
        }
        progress(this.logger, index, modelNames.length, "Loading 3D Models (Zone)");
        const modelName = Number(modelNames[index]);
        getMeshesForFilename(modelName, null, this.localReader, this.meshCache, this.textureCache, false, (meshes) => {
            if (meshes && modelGroups[modelName] && modelGroups[modelName].length > 0) {
                this.placeModelGroup(modelGroups[modelName], meshes);
            }
            this.renderModelGroups(modelNames, modelGroups, index + 1, callback);
        });
    }
    /**
     * Renders all zone meshes in a GW2 map described by the map's "zon2" chunk.
     * Output fileds generated:
     *
     * - *meshes* An array of THREE.Mesh objects visualizing all zone models refered by this map.
     *
     * @async
     * @param  {Function} callback Fires when renderer is finished, does not take arguments.
     */
    renderAsync(callback) {
        const startedAt = Date.now();
        this.getOutput().meshes = [];
        this.meshCache = {};
        this.textureCache = {};
        const zoneChunkData = this.mapFile.getChunk("zon2").data;
        const parameterChunkData = this.mapFile.getChunk("parm").data;
        const mapRect = parameterChunkData.rect;
        const zones = zoneChunkData.zoneArray;
        const zoneDefs = zoneChunkData.zoneDefArray;
        const terrainHeightSampler = this.getOutput(TerrainRenderer).terrainHeightSampler;
        if (!terrainHeightSampler) {
            this.logger.log(this.logger.TYPE_WARNING, "ZoneRenderer", "Terrain height sampler missing; skipping zone models");
            callback();
            return;
        }
        const zoneDefsByToken = new Map();
        for (const zoneDef of zoneDefs) {
            zoneDefsByToken.set(zoneDef.token, zoneDef);
        }
        const modelGroups = this.collectModelGroups(zones, zoneDefsByToken, mapRect, terrainHeightSampler);
        const modelNames = Object.keys(modelGroups);
        const totalPlacements = modelNames.reduce((count, modelName) => count + modelGroups[Number(modelName)].length, 0);
        const finish = () => {
            const elapsedMs = Date.now() - startedAt;
            this.logger.log(this.logger.TYPE_MESSAGE, "ZoneRenderer", `Loaded zone models in ${elapsedMs} ms (${zones.length} zones, ${totalPlacements} placements, ${modelNames.length} unique models, ${this.getOutput().meshes.length} emitted meshes)`);
            callback();
        };
        if (modelNames.length === 0) {
            this.meshCache = {};
            this.textureCache = {};
            finish();
            return;
        }
        this.renderModelGroups(modelNames, modelGroups, 0, finish);
    }
}

/**
 *
 * A renderer that generates a list of readable strings from a "strs" file.
 *
 * @class StringRenderer
 * @constructor
 * @extends DataRenderer
 * @param  {LocalReader} localReader  The LocalReader instance to read data from.
 * @param  {Object} settings     Any settings used by this renderer.
 * *Must* specify "id" the base ID or file ID of the string file to read strings from.
 * @param  {Object} context      Shared value object between renderers.
 * @param  {Logger} logger       The logging class to use for progress, warnings, errors et cetera.
 */
class StringRenderer extends DataRenderer {
    static rendererName = "StringRenderer";
    constructor(localReader, settings, context, logger) {
        super(localReader, settings, context, logger, "StringRenderer");
    }
    /**
     * Output fileds generated:
     *
     * - *strings* An array of objects. Each object has a "recid"-property specifing on what index within the file
     * a given string was found, and a "value"-property specigying the string value.
     *
     * - *language* An integer specifing the language of the loaded file.
     *
     * @async
     * @param  {Function} callback Fires when renderer is finished, does not take arguments.
     */
    renderAsync(callback) {
        const self = this;
        /// Get file id
        // const fileId = this.settings.id;
        /// Load the string file
        /// Set up output array
        this.getOutput().strings = [];
        this.localReader.loadFile(this.settings.id, function (inflatedData) {
            const dataView = new DataView(inflatedData);
            const end = dataView.byteLength - 2;
            /// skip past fcc
            let cursor = 4;
            let entryIndex = 0;
            while (end - cursor > 6) {
                const size = dataView.getUint16(cursor, true);
                cursor += 2;
                const decryptionOffset = dataView.getUint16(cursor, true);
                cursor += 2;
                const bitsPerSymbol = dataView.getUint16(cursor, true);
                cursor += 2;
                const entry = { size, decryptionOffset, bitsPerSymbol };
                entry.size -= 6;
                if (entry.size > 0) {
                    const isEncrypted = entry.decryptionOffset !== 0 || entry.bitsPerSymbol !== 0x10;
                    /// UTF-16
                    if (!isEncrypted) {
                        const value = new Uint16Array(inflatedData, cursor, entry.size / 2);
                        cursor += entry.size;
                        self.getOutput().strings.push({ value: String.fromCharCode(...value), recid: entryIndex });
                    }
                }
                entryIndex++;
            }
            self.getOutput().language = dataView.getUint16(end, true);
            callback();
        });
    }
}

function getCntcMainContent(file) {
    const mainChunk = file.getChunk("Main");
    if (!mainChunk?.data) {
        throw new Error(`Missing Main chunk in file type ${file.header?.type ?? "unknown"}`);
    }
    return mainChunk.data;
}
function getCntcEntries(content) {
    return content.indexEntries.map((entry, index) => {
        const begin = entry.offset;
        const next = content.indexEntries[index + 1];
        const end = next ? next.offset : content.content.length;
        const contentSlice = content.content.slice(begin, end);
        return {
            index,
            type: entry.type,
            begin,
            end,
            offset: entry.offset,
            namespaceIndex: entry.namespaceIndex,
            rootIndex: entry.rootIndex,
            contentSlice,
            size: contentSlice.length,
        };
    });
}
function readUint32LE(bytes, offset) {
    if (!bytes || offset < 0 || offset + 4 > bytes.length) {
        return null;
    }
    return ((bytes[offset] | (bytes[offset + 1] << 8) | (bytes[offset + 2] << 16) | ((bytes[offset + 3] << 24) >>> 0)) >>> 0);
}
function getCntcEntryEmbeddedType(entry) {
    return readUint32LE(entry.contentSlice, 16);
}
function getCntcEntryUniqueId(entry) {
    return readUint32LE(entry.contentSlice, 20);
}
function getCntcEntryDataId(entry) {
    return readUint32LE(entry.contentSlice, 40);
}
function findCntcEntryAtOffset(entries, offset) {
    return entries.find((entry) => offset >= entry.begin && offset < entry.end) ?? null;
}
function resolveCntcString(strings, index) {
    if (!strings || index == null || index < 0 || index >= strings.length) {
        return null;
    }
    const value = strings[index];
    return typeof value === "string" ? value : null;
}

const BASE_FIELD_DEFINITIONS = [
    { key: "decodedLength", label: "decoded length", valueKind: "entrySize" },
    { key: "embeddedType", label: "embedded type", valueKind: "entryAccessor", offset: 16, length: 4 },
    { key: "uniqueId", label: "unique id", valueKind: "entryAccessor", offset: 20, length: 4 },
    {
        key: "dataId",
        label: (_entry, definition) => definition?.dataIdLabel ?? "id",
        valueKind: "entryAccessor",
        offset: 40,
        length: 4,
    },
];
class CntcFieldRef {
    key;
    label;
    offset;
    valueKind;
    lookup;
    options;
    constructor(key, label, offset, options = {}) {
        this.key = key;
        this.label = label;
        this.offset = offset;
        this.valueKind = options.valueKind ?? "uint32";
        this.lookup = options.lookup;
        this.options = options;
    }
    read(entry) {
        return readUint32LE(entry.contentSlice, this.offset);
    }
    equals(expected) {
        return (entry) => this.read(entry) === expected;
    }
    assetReference(label) {
        return { label, offset: this.offset };
    }
    reference(key, kind, label) {
        return { key, kind, label, offset: this.offset, length: this.options.length ?? 4 };
    }
    toFieldDefinition() {
        return {
            key: this.key,
            label: this.label,
            valueKind: this.valueKind,
            offset: this.offset,
            length: this.options.length ?? 4,
            lookup: this.lookup,
            includeId: this.options.includeId,
            unknownLabel: this.options.unknownLabel,
            includeWhen: this.options.includeWhen,
            experimental: this.options.experimental,
        };
    }
}
class CntcTypeSchema {
    dataIdLabel;
    dataIdCaption;
    summaryCaption;
    summaryField;
    fieldRefs = [];
    referenceDefs = [];
    assetReferenceDefs = [];
    get fields() {
        return this.fieldRefs.map((field) => field.toFieldDefinition());
    }
    get references() {
        return this.referenceDefs;
    }
    get assetReferences() {
        return this.assetReferenceDefs;
    }
    get summaryFieldKey() {
        return this.summaryField?.key;
    }
    dataId(label, caption = label) {
        this.dataIdLabel = label;
        this.dataIdCaption = caption;
        return { label };
    }
    uint32(key, label, offset, options = {}) {
        return this.addField(new CntcFieldRef(key, label, offset, options));
    }
    lookup(key, label, offset, lookup, options = {}) {
        return this.addField(new CntcFieldRef(key, label, offset, { ...options, valueKind: "lookup", lookup }));
    }
    addReference(...references) {
        this.referenceDefs.push(...references);
    }
    addAssetReference(...references) {
        this.assetReferenceDefs.push(...references);
    }
    setSummaryField(field, caption) {
        this.summaryField = field;
        this.summaryCaption = caption;
    }
    addField(field) {
        this.fieldRefs.push(field);
        return field;
    }
}
class CntcBaseDefinition extends CntcTypeSchema {
    description;
    type;
    constructor(description, type) {
        super();
        this.description = description;
        this.type = type;
    }
}
function assetReference(label, offsetOrOffsetsOrStart, stride) {
    if (Array.isArray(offsetOrOffsetsOrStart))
        return { label, offsets: offsetOrOffsetsOrStart };
    if (stride != null)
        return { label, start: offsetOrOffsetsOrStart, stride };
    return { label, offset: offsetOrOffsetsOrStart };
}
function formatCntcFieldLabel(label, offset) {
    if (offset == null)
        return label;
    return `${label} @0x${offset.toString(16).toUpperCase()}`;
}
function formatCntcLookupValue(value, lookup, options = {}) {
    if (value == null)
        return null;
    if (!lookup)
        return value;
    const label = lookup[value];
    if (!label) {
        if (!options.unknownLabel)
            return value;
        return options.includeId ? `${options.unknownLabel} (${value})` : options.unknownLabel;
    }
    return options.includeId ? `${label} (${value})` : label;
}
function readBaseCntcFieldValue(entry, definition) {
    if (definition.includeWhen && !definition.includeWhen(entry)) {
        return null;
    }
    if (definition.valueKind === "entrySize") {
        return entry.contentSlice.length;
    }
    if (definition.valueKind === "entryAccessor") {
        if (definition.offset === 16)
            return getCntcEntryEmbeddedType(entry);
        if (definition.offset === 20)
            return getCntcEntryUniqueId(entry);
        if (definition.offset === 40)
            return getCntcEntryDataId(entry);
        return definition.offset == null ? null : readUint32LE(entry.contentSlice, definition.offset);
    }
    const rawValue = definition.offset == null ? null : readUint32LE(entry.contentSlice, definition.offset);
    if (definition.valueKind === "lookup") {
        return formatCntcLookupValue(rawValue, definition.lookup, {
            includeId: definition.includeId,
            unknownLabel: definition.unknownLabel,
        });
    }
    return rawValue;
}

const CNTC_TYPE_IDS = {
    ACHIEVEMENTS: 0,
    ACHIEVEMENT_CATEGORIES: 1,
    CRAFTING_RECIPES: 12,
    GUILD_UPGRADES: 27,
    ITEMS: 35,
    MAPS: 45,
    MINIPETS: 48,
    OUTFITS: 51,
    SKILLS: 64,
    SKINS: 66,
    WVW_RANK_DEFINITIONS: 85,
    ANIMATION_LISTENERS: 99,
    ANIMATION_BLEND_TREES: 100,
    CONFIGURATIONS: 149,
    CONTENT_ACCESS_GROUPS: 152,
    DAY_NIGHT_CYCLES: 167,
    DYNAMIC_CAMERAS: 179,
    DYNAMIC_CAMERA_TRANSITIONS: 180,
    EFFECTS: 182,
    ITEM_CONVERSION_ARRAYS: 236,
    ITEM_CRESTS: 237,
    ITEM_DEFAULTS: 238,
    MARKERS: 292,
    MOVEMENT_MODIFIERS: 302,
    MOVEMENT_SETTINGS: 304,
    RANDOM_UNLOCK_TABLES: 348,
    REWARDS: 349,
    REWARD_TRACKS: 352,
    TERRAIN_EFFECT_TABLES: 401,
    TERRAIN_DECAL_TABLES: 402,
    INTEGERS: 433,
    NUMBERS: 436,
};

const ITEM_TYPE_LOOKUP = {
    0: "Armor",
    2: "Back",
    3: "Bag",
    4: "Consumable",
    5: "Container",
    6: "CraftingMaterial",
    9: "Gathering",
    10: "Gizmo",
    11: "JadeTechModule",
    15: "MiniPet",
    17: "PowerCore",
    18: "Relic",
    19: "Tool",
    21: "Trinket",
    22: "Trophy",
    23: "UpgradeComponent",
    24: "Weapon",
};
const ITEM_RARITY_LOOKUP = {
    0: "Junk",
    1: "Basic",
    2: "Fine",
    3: "Masterwork",
    4: "Rare",
    5: "Exotic",
    6: "Ascended",
};
const ARMOR_SLOT_LOOKUP = {
    0: "Coat",
    1: "Leggings",
    2: "Gloves",
    3: "Helm",
    4: "HelmAquatic",
    5: "Boots",
    6: "Shoulders",
};
const ARMOR_WEIGHT_CLASS_LOOKUP = {
    1: "Light",
    2: "Medium",
    3: "Heavy",
};
class CntcItemSchema extends CntcTypeSchema {
    type = CNTC_TYPE_IDS.ITEMS;
    description = "Items";
    itemId = this.dataId("item id");
    itemTypeLookup = ITEM_TYPE_LOOKUP;
    rarityLookup = ITEM_RARITY_LOOKUP;
    armorSlotLookup = ARMOR_SLOT_LOOKUP;
    armorWeightClassLookup = ARMOR_WEIGHT_CLASS_LOOKUP;
    itemType = this.lookup("itemType", "item type", 44, this.itemTypeLookup, {
        includeId: true,
        unknownLabel: "Unknown",
    });
    rarity = this.lookup("rarity", "rarity", 96, this.rarityLookup, {
        includeId: true,
        unknownLabel: "Unknown",
    });
    level = this.uint32("level", "level", 116);
    armorSlot = this.lookup("armorSlot", "armor slot", 184, this.armorSlotLookup, {
        includeId: true,
        unknownLabel: "Unknown",
        includeWhen: this.itemType.equals(0),
    });
    armorWeightClass = this.lookup("armorWeightClass", "armor weight class", 280, this.armorWeightClassLookup, {
        includeId: true,
        unknownLabel: "Unknown",
        includeWhen: this.itemType.equals(0),
    });
    constructor() {
        super();
        this.setSummaryField(this.itemType, "Item Type");
        this.addReference({ key: "skin", kind: "skin", label: "skin ref", length: 4, targetType: CNTC_TYPE_IDS.SKINS });
        this.addAssetReference(assetReference("icon", 64));
    }
    getTypeEnum(entry) {
        if (entry.type !== CNTC_TYPE_IDS.ITEMS) {
            return null;
        }
        return this.itemType.read(entry);
    }
    getTypeLabel(entry) {
        const itemTypeId = this.getTypeEnum(entry);
        return itemTypeId == null
            ? ""
            : String(formatCntcLookupValue(itemTypeId, this.itemTypeLookup, { includeId: true, unknownLabel: "Unknown" }));
    }
    parse(entry) {
        if (entry.type !== CNTC_TYPE_IDS.ITEMS || !entry.contentSlice) {
            return null;
        }
        const itemTypeId = this.getTypeEnum(entry);
        const rarityId = this.rarity.read(entry);
        const item = {
            id: getCntcEntryDataId(entry),
            itemTypeId,
            itemType: String(formatCntcLookupValue(itemTypeId, this.itemTypeLookup, { unknownLabel: "Unknown" }) ?? "Unknown"),
            rarityId,
            rarity: rarityId == null
                ? null
                : (formatCntcLookupValue(rarityId, this.rarityLookup)?.toString() ?? null),
            level: this.level.read(entry),
        };
        if (itemTypeId === 0) {
            const slotId = this.armorSlot.read(entry);
            const weightClassId = this.armorWeightClass.read(entry);
            item.armor = {
                slotId,
                slot: slotId == null
                    ? null
                    : (formatCntcLookupValue(slotId, this.armorSlotLookup)?.toString() ?? null),
                weightClassId,
                weightClass: weightClassId == null
                    ? null
                    : (formatCntcLookupValue(weightClassId, this.armorWeightClassLookup)?.toString() ?? null),
            };
        }
        return item;
    }
}
const CNTC_ITEM_SCHEMA = new CntcItemSchema();

class CntcMapSchema extends CntcTypeSchema {
    type = CNTC_TYPE_IDS.MAPS;
    description = "Maps";
    mapId = this.dataId("map id");
    mapData = this.uint32("mapDataFileRefIndex", "map data file ref", 104);
    codename = this.uint32("mapNameStringIndex", "codename string index", 176);
    region = this.uint32("mapRegionStringIndex", "region string index", 272);
    constructor() {
        super();
        this.addReference(this.codename.reference("mapName", "string", "codename"), this.region.reference("mapRegion", "string", "region"));
        this.addAssetReference(this.mapData.assetReference("map data"), assetReference("portal", 96), assetReference("image", [88, 120]), assetReference("audio", [56, 64, 72, 80]));
    }
    getDataFileRefIndex(entry) {
        if (entry.type !== CNTC_TYPE_IDS.MAPS) {
            return null;
        }
        return this.mapData.read(entry);
    }
    getNameIndex(entry) {
        if (entry.type !== CNTC_TYPE_IDS.MAPS) {
            return null;
        }
        return this.codename.read(entry);
    }
    getRegionIndex(entry) {
        if (entry.type !== CNTC_TYPE_IDS.MAPS) {
            return null;
        }
        return this.region.read(entry);
    }
}
const CNTC_MAP_SCHEMA = new CntcMapSchema();

class CntcSkinSchema extends CntcTypeSchema {
    type = CNTC_TYPE_IDS.SKINS;
    description = "Skins";
    constructor() {
        super();
        this.addAssetReference(assetReference("model", 48), assetReference("icon", 88), assetReference("model", 192, 32));
    }
}
const CNTC_SKIN_SCHEMA = new CntcSkinSchema();

const KNOWN_TYPE_DEFINITIONS = [
    new CntcBaseDefinition("Achievements", CNTC_TYPE_IDS.ACHIEVEMENTS),
    new CntcBaseDefinition("Achievement Categories", CNTC_TYPE_IDS.ACHIEVEMENT_CATEGORIES),
    new CntcBaseDefinition("Crafting Recipes", CNTC_TYPE_IDS.CRAFTING_RECIPES),
    new CntcBaseDefinition("Guild Upgrades", CNTC_TYPE_IDS.GUILD_UPGRADES),
    CNTC_ITEM_SCHEMA,
    CNTC_MAP_SCHEMA,
    new CntcBaseDefinition("Minipets", CNTC_TYPE_IDS.MINIPETS),
    new CntcBaseDefinition("Outfits", CNTC_TYPE_IDS.OUTFITS),
    new CntcBaseDefinition("Skills", CNTC_TYPE_IDS.SKILLS),
    CNTC_SKIN_SCHEMA,
    new CntcBaseDefinition("WvW Rank Definitions", CNTC_TYPE_IDS.WVW_RANK_DEFINITIONS),
    new CntcBaseDefinition("Animation Listeners", CNTC_TYPE_IDS.ANIMATION_LISTENERS),
    new CntcBaseDefinition("Animation Blend Trees", CNTC_TYPE_IDS.ANIMATION_BLEND_TREES),
    new CntcBaseDefinition("Configurations", CNTC_TYPE_IDS.CONFIGURATIONS),
    new CntcBaseDefinition("Content Access Groups", CNTC_TYPE_IDS.CONTENT_ACCESS_GROUPS),
    new CntcBaseDefinition("Day/Night Cycles", CNTC_TYPE_IDS.DAY_NIGHT_CYCLES),
    new CntcBaseDefinition("Dynamic Cameras", CNTC_TYPE_IDS.DYNAMIC_CAMERAS),
    new CntcBaseDefinition("Dynamic Camera Transitions", CNTC_TYPE_IDS.DYNAMIC_CAMERA_TRANSITIONS),
    new CntcBaseDefinition("Effects", CNTC_TYPE_IDS.EFFECTS),
    new CntcBaseDefinition("Item Conversion Arrays", CNTC_TYPE_IDS.ITEM_CONVERSION_ARRAYS),
    new CntcBaseDefinition("Item Crests", CNTC_TYPE_IDS.ITEM_CRESTS),
    new CntcBaseDefinition("Item Defaults", CNTC_TYPE_IDS.ITEM_DEFAULTS),
    new CntcBaseDefinition("Markers", CNTC_TYPE_IDS.MARKERS),
    new CntcBaseDefinition("Movement Modifiers", CNTC_TYPE_IDS.MOVEMENT_MODIFIERS),
    new CntcBaseDefinition("Movement Settings", CNTC_TYPE_IDS.MOVEMENT_SETTINGS),
    new CntcBaseDefinition("Random Unlock Tables", CNTC_TYPE_IDS.RANDOM_UNLOCK_TABLES),
    new CntcBaseDefinition("Rewards", CNTC_TYPE_IDS.REWARDS),
    new CntcBaseDefinition("Reward Tracks", CNTC_TYPE_IDS.REWARD_TRACKS),
    new CntcBaseDefinition("Terrain Effect Tables", CNTC_TYPE_IDS.TERRAIN_EFFECT_TABLES),
    new CntcBaseDefinition("Terrain Decal Tables", CNTC_TYPE_IDS.TERRAIN_DECAL_TABLES),
    new CntcBaseDefinition("Integers", CNTC_TYPE_IDS.INTEGERS),
    new CntcBaseDefinition("Numbers", CNTC_TYPE_IDS.NUMBERS),
];

const CNTC_TYPE_DEFINITIONS = Object.fromEntries(KNOWN_TYPE_DEFINITIONS.map((definition) => [definition.type, definition]));
Object.fromEntries(KNOWN_TYPE_DEFINITIONS.map((definition) => [definition.type, definition.description]));
function matchesAssetReference(definition, offset) {
    if ("offset" in definition)
        return definition.offset === offset;
    if ("offsets" in definition)
        return definition.offsets.includes(offset);
    return offset >= definition.start && (offset - definition.start) % definition.stride === 0;
}
function getCntcTypeDefinition(type) {
    return CNTC_TYPE_DEFINITIONS[type];
}
function getCntcTypeDescription(type) {
    return getCntcTypeDefinition(type)?.description ?? "";
}
function getCntcDataIdCaption(type) {
    return getCntcTypeDefinition(type)?.dataIdCaption ?? "data id";
}
function getCntcFieldDefinitions(type) {
    return getCntcTypeDefinition(type)?.fields ?? [];
}
function getCntcFieldDefinition(type, key) {
    return [...BASE_FIELD_DEFINITIONS, ...getCntcFieldDefinitions(type)].find((field) => field.key === key);
}
function getCntcReferenceDefinitions(type) {
    return getCntcTypeDefinition(type)?.references ?? [];
}
function getCntcAssetReferenceLabel(type, offset) {
    const definition = getCntcTypeDefinition(type);
    const assetReference = definition?.assetReferences.find((candidate) => matchesAssetReference(candidate, offset));
    const hex = `@0x${offset.toString(16).toUpperCase()}`;
    return assetReference ? `${assetReference.label} ${hex}` : `file ref ${hex}`;
}
function getCntcEntrySummaryCaption(type) {
    const definition = getCntcTypeDefinition(type);
    if (definition?.summaryCaption)
        return definition.summaryCaption;
    if (!definition?.summaryFieldKey)
        return "";
    const field = getCntcFieldDefinition(type, definition.summaryFieldKey);
    return field && typeof field.label === "string" ? field.label : "";
}
function getCntcEntrySummaryField(type) {
    const definition = getCntcTypeDefinition(type);
    return definition?.summaryFieldKey ? getCntcFieldDefinition(type, definition.summaryFieldKey) : undefined;
}
function getCntcEntrySummaryValue(entry) {
    const field = getCntcEntrySummaryField(entry.type);
    if (!field)
        return "";
    const value = readBaseCntcFieldValue(entry, field);
    return value == null ? "" : String(value);
}

function describeCntcEntry(entry) {
    const typeDefinition = getCntcTypeDefinition(entry.type);
    const fieldDefinitions = [...BASE_FIELD_DEFINITIONS, ...getCntcFieldDefinitions(entry.type)].filter((definition) => !definition.includeWhen || definition.includeWhen(entry));
    return fieldDefinitions.map((definition) => {
        const label = typeof definition.label === "function" ? definition.label(entry, typeDefinition) : definition.label;
        return {
            label: formatCntcFieldLabel(label, definition.offset),
            offset: definition.offset,
            length: definition.length,
            value: readBaseCntcFieldValue(entry, definition),
            experimental: definition.experimental,
        };
    });
}

function describeCntcReferences(entry) {
    return getCntcReferenceDefinitions(entry.type).map((definition) => ({
        kind: definition.kind,
        label: definition.label,
        offset: definition.offset,
        length: definition.length ?? 4,
        targetType: definition.targetType,
    }));
}
const ITEM_SKIN_FIXUP_HINT = 176;
const PACK_SEARCH_SPAN = 64;
class CntcResolver {
    reader;
    fileCache = new Map();
    packBaseIdCache = new Map();
    constructor(reader) {
        this.reader = reader;
    }
    seedFile(file) {
        this.fileCache.set(file.baseId, Promise.resolve(file));
    }
    async loadFile(baseId) {
        let pending = this.fileCache.get(baseId);
        if (!pending) {
            pending = this.readFile(baseId);
            this.fileCache.set(baseId, pending);
        }
        return pending;
    }
    async listReferences(baseId, entry) {
        const references = [];
        const file = await this.loadFile(baseId);
        if (file) {
            for (const fixup of file.mainContent.fileIndices) {
                if (fixup.relocOffset < entry.begin || fixup.relocOffset >= entry.end)
                    continue;
                const offset = fixup.relocOffset - entry.begin;
                references.push({ kind: "asset", label: getCntcAssetReferenceLabel(entry.type, offset), offset, length: 4 });
            }
        }
        references.push(...describeCntcReferences(entry));
        return references.sort((left, right) => (left.offset ?? Number.MAX_SAFE_INTEGER) - (right.offset ?? Number.MAX_SAFE_INTEGER));
    }
    async resolveReference(baseId, entry, reference) {
        if (reference.kind === "skin") {
            const skin = await this.resolveSkinReference(baseId, entry, reference.targetType);
            if (!skin)
                return null;
            return {
                label: `skin ref @0x${skin.sourceOffset.toString(16).toUpperCase()}`,
                offset: skin.sourceOffset,
                length: 4,
                display: skin.skinId != null ? String(skin.skinId) : "(skin)",
                navigation: {
                    target: "entry",
                    baseId: skin.baseId,
                    type: skin.type,
                    dataId: skin.dataId,
                    uniqueId: skin.uniqueId,
                    offset: skin.offset,
                },
            };
        }
        if (reference.kind === "asset") {
            const fileRefIndex = reference.offset == null ? null : readUint32LE(entry.contentSlice, reference.offset);
            const assetBaseId = await this.resolveFileRef(baseId, fileRefIndex);
            if (assetBaseId == null)
                return null;
            return {
                label: reference.label,
                offset: reference.offset,
                length: reference.length,
                display: String(assetBaseId),
                navigation: { target: "file", baseId: assetBaseId },
            };
        }
        if (reference.kind === "string") {
            const stringIndex = reference.offset == null ? null : readUint32LE(entry.contentSlice, reference.offset);
            const value = await this.resolveString(baseId, stringIndex);
            if (value == null)
                return null;
            return {
                label: reference.label,
                offset: reference.offset,
                length: reference.length,
                display: value,
                navigation: { target: "none" },
            };
        }
        return null;
    }
    async resolveSkinReference(itemBaseId, itemEntry, targetType) {
        const file = await this.loadFile(itemBaseId);
        if (!file)
            return null;
        const fixups = file.mainContent.externalOffsets
            .filter((fixup) => fixup.relocOffset >= itemEntry.begin && fixup.relocOffset < itemEntry.end)
            .sort((left, right) => Math.abs(left.relocOffset - (itemEntry.begin + ITEM_SKIN_FIXUP_HINT)) -
            Math.abs(right.relocOffset - (itemEntry.begin + ITEM_SKIN_FIXUP_HINT)));
        for (const fixup of fixups) {
            const targetOffset = readUint32LE(file.mainContent.content, fixup.relocOffset);
            if (targetOffset == null)
                continue;
            const targetBaseId = await this.resolveTargetBaseId(itemBaseId, fixup.targetFileIndex);
            if (targetBaseId == null)
                continue;
            const targetFile = await this.loadFile(targetBaseId);
            if (!targetFile)
                continue;
            const targetEntry = findCntcEntryAtOffset(targetFile.entries, targetOffset);
            if (!targetEntry || (targetType != null && targetEntry.type !== targetType))
                continue;
            return {
                skinId: getCntcEntryDataId(targetEntry),
                baseId: targetBaseId,
                type: targetEntry.type,
                dataId: getCntcEntryDataId(targetEntry),
                uniqueId: getCntcEntryUniqueId(targetEntry),
                offset: targetEntry.begin,
                sourceOffset: fixup.relocOffset - itemEntry.begin,
            };
        }
        return null;
    }
    async resolveString(baseId, stringIndex) {
        if (stringIndex == null)
            return null;
        const file = await this.loadFile(baseId);
        if (!file)
            return null;
        return resolveCntcString(file.mainContent.strings, stringIndex);
    }
    async resolveFileRef(anchorBaseId, fileRefIndex) {
        if (fileRefIndex == null)
            return null;
        const packBaseId = await this.resolvePackBaseId(anchorBaseId);
        if (packBaseId == null)
            return null;
        const packFile = await this.loadFile(packBaseId);
        if (!packFile)
            return null;
        const targetBaseId = packFile.mainContent.fileRefs[fileRefIndex];
        return Number.isFinite(targetBaseId) && targetBaseId > 0 ? targetBaseId : null;
    }
    async resolveTargetBaseId(anchorBaseId, targetFileIndex) {
        const packBaseId = await this.resolvePackBaseId(anchorBaseId);
        return packBaseId == null ? null : packBaseId + targetFileIndex;
    }
    async resolvePackBaseId(anchorBaseId) {
        let pending = this.packBaseIdCache.get(anchorBaseId);
        if (!pending) {
            pending = (async () => {
                const floor = Math.max(0, anchorBaseId - PACK_SEARCH_SPAN);
                for (let candidate = anchorBaseId; candidate >= floor; candidate -= 1) {
                    const file = await this.loadFile(candidate);
                    if (file?.mainContent.typeInfos.length)
                        return candidate;
                }
                return null;
            })();
            this.packBaseIdCache.set(anchorBaseId, pending);
        }
        return pending;
    }
    async readFile(baseId) {
        const mftId = this.reader.getFileIndex(baseId);
        if (!Number.isFinite(mftId))
            return null;
        const file = await this.reader.readFile(mftId, false, true);
        if (!file.buffer)
            return null;
        const packfile = new FileParser(file.buffer);
        if (packfile.header.type !== "cntc")
            return null;
        const mainContent = getCntcMainContent(packfile);
        return { baseId, mainContent, entries: getCntcEntries(mainContent) };
    }
}

/**
 * Public CNTC API.
 *
 * Keep this facade narrow. Schema-building helpers and registry internals are
 * intentionally imported by sibling modules directly instead of exported here.
 */

var Cntc = /*#__PURE__*/Object.freeze({
  __proto__: null,
  CNTC_TYPE_IDS: CNTC_TYPE_IDS,
  CntcResolver: CntcResolver,
  describeCntcEntry: describeCntcEntry,
  getCntcDataIdCaption: getCntcDataIdCaption,
  getCntcEntries: getCntcEntries,
  getCntcEntryDataId: getCntcEntryDataId,
  getCntcEntrySummaryCaption: getCntcEntrySummaryCaption,
  getCntcEntrySummaryValue: getCntcEntrySummaryValue,
  getCntcEntryUniqueId: getCntcEntryUniqueId,
  getCntcMainContent: getCntcMainContent,
  getCntcTypeDefinition: getCntcTypeDefinition,
  getCntcTypeDescription: getCntcTypeDescription,
  readUint32LE: readUint32LE,
  resolveCntcString: resolveCntcString
});

var name = "t3d-lib";
var version = "3.0.0";
var license = "GPL-3.0";
var main = "build/T3D-node.cjs";
var module = "build/T3D.mjs";
var exports = {
	".": {
		require: "./build/T3D-node.cjs",
		"import": "./build/T3D.mjs",
		"default": "./build/T3D.mjs"
	}
};
var types = "src/T3DLib.ts";
var scripts = {
	build: "rollup -c",
	version: "npm run build; git add build",
	"lint:check": "eslint src",
	"lint:fix": "eslint src --fix",
	"format:fix": "prettier --write ./src",
	"format:check": "prettier --check ./src",
	test: "node -r ts-node/register/transpile-only --test ./test/*.test.cjs"
};
var dependencies = {
	"t3d-parser": "*",
	three: "0.184.0",
	vblob: "1.0.2",
	"web-worker": "1.0.0"
};
var devDependencies = {
	"@rollup/plugin-commonjs": "^26.0.1",
	"@rollup/plugin-json": "^6.1.0",
	"@rollup/plugin-node-resolve": "^15.2.3",
	"@rollup/plugin-typescript": "^11.1.6",
	"@types/three": "0.184.0",
	"@typescript-eslint/eslint-plugin": "8.5.0",
	"@typescript-eslint/parser": "8.5.0",
	rollup: "4.21.3",
	tslib: "2.7.0",
	typescript: "5.5.4"
};
var prettier = {
	useTabs: false,
	trailingComma: "es5",
	tabWidth: 2,
	printWidth: 120
};
var packageJson = {
	name: name,
	version: version,
	license: license,
	main: main,
	module: module,
	exports: exports,
	types: types,
	scripts: scripts,
	dependencies: dependencies,
	devDependencies: devDependencies,
	prettier: prettier
};

/* INCLUDES */
/* PRIVATE VARS */
const _settings = {
    t3dtoolsWorker: "modules/t3dtools/t3dworker.js",
    concurrentTasks: typeof navigator !== "undefined" ? navigator.hardwareConcurrency : 1,
};
const T3D = {
    version: packageJson.version,
    DataRenderer: DataRenderer,
    EnvironmentRenderer: EnvironmentRenderer,
    HavokRenderer: HavokRenderer,
    PropertiesRenderer: PropertiesRenderer,
    SingleModelRenderer: SingleModelRenderer,
    TerrainRenderer: TerrainRenderer,
    ZoneRenderer: ZoneRenderer,
    StringRenderer: StringRenderer,
    Logger: logger,
    MapFileList: mapMap,
    MaterialUtils: MaterialUtils,
    LightingUtils: LightingUtils,
    MathUtils: MathUtils,
    RenderUtils: RenderUtils,
    PersistantStore: PersistantStore,
    FileTypes: FileTypes,
    Cntc: Cntc,
    /**
     * Creates a new instance of LocalReader with an pNaCl inflater connected to it.
     */
    getLocalReader: function (file, callback, t3dtoolsWorker, noIndexedDB) {
        const path = t3dtoolsWorker || _settings.t3dtoolsWorker;
        // Create the instance and init the threads
        const lrInstance = new LocalReader({
            workerPath: path,
            workersNb: _settings.concurrentTasks,
            noIndexedDB,
        });
        /// Callback with the lrInstance
        lrInstance.openArchive(file).then(() => {
            callback(lrInstance);
        });
        return lrInstance;
    },
    /**
     * Utility method for acceccing a list containing information about all files
     * in the .dat connected to the provided LocalReader instance. This method first
     * tries to read a local indexing list from the client's localstorage and
     * fallbacks to generating the list by scanning the MFT indices of the .dat
     * and peeking each file in order to find out what filetype it has.
     *
     * Note that peeking the files is the time consuming task, so if you don't want
     * yout application to spend time indexing the .dat and have a priori knowledge
     * about the required file Id's you should not use this method.
     **/
    getFileListAsync: function (localReader, callback) {
        localReader.readFileList().then((result) => {
            const returnObj = {};
            for (const fileEntry of result) {
                if (returnObj[fileEntry.fileType] === undefined) {
                    returnObj[fileEntry.fileType] = [];
                }
                returnObj[fileEntry.fileType].push(fileEntry.mftId);
            }
            callback(returnObj);
        });
    },
    getFileListIncremental: function (localReader, callbacks, options) {
        return localReader.readFileListIncremental(callbacks, undefined, options);
    },
    /**
     * Utility method for acceccing a list containing information about all map files
     * in the .dat connected to the provided LocalReader instance. This method first
     * tries to read a local indexing list from the client's localstorage and
     * fallbacks to generating the list by scanning the MFT indices of the .dat
     * and peeking each file in order to find out what filetype it has.
     *
     * If the searchAll flag is not set to true, this process will only scan files
     * from the "T3D/MapFileList" property.
     **/
    getMapListAsync: function (localReader, callback, searchAll) {
        function restoreOuput(array) {
            const returnArray = [];
            for (const elt of array) {
                let category = returnArray.findIndex((i) => i.name === elt.category);
                if (category === -1) {
                    category = returnArray.push({ name: elt.category, maps: [] }) - 1;
                }
                returnArray[category].maps.push({
                    fileName: elt.baseId,
                    name: elt.name,
                });
            }
            // And resort it in order
            returnArray.sort((i, j) => {
                if (i.name < j.name)
                    return -1;
                if (i.name > j.name)
                    return 1;
                return 0;
            });
            return { maps: returnArray };
        }
        /// If seachAll flag is true, force a deep search
        if (searchAll) {
            localReader.readFileList().then(() => {
                localReader.getMapList().then((result) => {
                    callback(restoreOuput(result));
                });
            });
            return;
        }
        else {
            localReader.getMapList().then((result) => {
                callback(restoreOuput(result));
            });
        }
    },
    /**
     * Utility method used for rendering map files. Loads a map file and applies
     * the provided renderers to it.
     **/
    renderMapContentsAsync: function (localReader, fileName, renderers, callback, logger) {
        /// VO for storing result from renderers
        const context = {};
        let runAllRenderers;
        /// Make sure we got an actuall ID number
        if (parseInt(fileName)) {
            /// File name is baseId, load using local reader.
            localReader.loadFile(parseInt(fileName), function (arrayBuffer) {
                /// Initiate Map file object. Connect callback
                const mapFile = new FileParser(arrayBuffer);
                /// Populate VO by running the renderers
                runAllRenderers = function (i) {
                    /// Run each renderer
                    if (i < renderers.length) {
                        T3D.runRenderer(renderers[i].renderClass, localReader, Object.assign(renderers[i].settings, { mapFile: mapFile }), context, () => runAllRenderers(i + 1));
                    }
                    /// Fire callback with VO when done
                    else {
                        callback(context);
                    }
                };
                /// Starting point for running each renderer
                runAllRenderers(0);
            });
        }
        /// Primitive error message...
        else {
            const outputLogger = logger || T3D.Logger;
            outputLogger.log(T3D.Logger.TYPE_ERROR, "Map id must be an integer!, was:", fileName);
        }
    },
    /**
     * Utility method for applying a single renderer to a LocalReader insatnce.
     **/
    runRenderer: function (renderClass, localReader, settings, context, cb) {
        const r = new renderClass(localReader, settings, context, undefined, renderClass.rendererName);
        r.renderAsync(cb);
    },
    getContextValue: function (context, clazz, propName, defaultValue) {
        const output = context[clazz.rendererName];
        if (output) {
            return (output[propName] ? output[propName] : defaultValue);
        }
        return defaultValue;
    },
    setEnvironmentVariant: function (context, variantId) {
        return setActiveEnvironmentVariant(context, variantId);
    },
    disposeEnvironmentResources: function (context) {
        return disposeEnvironmentResources(context);
    },
    /**
     * Check if the client web browser can render WebGL 3D content.
     *
     * @private
     * @param  {boolean} return_context flag making this method return the canvas object instead of true
     * @return {boolean} true if the client is WebGL enabled, false otherwise
     */
    hasWebGL: function (return_context) {
        if (window.WebGLRenderingContext) {
            const canvas = document.createElement("canvas");
            const names = ["webgl", "experimental-webgl", "moz-webgl", "webkit-3d"];
            let context = false;
            for (let i = 0; i < 4; i++) {
                try {
                    context = canvas.getContext(names[i]);
                    if (context && typeof context.getParameter === "function") {
                        // WebGL is enabled
                        if (return_context) {
                            // return WebGL object if the function's argument is present
                            return { name: names[i], gl: context };
                        }
                        // else, return just true
                        return true;
                    }
                }
                catch {
                    //continue
                }
            }
            // WebGL is supported, but disabled
            return false;
        }
        // WebGL not supported
        return false;
    },
};
/* PRIVATE METHODS */
/**
 * Performs checks for required browser capabilities and required third party libraries.
 * Loggs any warnings or error messages.
 *
 * @private
 * @return {Number} The ammount of errors and warnings generated.
 */
function checkRequirements() {
    let numErrors = 0;
    if (!globalThis.window || !window.indexedDB) {
        T3D.Logger.log(T3D.Logger.TYPE_ERROR, "T3D persistant storing and loading requires indexedDB support.");
        numErrors++;
    }
    if (numErrors < 1) {
        T3D.Logger.log(T3D.Logger.TYPE_MESSAGE, "Tyria 3D API v" + T3D.version + " initialized.");
    }
    return numErrors;
}
/// Library checks requirements on startup
checkRequirements();

export { Cntc, DataRenderer, EnvironmentRenderer, FileTypes, HavokRenderer, LocalReader, logger as Logger, mapMap as MapFileList, MaterialUtils, MathUtils, PersistantStore, PropertiesRenderer, RenderUtils, SingleModelRenderer, StringRenderer, TerrainRenderer, ZoneRenderer, T3D as default };
//# sourceMappingURL=T3D.mjs.map
